import { useQueryClient } from '@tanstack/react-query';
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  InteractionManager,
  TextInput,
  useWindowDimensions,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ConfirmationDialog } from '../components/ConfirmationDialog';
import {
  CorrespondenceDock,
  CorrespondenceLauncher,
  CorrespondencePanel,
  DISCUSSION_QUERY_PARAM,
  DiscussionToggle,
  THREAD_QUERY_PARAM,
  arrivalThreadIdFrom,
  arrivesWithDiscussionOpen,
  shouldDock,
  useCorrespondenceSessionFor,
} from '../components/correspondence';
import { SelectedFile } from '../components/forms/FileUpload';
import { StepFirstSubsectionExpandProvider } from '../components/forms/stepFirstSubsectionExpandContext';
import { RotateCcw } from '../components/icons';
import { DevicesView } from '../components/locationTags/devices/DevicesView';
import {
  DevicesViewToolbarButton,
  LocationTagsControl,
} from '../components/locationTags/LocationTagsControl';
import { ProcessingModal } from '../components/modals/ProcessingModal';
import { RecordingWarningModal } from '../components/modals/RecordingWarningModal';
import { RequestDeliverablesDialog } from '../components/modals/RequestDeliverablesDialog';
import { TextIntakeModal } from '../components/modals/TextIntakeModal';
import { PermissionAwareButton } from '../components/PermissionAwareButton';
import {
  RecordTableDragScrollLockProvider,
  useRecordTableDragScrollLock,
} from '../components/recordTable/RecordTableDragScrollLock';
import {
  stripRepeatableInstanceArraysFromPatch,
  dirtyFieldsAreOnlyRepeatablePatchKeys,
} from '../components/repeatableSections/repeatableInstanceEditSessionUtils';
import { RequestFailedIndicator } from '../components/RequestFailedIndicator';
import { AudioRecordingBar } from '../components/shared/AudioRecordingBar';
import { AudioRecordingButton } from '../components/shared/AudioRecordingButton';
import {
  KeyboardAwareFormScrollView,
  KEYBOARD_BOTTOM_OFFSET,
} from '../components/shared/KeyboardAwareFormScrollView';
import { KeyboardDismissToolbar } from '../components/shared/KeyboardDismissToolbar';
import { PasteTextButton } from '../components/shared/PasteTextButton';
import {
  RequestToolsMenu,
  type RequestToolsMenuItem,
} from '../components/shared/RequestToolsMenu';
import { ToolbarActionButton } from '../components/shared/ToolbarActionButton';
import { UploadProgressBar } from '../components/shared/UploadProgressBar';
import { ImpersonationAuditStep } from '../components/steps/ImpersonationAuditStep';
import { RequestDeliverablesStep } from '../components/steps/RequestDeliverablesStep';
import { featureFlags } from '../config/featureFlags';
import { COOLING_SYSTEM_EDIT_SAVE_LABEL } from '../constants/coolingSystemConfigurationsSubsectionCopy';
import { LARGE_UPS_EDIT_SAVE_LABEL } from '../constants/largeUpsConfigurationsSubsectionCopy';
import { LIQUID_COOLING_CDU_EDIT_SAVE_LABEL } from '../constants/liquidCoolingCduConfigurationsSubsectionCopy';
import { POWER_DISTRIBUTION_EDIT_SAVE_LABEL } from '../constants/powerDistributionConfigurationsSubsectionCopy';
import {
  RACK_DISCARD_UNSAVED_DISCARD_LABEL,
  RACK_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
  RACK_DISCARD_UNSAVED_MESSAGE,
  RACK_DISCARD_UNSAVED_TITLE,
  RACK_EDIT_SAVE_LABEL,
} from '../constants/rackConfigurationsSubsectionCopy';
import {
  getRequestFormNativeFooterPaddingBottom,
  REQUEST_FORM_NATIVE_FOOTER_CONTENT_HEIGHT,
  REQUEST_FORM_NATIVE_FOOTER_VERTICAL_PADDING,
} from '../constants/requestFormLayout';
import { SECTION_IDS } from '../constants/sectionIds';
import { SMALL_UPS_EDIT_SAVE_LABEL } from '../constants/smallUpsConfigurationsSubsectionCopy';
import { useFormEditing } from '../contexts/FormEditingContext';
import {
  dismissActiveRichTextKeyboard,
  FormKeyboardScrollProvider,
} from '../contexts/FormKeyboardScrollContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useRequestFrameToolbar } from '../contexts/RequestFrameContext';
import {
  useRegisterRequestLeaveGuard,
  useRegisterRequestStepChangeGuard,
} from '../contexts/RequestLeaveGuardContext';
import { useRequestSaveTransition } from '../contexts/RequestSaveTransitionContext';
import {
  useRegisterRequestScope,
  useRequestScope,
  type RequestScopeRegistration,
} from '../contexts/RequestScopeContext';
import { RequestStageProvider } from '../contexts/RequestStageContext';
import { useSidebarStepMetrics } from '../contexts/SidebarStepMetricsContext';
import { useUOM } from '../contexts/UOMContext';
import {
  applyAttachmentGroupedBugRequestOverlay,
  getAttachmentGroupedBugRequestOverlay,
  isAttachmentGroupedBugFixtureEnabled,
  mergeAttachmentGroupedBugFixture,
} from '../dev/attachmentGroupedViewBugFixture';
import {
  debugAttachmentScope,
  registerAttachmentScopeDebugGlobals,
} from '../dev/attachmentScopeDebug';
import {
  isFacilityPowerAttachmentFixtureEnabled,
  mergeFacilityPowerAttachmentFixture,
} from '../dev/facilityPowerFamilyAttachmentFixture';
import { useApprovalGroup } from '../hooks/queries/useApprovalGroup';
import { useJobStatus } from '../hooks/queries/useJobStatus';
import {
  useOfflineRequestQuery,
  useOfflineUpdateRequest,
  useOfflineDeleteRequest,
  offlineRequestsKeys,
} from '../hooks/queries/useOfflineRequestsQuery';
import { rackConfigurationsKeys } from '../hooks/queries/useRackConfigurations';
import { useRequestCorrespondenceState } from '../hooks/queries/useRequestCorrespondenceState';
import { useTranscribeExistingRequest } from '../hooks/queries/useTranscribeExistingRequest';
import { useApprovalStampOnlyAccess } from '../hooks/useApprovalStampOnlyAccess';
import { useAttachments } from '../hooks/useAttachments';
import { useAudioRecording } from '../hooks/useAudioRecording';
import { useButtonPermissions } from '../hooks/useButtonPermissions';
import { useCloseDraftRequest } from '../hooks/useCloseDraftRequest';
import { useCompactRequestFooter } from '../hooks/useCompactRequestFooter';
import { useConfirmationDialog } from '../hooks/useConfirmationDialog';
import { useCoolingSystemEngineSaveBridge } from '../hooks/useCoolingSystemEngineSaveBridge';
import {
  useDeliverablesEditSession,
  type DeliverablesFlushOutcome,
} from '../hooks/useDeliverablesEditSession';
import { useFormPermissions } from '../hooks/useFormPermissions';
import { useInlineImageUploadFooterProgress } from '../hooks/useInlineImageUploadFooterProgress';
import { useInlineImageUploadsInFlight } from '../hooks/useInlineImageUploadsInFlight';
import { useLargeUpsEngineSaveBridge } from '../hooks/useLargeUpsEngineSaveBridge';
import { useLiquidCoolingCduEngineSaveBridge } from '../hooks/useLiquidCoolingCduEngineSaveBridge';
import { useOfflineFileRestriction } from '../hooks/useOfflineFileRestriction';
import { useOfflineToast } from '../hooks/useOfflineToast';
import { usePowerDistributionEngineSaveBridge } from '../hooks/usePowerDistributionEngineSaveBridge';
import { useRacksEngineSaveBridge } from '../hooks/useRacksEngineSaveBridge';
import { useReconcileRepeatableSectionsAfterSync } from '../hooks/useReconcileRepeatableSectionsAfterSync';
import { useRecordReadOnImportCompletion } from '../hooks/useRecordReadOnImportCompletion';
import { useRecordRequestRead } from '../hooks/useRecordRequestRead';
import { useRejectRequest } from '../hooks/useRejectRequest';
import {
  useBeforeUnloadPersist,
  useDebouncedCallback,
} from '../hooks/useRequestDraftPersistence';
import { useResponsive } from '../hooks/useResponsive';
import { useSmallUpsEngineSaveBridge } from '../hooks/useSmallUpsEngineSaveBridge';
import { useStageUpdate } from '../hooks/useStageUpdate';
import { useStepVisibility } from '../hooks/useStepVisibility';
import {
  INSTANCE_UNIT_FIELDS,
  markDimensionUnitsDirty,
  remapInstanceUnitIds,
} from '../request/dimensionUnitChoices';
import { DimensionUnitsProvider } from '../request/dimensionUnits';
import { Summary } from '../request/steps/summary/Summary';
import {
  attemptAggregateSave,
  type AggregateFamilyBridges,
} from '../services/aggregateSaveOrchestrator';
import { api, ApiError } from '../services/api';
import { attachmentService } from '../services/attachmentService';
import { fieldPermissionService } from '../services/auth/fieldPermissionService';
import { requestAuthService } from '../services/auth/requestAuthService';
import { roleService } from '../services/auth/roleService';
import {
  fileUploadService,
  FileUploadProgress,
} from '../services/fileUploadService';
import { offlineCache } from '../services/offlineCache';
import { getPendingRackOpsForRequest } from '../services/rackOfflineSync';
import {
  clearEditRequestDraftSnapshot,
  readEditRequestDraftSnapshot,
  writeEditRequestDraftSnapshot,
  writeEditRequestDraftSnapshotSync,
  REQUEST_DRAFT_RESTORED_MESSAGE,
  type EditRequestDraftSnapshot,
  isBrowserReloadNavigation,
} from '../services/requestDraftSnapshot';
import { syncService } from '../services/syncService';
import { transcriptionService } from '../services/transcriptionService';
import { colors, fontSize, lineHeight, spacing } from '../theme';
import { AppRole } from '../types/auth';
import {
  RequestStage,
  getNextStage,
  getStageDisplayName,
  normalizeStage,
} from '../types/stages';
import { TranscriptionJobStatus } from '../types/transcription';
import { consumeAttachmentsUploadedHint } from '../utils/attachmentUploadHint';
import { generateAudioFilename, renameAudioFile } from '../utils/audioFilename';
import { findRedirectStepIdWhenSwitchingToBasic } from '../utils/basicViewStepRedirect';
import {
  getStageConfirmationCopy,
  shouldSkipDeliverablesDialog,
} from '../utils/deliverablesSubmitDialog';
import { canReadDeliverablesAtStage } from '../utils/deliverablesVisibility';
import {
  coerceDevicesUnitId,
  devicesEditorNavigation,
} from '../utils/devicesEditorNavigation';
import {
  applyDevicesStepReorder,
  type DevicesReorderSessionMap,
} from '../utils/devicesReorderRegistry';
import {
  normalizeRequestFormStepId,
  LEGACY_COMBINED_SITE_STEP_ID,
} from '../utils/formStepIds';
import { buildDetailFormSteps } from '../utils/formSteps';
import { type FormValidationError } from '../utils/formValidation';
import { normalizeRequestNumericFields } from '../utils/normalizeRequestNumericFields';
import { buildRemappedRequestRoute } from '../utils/offlineRequestRedirect';
import { remapSelectedFilesForRepeatableInstances } from '../utils/repeatableInstanceAttachmentSections';
import {
  REQUEST_DETAIL_AWAITING_REJECT_POLL_MS,
  shouldPollDetailForRejectionReadModel,
} from '../utils/requestDetailApprovalPoll';
import {
  commitRepeatableSaveTarget,
  resolveRepeatableSaveTarget,
  resolveRequestFooterSaveLabel,
} from '../utils/requestFooterSaveContext';
import {
  getRequestCorrespondenceLabel,
  getRequestDetailToolbarRequestNumber,
  getRequestDetailToolbarTitle,
} from '../utils/requestListDisplay';
import {
  resolveRequestorIsExternal,
  showsInternalStageDetail,
} from '../utils/requestorBoundary';
import {
  hasVertivSponsorSelection,
  requiresVertivSponsorForManualSave,
  requiresVertivSponsorForStageAdvance,
} from '../utils/requestSaveGates';
import {
  diffRemovedOptionalStepIds,
  expandRemovedStepIdsForDataClear,
  pruneDirtyFieldsForRemovedSteps,
  stripFormFieldsForRemovedSteps,
} from '../utils/requestScopeChange';
import { resolveEnabledStepIds } from '../utils/resolveRequestFormSteps';
import { waitForInlineImagePipelineIdle } from '../utils/richContent/inlineAttachmentName';
import {
  collectRequestRichHtmlAttachmentIds,
  removeRichHtmlAttachmentFromRequestData,
} from '../utils/richContent/richHtmlAttachmentRefs';
import { prepareTextAreaFieldsForTransport } from '../utils/richContent/sanitizeTextArea';
import {
  applyScopeRemovalSideEffects,
  purgeScopeRemovalCaches,
} from '../utils/scopeRemovalSideEffects';
import { shouldShowStepInSidebarNav } from '../utils/sidebarScopeStepVisibility';
import { countSupportingDocumentAttachments } from '../utils/supportingDocumentsCount';
import {
  buildTeamMembersFromEditedData,
  parsePartnerContributors,
  parseTeamMemberEmails,
  saveAllTeamMembersFromEditedData,
} from '../utils/teamMemberUtils';
import { showToast } from '../utils/toast';
import { opportunitySpecifySaveError } from '../validation/opportunityProjectFieldsSchema';
import {
  AlertCircle,
  Edit,
  Save,
  Archive,
  ChevronRight,
  Mic,
  TextIcon,
  XCircle,
} from './../components/icons';

import type { RepeatableInstanceSaveHandle } from '../components/repeatableSections/repeatableInstanceSaveHandle';
import type {
  OptionalRequestFormStepId,
  RequestFormStepId,
} from '../config/requestFormStepRegistry';
import type { AudioFile } from '../services/audio';

/**
 * Reports a save honestly when the deliverables part of it did not land.
 *
 * A deliverables refusal deliberately does not throw — one refused section must not abort a
 * save that also writes racks, UPS units and attachments — so every success toast has to ask
 * what the flush actually achieved. Without this the screen said "Changes saved
 * successfully!" over a `409` the server had just used to refuse the write, which is the
 * report-success-for-a-write-that-did-not-happen defect the rest of the change removes.
 * (MR !489 review, batch 1b.)
 *
 * The rest of the save is genuinely saved in that case, so the message says so rather than
 * calling the whole thing a failure.
 */
function reportSaveOutcome(
  deliverablesOutcome: DeliverablesFlushOutcome,
  successMessage: string,
): void {
  if (deliverablesOutcome === 'conflict') {
    showToast.error(
      'Your other changes were saved. The deliverables were not: they changed while you were editing. Reopen the request to see the current list.',
    );
    return;
  }
  if (deliverablesOutcome === 'failed') {
    showToast.error(
      'Your other changes were saved. The deliverables were not — open the Deliverables step and save them again.',
    );
    return;
  }
  showToast.success(successMessage);
}

function RequestDetailFormScroll({
  scrollViewRef,
  contentContainerStyle = styles.content,
  onLayout,
  scrollPaddingBottom,
  children,
}: {
  scrollViewRef: React.RefObject<ScrollView | null>;
  /**
   * Native keeps `styles.content` (the default). Web passes its row layout
   * and puts `styles.content` on the column inside it (spec 2026-09-07).
   */
  contentContainerStyle?: StyleProp<ViewStyle>;
  /** Web: measures the pane so the docked panel can be pinned to its height. */
  onLayout?: (event: LayoutChangeEvent) => void;
  /**
   * Web: height of the sticky footer band, applied as `scroll-padding-bottom`
   * on the scroll container so focus scrolling keeps the focused field above
   * the band (P3). Style prop, not a ref effect: react-native-web 0.21.2
   * compiles unknown longhands verbatim -- executed check, `atomic()` and
   * `inline()` both emit `scroll-padding-bottom:<n>px` -- so no DOM access is
   * needed. Cast like the existing web-only `overflow: 'hidden' as any`.
   */
  scrollPaddingBottom?: number;
  children: React.ReactNode;
}) {
  const { isTableDragActive } = useRecordTableDragScrollLock();
  return (
    <KeyboardAwareFormScrollView
      ref={scrollViewRef}
      style={[
        styles.scrollContainer,
        scrollPaddingBottom ? ({ scrollPaddingBottom } as any) : null,
      ]}
      contentContainerStyle={contentContainerStyle}
      onLayout={onLayout}
      showsVerticalScrollIndicator={true}
      bottomOffset={KEYBOARD_BOTTOM_OFFSET}
      scrollEnabled={!isTableDragActive}
    >
      {children}
    </KeyboardAwareFormScrollView>
  );
}
import type { CoolingSystemConfiguration } from '../types/coolingSystem';
import type { LargeUpsConfiguration } from '../types/largeUps';
import type { LiquidCoolingCduConfiguration } from '../types/liquidCoolingCdu';
import type { PowerDistributionConfiguration } from '../types/powerDistribution';
import type { RackConfiguration } from '../types/rack';
import type { SmallUpsConfiguration } from '../types/smallUps';
import type { OrderableUnit } from '../utils/applyVisibleUnitsReorder';
import type { DeviceSourceUnit, DeviceStepId } from '../utils/deviceRowMapper';

const isWeb = Platform.OS === 'web';

/**
 * Queue an offline team-member edit onto the request's own update queue.
 *
 * Both the Save path and the Submit path need this and used to differ: Save was gated on
 * `isOnline &&`, so offline it neither saved nor queued, and Submit still called the online
 * writer unconditionally. Same shape in one place so they cannot drift again.
 *
 * Sponsors and contributors are normalised to plain email arrays because that is what the
 * replay's `saveTeamMembersFromForm` expects; a queued JSON string would not survive it.
 * Partners keep their `{ email, accessLevel }` objects — collapsing them to emails is what
 * silently demoted a `read_write` partner to read-only. `useDefaultSponsors` and `region`
 * ride along so the replay resolves the same Default Sponsor role the online path would.
 */
const queueOfflineTeamMemberEdit = async (
  requestId: string,
  processedData: Record<string, any>,
): Promise<void> => {
  await offlineCache.addToOfflineUpdateQueue(
    requestId,
    {},
    {
      ...(processedData.vertivSponsors !== undefined && {
        vertivSponsors: parseTeamMemberEmails(processedData.vertivSponsors),
      }),
      ...(processedData.vertivContributors !== undefined && {
        vertivContributors: parseTeamMemberEmails(
          processedData.vertivContributors,
        ),
      }),
      ...(processedData.partnerContributors !== undefined && {
        partnerContributors: parsePartnerContributors(
          processedData.partnerContributors,
        ),
      }),
      ...(processedData.useDefaultSponsors !== undefined && {
        useDefaultSponsors: processedData.useDefaultSponsors,
      }),
      ...(processedData.region !== undefined && {
        region: processedData.region,
      }),
    },
  );
};

interface RequestDetailProps {
  navigate: (path: string) => void;
  sidebarCollapsed?: boolean; // Optional and unused but kept for interface compatibility
  requestId?: string; // Add optional requestId prop for mobile platforms
  currentStep?: string;
  onStepChange?: (stepId: string) => void;
  onStageChange?: (stage: string) => void;
  onEffectiveStageChange?: (effectiveStage: string | undefined) => void; // For basic/advanced view mode
}

const getQueryParam = (param: string): string | null => {
  if (Platform.OS === 'web') {
    const urlParams = new URLSearchParams(
      (globalThis as any).window?.location?.search || '',
    );
    return urlParams.get(param);
  }
  // On native, we need to parse query params from the currentRoute stored in navigation state
  // This is set by App.tsx when navigating
  try {
    const storedRoute = (global as any).__currentRoute || '';
    if (storedRoute.includes('?')) {
      const queryString = storedRoute.split('?')[1];
      const urlParams = new URLSearchParams(queryString);
      return urlParams.get(param);
    }
  } catch (error) {
    console.warn('Failed to parse query param on native:', error);
  }
  return null;
};

/**
 * The reject reason is capped by character count, so the banner shows it as one
 * paragraph: line breaks would let a short reason fill the screen.
 */
export function collapseLineBreaks(text: string): string {
  return text.replace(/\s*[\r\n]+\s*/g, ' ').trim();
}

/** EXPERIENCE.md email-subject split, reused as the Approve success toast. */
export function approveSuccessToastMessage(viewerIsRequestor: boolean): string {
  return viewerIsRequestor
    ? 'Your request was sent to Vertiv.'
    : 'Request sent to Vertiv.';
}

/** Vertiv force-through on awaiting uses Site Assessment Submit cells, not Draft. */
export function submitPermissionStageForDetail(
  currentStage: RequestStage,
  isPartnerViewer: boolean,
): RequestStage {
  if (currentStage === RequestStage.AWAITING_APPROVAL && !isPartnerViewer) {
    return RequestStage.SITE_ASSESSMENT;
  }
  return currentStage;
}

export const AWAITING_APPROVAL_SUBMIT_TOAST =
  'Waiting for approval. Vertiv cannot see this request yet.';

// Keep aligned with the server-side rejection reason validation.
export const REJECT_REASON_MAX_LENGTH = 255;
export const REJECT_REASON_ACCESSORY_VIEW_ID = 'reject-reason-keyboard-dismiss';

export const RequestDetail: React.FC<RequestDetailProps> = ({
  navigate,
  sidebarCollapsed: _sidebarCollapsed,
  requestId: propRequestId,
  currentStep = 'opportunity',
  onStepChange,
  onStageChange,
  onEffectiveStageChange,
}) => {
  const requestSaveTransition = useRequestSaveTransition();
  const stepId = normalizeRequestFormStepId(currentStep);

  /**
   * Correspondence panel (#1069). Open state lives here rather than in the
   * launcher, because when the panel docks the screen has to lay out around it.
   * Docking is width-gated — see `components/correspondence/dockLayout.ts` for
   * why the threshold sits above the 1200 request-layout breakpoint.
   */
  const { width: viewportWidth, height: viewportHeight } =
    useWindowDimensions();
  // CLOSED on arrival, on every viewport and every platform (#1069 Story 5.3).
  //
  // ⚠️ THIS REVERSES `63d38ac39`, WHICH OPENED IT AT DOCKED WIDTHS, and the
  // reason is recorded so nobody flips it a third time: the owner ran the
  // docked panel open-by-default and preferred arriving at the request itself.
  // `defaultDiscussionOpen` existed only to compute that default and was
  // deleted with it, rather than left as a wrapper returning a constant.
  // Docking is unaffected — `isDiscussionDocked` below still decides WHERE the
  // panel goes once opened; this decides only that it starts shut.
  //
  // ⚠️ ONE EXCEPTION, ADDED 2026-09-11 BY THE OWNER: the request-list marker
  // (`DiscussionRowIndicator`) opens the request WITH the panel showing, via
  // `?discussion=open` (`discussionEntry.ts`). Read once, on mount, so a later
  // close is not undone by a re-render; every other way in still arrives shut.
  //
  // `thread=` (#1599 Story 2.3) is the second half of that exception, same
  // discipline: read once, on mount, never written back, and honoured only
  // beside `discussion=open` (see `arrivalThreadId`, beside the session).
  const [discussionOpen, setDiscussionOpen] = useState(() =>
    arrivesWithDiscussionOpen(getQueryParam(DISCUSSION_QUERY_PARAM)),
  );
  const isDiscussionDocked = shouldDock(viewportWidth, Platform.OS === 'web');
  // ⚠️ A RESIZE NEVER OPENS OR CLOSES THE PANEL (owner ruling 2026-09-25,
  // `spec-1069-panel-survives-resize.md`). This SUPERSEDES the docked-panel
  // spec's P1 and Story 5.3 AC 117, which closed the panel on the docked ->
  // overlay edge so a narrowed window was not handed an overlay nobody asked
  // for; `useCloseOnUndock` was deleted with that rule. Open at one width is
  // open at every width, and widening never re-opens a closed panel.
  //
  // Staying open is not enough, because the docked and overlay panels are
  // two mounts: the user's PLACE (tab, conversation, draft, files, an edit or
  // a send in flight) lives in `discussionSession` below, one per request,
  // handed to BOTH. It also survives a close (ruling 2026-09-26).
  /**
   * Height of the scroll pane, handed to the dock so its sticky box stops at
   * the pane's bottom (spec 2026-09-07). Seeded from the window height so the
   * dock is bounded from the first frame, then replaced by the scroll view's
   * measured height; a zero or negative layout is ignored (P2).
   */
  const [dockPaneHeight, setDockPaneHeight] = useState<number>(viewportHeight);
  const handleDockPaneLayout = useCallback((event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    if (height > 0) setDockPaneHeight(height);
  }, []);
  /**
   * Height of the sticky footer band (web), applied to the scroll view as
   * `scroll-padding-bottom` so focus-driven scrolling (Tab into the last
   * field) lands the field above the band rather than under it (P3).
   */
  /**
   * Does a composer field in the DOCKED discussion panel hold focus?
   *
   * ⚠️ IT SWITCHES `scrollPaddingBottom` OFF, AND THAT IS THE WHOLE POINT
   * (defect 3, 2026-09-15). The allowance below exists so Tab-into-the-last
   * field lands above the sticky footer band. But it applies to EVERY
   * scroll-into-view in this scroller, and since the spec of 2026-09-07 the
   * docked panel shares the scroller with the request — so the composer's
   * caret, which sits at the bottom edge of its box and therefore inside the
   * band, made every keystroke ask this scroller to reveal it. The panel is
   * `position: sticky`: scrolling cannot move it, the caret stays in the band,
   * and the request content slid under the panel a line per character until
   * the scroller hit its end.
   *
   * Measured in the running app, 2026-09-15: 40 characters moved the page
   * 220px over 14 scroll events; with the allowance at 0, the same 40
   * characters moved it 0px over 0 events.
   *
   * Only the composer's own fields switch it off, and only while they hold
   * focus, so every request field keeps the band clearance it was given.
   */
  const [composerFocused, setComposerFocused] = useState(false);
  const [footerBandHeight, setFooterBandHeight] = useState(0);
  const handleFooterBandLayout = useCallback((event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    if (height >= 0) setFooterBandHeight(Math.ceil(height));
  }, []);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<any>({});
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());
  const [opportunityValidationErrors, setOpportunityValidationErrors] =
    useState<FormValidationError[]>([]);
  const [
    opportunityRhfValidationRevision,
    setOpportunityRhfValidationRevision,
  ] = useState(0);
  const [rackSessionDirty, setRackSessionDirty] = useState(false);
  const [smallUpsSessionDirty, setSmallUpsSessionDirty] = useState(false);
  const [largeUpsSessionDirty, setLargeUpsSessionDirty] = useState(false);
  const [powerDistributionSessionDirty, setPowerDistributionSessionDirty] =
    useState(false);
  const [coolingSystemSessionDirty, setCoolingSystemSessionDirty] =
    useState(false);
  const [liquidCoolingCduSessionDirty, setLiquidCoolingCduSessionDirty] =
    useState(false);
  const [rackEditorOpen, setRackEditorOpen] = useState(false);
  const instanceSaveHandleRef = useRef<RepeatableInstanceSaveHandle | null>(
    null,
  );
  const [instanceEditorFooterLabel, setInstanceEditorFooterLabel] = useState<
    string | null
  >(null);
  const [smallUpsEditorOpen, setSmallUpsEditorOpen] = useState(false);
  const [largeUpsEditorOpen, setLargeUpsEditorOpen] = useState(false);
  const [powerDistributionEditorOpen, setPowerDistributionEditorOpen] =
    useState(false);
  const [coolingSystemEditorOpen, setCoolingSystemEditorOpen] = useState(false);
  const [liquidCoolingCduEditorOpen, setLiquidCoolingCduEditorOpen] =
    useState(false);
  const handleRackSessionDirtyChange = useCallback((dirty: boolean) => {
    setRackSessionDirty(dirty);
  }, []);
  const handleSmallUpsSessionDirtyChange = useCallback((dirty: boolean) => {
    setSmallUpsSessionDirty(dirty);
  }, []);
  const handleLargeUpsSessionDirtyChange = useCallback((dirty: boolean) => {
    setLargeUpsSessionDirty(dirty);
  }, []);
  const handlePowerDistributionSessionDirtyChange = useCallback(
    (dirty: boolean) => {
      setPowerDistributionSessionDirty(dirty);
    },
    [],
  );
  const handleCoolingSystemSessionDirtyChange = useCallback(
    (dirty: boolean) => {
      setCoolingSystemSessionDirty(dirty);
    },
    [],
  );
  const handleLiquidCoolingCduSessionDirtyChange = useCallback(
    (dirty: boolean) => {
      setLiquidCoolingCduSessionDirty(dirty);
    },
    [],
  );
  const handleRackEditorActiveChange = useCallback((active: boolean) => {
    setRackEditorOpen(active);
  }, []);
  const registerInstanceSaveHandle = useCallback(
    (handle: RepeatableInstanceSaveHandle | null) => {
      instanceSaveHandleRef.current = handle;
    },
    [],
  );
  const handleInstanceEditorActiveChange = useCallback(
    (active: boolean, footerSaveLabel?: string) => {
      setInstanceEditorFooterLabel(
        active && footerSaveLabel ? footerSaveLabel : null,
      );
    },
    [],
  );
  const handleSmallUpsEditorActiveChange = useCallback((active: boolean) => {
    setSmallUpsEditorOpen(active);
  }, []);
  const handleLargeUpsEditorActiveChange = useCallback((active: boolean) => {
    setLargeUpsEditorOpen(active);
  }, []);
  const handlePowerDistributionEditorActiveChange = useCallback(
    (active: boolean) => {
      setPowerDistributionEditorOpen(active);
    },
    [],
  );
  const handleCoolingSystemEditorActiveChange = useCallback(
    (active: boolean) => {
      setCoolingSystemEditorOpen(active);
    },
    [],
  );
  const handleLiquidCoolingCduEditorActiveChange = useCallback(
    (active: boolean) => {
      setLiquidCoolingCduEditorOpen(active);
    },
    [],
  );
  const repeatableSaveTarget = resolveRepeatableSaveTarget(stepId, {
    facilityPowerLabel: instanceEditorFooterLabel,
    rackOpen: rackEditorOpen,
    smallUpsOpen: smallUpsEditorOpen,
    largeUpsOpen: largeUpsEditorOpen,
    powerDistributionOpen: powerDistributionEditorOpen,
    coolingOpen: coolingSystemEditorOpen,
    liquidCoolingOpen: liquidCoolingCduEditorOpen,
  });
  const footerSaveLabel = resolveRequestFooterSaveLabel(
    repeatableSaveTarget,
    instanceEditorFooterLabel,
    {
      rack: RACK_EDIT_SAVE_LABEL,
      'small-ups': SMALL_UPS_EDIT_SAVE_LABEL,
      'large-ups': LARGE_UPS_EDIT_SAVE_LABEL,
      'power-distribution': POWER_DISTRIBUTION_EDIT_SAVE_LABEL,
      cooling: COOLING_SYSTEM_EDIT_SAVE_LABEL,
      'liquid-cooling': LIQUID_COOLING_CDU_EDIT_SAVE_LABEL,
    },
    Platform.OS === 'web' ? 'Save Changes' : 'Save',
  );
  const [selectedFiles, setSelectedFiles] = useState<
    Record<string, SelectedFile[]>
  >({});
  const [showStageChangeConfirmation, setShowStageChangeConfirmation] =
    useState(false);
  /**
   * Draft submit opens this instead of the stage-change confirmation (FR20). Every other
   * stage, including the Close path, keeps `showStageChangeConfirmation` untouched. It is a live
   * view of the request's deliverables — nothing is staged in it, so nothing has to be
   * carried across a failed submit and nothing has to be cleared after a committed one.
   */
  const [showDeliverablesDialog, setShowDeliverablesDialog] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  /** Draft close (stage -> CLOSED, no archive). Separate dialog from the Archive one. */
  const [showCloseDraftConfirmation, setShowCloseDraftConfirmation] =
    useState(false);
  const [showRejectConfirmation, setShowRejectConfirmation] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectReasonError, setRejectReasonError] = useState<string | null>(
    null,
  );
  const [originalRequestorEmail, setOriginalRequestorEmail] = useState<
    string | null
  >(null);
  const [isAdvancedView, setIsAdvancedView] = useState(false);
  const advancedViewDefaultAppliedRef = useRef(false);
  const [isRefetchingAfterSave, setIsRefetchingAfterSave] = useState(false);
  const [uploadProgress, setUploadProgress] =
    useState<FileUploadProgress | null>(null);
  useInlineImageUploadFooterProgress(setUploadProgress);
  const [isManualSaving, setIsManualSaving] = useState(false);
  const inlineImageUploadsInFlight = useInlineImageUploadsInFlight();
  const [pendingCaptionEdits, setPendingCaptionEdits] = useState<
    Record<number, string>
  >({});
  // Edit-draft restore (#1114 / #997 review): the baseline captured when a
  // restored rack session was snapshotted — seeds the bridge's held baseline
  // so a stale restored session is not diffed against restore-time-fresh
  // server truth (which would DELETE racks added since the snapshot).
  const [restoredRackBaseline, setRestoredRackBaseline] = useState<
    RackConfiguration[] | null
  >(null);
  const [restoredSmallUpsBaseline, setRestoredSmallUpsBaseline] = useState<
    SmallUpsConfiguration[] | null
  >(null);
  const [restoredLargeUpsBaseline, setRestoredLargeUpsBaseline] = useState<
    LargeUpsConfiguration[] | null
  >(null);
  const [restoredCoolingSystemBaseline, setRestoredCoolingSystemBaseline] =
    useState<CoolingSystemConfiguration[] | null>(null);
  const [
    restoredPowerDistributionBaseline,
    setRestoredPowerDistributionBaseline,
  ] = useState<PowerDistributionConfiguration[] | null>(null);
  const [
    restoredLiquidCoolingCduBaseline,
    setRestoredLiquidCoolingCduBaseline,
  ] = useState<LiquidCoolingCduConfiguration[] | null>(null);
  const [pendingEditDraft, setPendingEditDraft] =
    useState<EditRequestDraftSnapshot | null>(null);
  const editDraftHydratedRef = useRef(false);
  const editDraftPersistSuspendedRef = useRef(false);

  // Processing modal state for in-app recording transcription
  const [processingModalVisible, setProcessingModalVisible] = useState(false);
  // Text intake (Paste text) keeps the completion modal open until dismissed;
  // audio dictation keeps the default 30s auto-close.
  const [processingModalAutoClose, setProcessingModalAutoClose] =
    useState(true);
  const [processingFileName, setProcessingFileName] = useState<string>();

  // Text intake (Paste text) modal state
  const [textIntakeModalVisible, setTextIntakeModalVisible] = useState(false);
  const [textIntakeSubmitting, setTextIntakeSubmitting] = useState(false);

  // Background processing tracking (independent of modal visibility)
  // Allows polling to continue even after modal is closed
  const [backgroundProcessingRequestId, setBackgroundProcessingRequestId] =
    useState<number>();

  // Audio recording state
  const [showRecordingWarning, setShowRecordingWarning] = useState(false);

  // Audio recording hook
  const {
    recordingState,
    elapsedTime,
    handleStartRecording,
    handlePauseRecording,
    handleResumeRecording,
    handleAcceptRecording,
    handleDiscardRecording,
    forceCancelRecording,
  } = useAudioRecording();

  // Cleanup recording on unmount (navigating away from this screen)
  useEffect(() => {
    return () => {
      // Force cancel any active recording when component unmounts
      // This handles cases where user navigates away during recording
      forceCancelRecording().then(() => {
        // Note: We can't show a toast here reliably as the component is unmounting
        // The navigation cleanup in App.tsx will handle the toast
      });
    };
  }, [forceCancelRecording]);

  // Transcription mutation for existing requests
  const transcribeExistingMutation = useTranscribeExistingRequest();

  const { uom, setUOM } = useUOM();
  const { isSmallScreen } = useResponsive();
  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();
  // ⚠️ READ HERE, NOT FROM THE MODULE-SCOPE `isWeb` (2026-09-25). That one is
  // frozen at import, where `setupTests.ts` has set 'ios', so no suite could
  // ever render this screen's web layout: the docked panel's real host, the
  // web scroll row, went untested (`correspondence.md`, "A module-scope
  // `Platform.OS` read voids the entire web contract"). Constant in the app,
  // so behaviour-neutral there. The stylesheet keeps the module value.
  const isWebLayout = Platform.OS === 'web';
  const nativeFooterPaddingBottom = !isWebLayout
    ? getRequestFormNativeFooterPaddingBottom(safeAreaBottomInset)
    : undefined;
  const { shouldDisableUploads } = useOfflineFileRestriction();
  const { setEditingScreen, setIsEditing: setFormIsEditing } = useFormEditing();
  const scrollViewRef = useRef<ScrollView>(null);

  // Get request ID from props (mobile) or URL parameters (web)
  const requestId = propRequestId || getQueryParam('id');

  // Get form permissions with requestId for partner contributor access level checks
  const { user, isITAdministrator } = useFormPermissions(
    undefined,
    requestId || undefined,
  );
  // The discussion panel's session: one per request and viewer (an
  // impersonation swap is a different person's place), never subscribed to
  // here, so typing in the composer does not re-render this screen. See the
  // note beside `discussionOpen`.
  //
  // #1599 Story 2.3: the conversation a mention link named, read ONCE on
  // mount and handed to the session, which the panel's resolver consumes.
  // Only beside `discussion=open` (a `thread=` alone is not a way in), and
  // only while the URL still names THIS request, so the seed never follows
  // the screen onto another one (the offline-id remap). Never read back here:
  // the screen does not subscribe to the session.
  const [arrivalThreadId] = useState(() =>
    arrivesWithDiscussionOpen(getQueryParam(DISCUSSION_QUERY_PARAM)) &&
    getQueryParam('id') === requestId
      ? arrivalThreadIdFrom(getQueryParam(THREAD_QUERY_PARAM))
      : null,
  );
  const discussionSession = useCorrespondenceSessionFor(
    requestId ?? undefined,
    user?.id,
    arrivalThreadId,
  );

  /**
   * Deliverables are request fields, so they join the edit session: read-only until Edit,
   * held pending, committed by Save, discarded by Cancel. The session lives here rather
   * than in the step because the step is unmounted whenever the user is on another one, and
   * because the submit dialog is a second view of the same pending list.
   */
  const deliverablesSession = useDeliverablesEditSession(
    requestId || undefined,
  );
  const { saveHandle: deliverablesSaveHandle, discardPendingDeliverables } =
    deliverablesSession;
  /** Set when the submit dialog opens; called if it is cancelled. See the hook's snapshot. */
  const restoreDeliverablesOnDialogCancelRef = useRef<(() => void) | null>(
    null,
  );

  const queryClient = useQueryClient();

  // Refetch repeatable-section lists once an offline sync flushes their queued
  // ops, so optimistic offline copies reconcile with the synced server copies
  // without a manual refresh (fixes duplicate-until-refresh on reconnect).
  useReconcileRepeatableSectionsAfterSync();

  const persistDirtyFacilityPowerInstances = useCallback(
    async (instances: unknown[]) => {
      if (!requestId) {
        return;
      }
      await offlineCache.updateCachedRequestRepeatableConfigurations(
        requestId,
        { facilityPowerInstances: instances },
      );
      queryClient.setQueryData(
        offlineRequestsKeys.detail(requestId),
        (current: typeof request | undefined) =>
          current ? { ...current, facilityPowerInstances: instances } : current,
      );
    },
    [queryClient, requestId],
  );

  const {
    data: request,
    isLoading,
    error,
    isFetching,
    refetch: refetchRequestDetail,
  } = useOfflineRequestQuery(requestId || '');

  /**
   * FR54: does this request have a discussion waiting on this user, reconciled
   * with what the panel can see (#1069 P3.2, P3.3)? The query, the one
   * reconciliation point (`useUnreadSpaceState`) and the mark-read
   * invalidations live in `useRequestCorrespondenceState` (Story 2.2, D2) so
   * that the suites render exactly what this screen renders; every surface
   * below — the toggle, the panel's space tabs, the panel — reads the result.
   */
  const {
    spaceUnread: correspondenceSpaceUnread,
    hasUnread: correspondenceHasUnread,
    noteLocalAnswer: noteCorrespondenceSpaceUnread,
    handleThreadRead,
    spaceActivity: correspondenceSpaceActivity,
    accessRevoked: correspondenceAccessRevoked,
  } = useRequestCorrespondenceState(requestId || undefined, {
    // Story 2.3: the summary ticks every 10 s while the panel is open, and
    // its per-space clocks and the revoke signal go to BOTH panel mounts below.
    panelOpen: discussionOpen,
  });

  const storedEnabledStepIds = React.useMemo(() => {
    if (editedData.enabledStepIds !== undefined) {
      return editedData.enabledStepIds as string[] | null;
    }
    const bugFixtureOverlay = getAttachmentGroupedBugRequestOverlay();
    if (bugFixtureOverlay?.enabledStepIds) {
      return bugFixtureOverlay.enabledStepIds;
    }
    return request?.enabledStepIds ?? null;
  }, [editedData.enabledStepIds, request?.enabledStepIds]);

  const resolvedEnabledStepIds = React.useMemo(
    () => resolveEnabledStepIds(storedEnabledStepIds),
    [storedEnabledStepIds],
  );

  const detailSteps = React.useMemo(
    () => buildDetailFormSteps(storedEnabledStepIds),
    [storedEnabledStepIds],
  );

  const detailStepIds = React.useMemo(
    () => detailSteps.map(step => step.id),
    [detailSteps],
  );

  React.useEffect(() => {
    if (
      detailSteps.length > 0 &&
      stepId !== 'impersonation-audit' &&
      // Detail-only steps live outside the shared step registry, so without an exemption
      // here they bounce straight back to step 0 on arrival.
      stepId !== 'deliverables' &&
      stepId !== 'location-view' &&
      !detailSteps.some(step => step.id === stepId) &&
      onStepChange
    ) {
      onStepChange(detailSteps[0].id);
    }
  }, [detailSteps, onStepChange, stepId]);

  const rackLegacyRequestFields = React.useMemo(():
    | Record<string, unknown>
    | undefined => {
    if (!request) {
      return undefined;
    }
    return { ...(request as Record<string, unknown>), ...editedData };
  }, [request, editedData]);

  const isOfflineRequestId = Boolean(requestId?.startsWith('o-'));

  // #1575 FR-1/FR-2: the person has opened this request. Fire and forget — the
  // hook owns the once-per-id guard, the `o-` filter and the offline queueing,
  // and never rejects, so nothing here waits on it or can be broken by it.
  useRecordRequestRead(requestId);

  // Declared ahead of the racks bridge hook, which consumes it for the
  // post-flush session write-back (stable setters only — safe to hoist).
  const applyEditedDataPatch = useCallback((patch: Record<string, unknown>) => {
    setEditedData((prev: Record<string, unknown>) => ({ ...prev, ...patch }));
    setDirtyFields(prev => {
      const next = new Set(prev);
      for (const key of Object.keys(patch)) {
        next.add(key);
      }
      return next;
    });
  }, []);
  // #997 Story 6.3 Phase 4b — the Racks step runs on the descriptor engine.
  // Session continuity lives in editedData.rackConfigurations (engine bridge);
  // this hook owns the save-time REST flush (held edit-session baseline vs
  // editedData) and delegates editor-open ops to the engine's save handle.
  const {
    saveHandle: rackSaveHandle,
    registerInstanceSaveHandle: registerRackInstanceSaveHandle,
    sessionBaseline: rackSessionBaseline,
  } = useRacksEngineSaveBridge({
    requestId,
    isEditing,
    isOfflineRequest: isOfflineRequestId || shouldDisableUploads,
    legacyRequestFields: rackLegacyRequestFields,
    persistedEmbedRacks: (request as Record<string, unknown> | undefined)
      ?.rackConfigurations,
    editedRackConfigurations: editedData?.rackConfigurations,
    rackConfigurationsDirty: dirtyFields.has('rackConfigurations'),
    applyEditedDataPatch,
    restoredBaseline: restoredRackBaseline,
    onDirtyChange: handleRackSessionDirtyChange,
  });
  // #997 Story 6.4 Phase 4b — the Small UPS step runs on the descriptor engine.
  // Session continuity lives in editedData.smallUpsConfigurations (engine
  // bridge); this hook owns the save-time REST flush + Obs 6 created-id map.
  const {
    saveHandle: smallUpsSaveHandle,
    registerInstanceSaveHandle: registerSmallUpsInstanceSaveHandle,
    sessionBaseline: smallUpsSessionBaseline,
  } = useSmallUpsEngineSaveBridge({
    requestId,
    isEditing,
    isOfflineRequest: isOfflineRequestId || shouldDisableUploads,
    legacyRequestFields: rackLegacyRequestFields,
    persistedEmbedSmallUps: (request as Record<string, unknown> | undefined)
      ?.smallUpsConfigurations,
    editedSmallUpsConfigurations: editedData?.smallUpsConfigurations,
    smallUpsConfigurationsDirty: dirtyFields.has('smallUpsConfigurations'),
    applyEditedDataPatch,
    restoredBaseline: restoredSmallUpsBaseline,
    onDirtyChange: handleSmallUpsSessionDirtyChange,
  });
  // #997 Story 6.5 — the Large UPS step runs on the descriptor engine. Session
  // continuity lives in editedData.largeUpsConfigurations (engine bridge); this
  // hook owns the save-time REST flush + Obs 6 created-id map.
  const {
    saveHandle: largeUpsSaveHandle,
    registerInstanceSaveHandle: registerLargeUpsInstanceSaveHandle,
    sessionBaseline: largeUpsSessionBaseline,
  } = useLargeUpsEngineSaveBridge({
    requestId,
    isEditing,
    isOfflineRequest: isOfflineRequestId || shouldDisableUploads,
    legacyRequestFields: rackLegacyRequestFields,
    persistedEmbedLargeUps: (request as Record<string, unknown> | undefined)
      ?.largeUpsConfigurations,
    editedLargeUpsConfigurations: editedData?.largeUpsConfigurations,
    largeUpsConfigurationsDirty: dirtyFields.has('largeUpsConfigurations'),
    applyEditedDataPatch,
    restoredBaseline: restoredLargeUpsBaseline,
    onDirtyChange: handleLargeUpsSessionDirtyChange,
  });
  // #997 Story 6.7 — the Power Distribution step runs on the descriptor engine.
  // Session continuity lives in editedData.powerDistributionConfigurations (engine
  // bridge); this hook owns the save-time REST flush + Obs 6 created-id map.
  const {
    saveHandle: powerDistributionSaveHandle,
    registerInstanceSaveHandle: registerPowerDistributionInstanceSaveHandle,
    sessionBaseline: powerDistributionSessionBaseline,
  } = usePowerDistributionEngineSaveBridge({
    requestId,
    isEditing,
    isOfflineRequest: isOfflineRequestId || shouldDisableUploads,
    legacyRequestFields: rackLegacyRequestFields,
    persistedEmbedPowerDistribution: (
      request as Record<string, unknown> | undefined
    )?.powerDistributionConfigurations,
    editedPowerDistributionConfigurations:
      editedData?.powerDistributionConfigurations,
    powerDistributionConfigurationsDirty: dirtyFields.has(
      'powerDistributionConfigurations',
    ),
    applyEditedDataPatch,
    restoredBaseline: restoredPowerDistributionBaseline,
    onDirtyChange: handlePowerDistributionSessionDirtyChange,
  });
  // #997 Story 6.6 — the Cooling step runs on the descriptor engine. Session
  // continuity lives in editedData.coolingSystemConfigurations (engine bridge);
  // this hook owns the save-time REST flush + Obs 6 created-id map.
  const {
    saveHandle: coolingSystemSaveHandle,
    registerInstanceSaveHandle: registerCoolingSystemInstanceSaveHandle,
    sessionBaseline: coolingSystemSessionBaseline,
  } = useCoolingSystemEngineSaveBridge({
    requestId,
    isEditing,
    isOfflineRequest: isOfflineRequestId || shouldDisableUploads,
    legacyRequestFields: rackLegacyRequestFields,
    persistedEmbedCoolingSystems: (
      request as Record<string, unknown> | undefined
    )?.coolingSystemConfigurations,
    editedCoolingSystemConfigurations: editedData?.coolingSystemConfigurations,
    coolingSystemConfigurationsDirty: dirtyFields.has(
      'coolingSystemConfigurations',
    ),
    applyEditedDataPatch,
    restoredBaseline: restoredCoolingSystemBaseline,
    onDirtyChange: handleCoolingSystemSessionDirtyChange,
  });
  // #997 Story 6.8 — the Liquid Cooling step runs on the descriptor engine.
  // Session continuity lives in editedData.liquidCoolingCduConfigurations (engine
  // bridge); this hook owns the save-time REST flush + Obs 6 created-id map.
  const {
    saveHandle: liquidCoolingCduSaveHandle,
    registerInstanceSaveHandle: registerLiquidCoolingCduInstanceSaveHandle,
    sessionBaseline: liquidCoolingCduSessionBaseline,
  } = useLiquidCoolingCduEngineSaveBridge({
    requestId,
    isEditing,
    isOfflineRequest: isOfflineRequestId || shouldDisableUploads,
    legacyRequestFields: rackLegacyRequestFields,
    persistedEmbedLiquidCoolingCdus: (
      request as Record<string, unknown> | undefined
    )?.liquidCoolingCduConfigurations,
    editedLiquidCoolingCduConfigurations:
      editedData?.liquidCoolingCduConfigurations,
    liquidCoolingCduConfigurationsDirty: dirtyFields.has(
      'liquidCoolingCduConfigurations',
    ),
    applyEditedDataPatch,
    restoredBaseline: restoredLiquidCoolingCduBaseline,
    onDirtyChange: handleLiquidCoolingCduSessionDirtyChange,
  });
  const {
    setFormValues,
    setRackUnitCount,
    setFacilityPowerUnitCount,
    setSupportingDocumentCount,
    setShowFullDetails,
    setRequestStage,
    setRequestId,
  } = useSidebarStepMetrics();
  const mergedSidebarFormValues = React.useMemo((): Record<
    string,
    unknown
  > | null => {
    if (!request) {
      return null;
    }
    // All six repeatable-section sessions (Racks, Small UPS, Large UPS, Cooling,
    // Power Distribution, Liquid Cooling) now live in editedData (engine bridge),
    // already merged into `base` — no separate session patch (#997 Story 6.8).
    return { ...(request as Record<string, unknown>), ...editedData };
  }, [request, editedData]);

  useEffect(() => {
    setFormValues(mergedSidebarFormValues);
  }, [mergedSidebarFormValues, setFormValues]);

  useEffect(() => {
    return () => setFormValues(null);
  }, [setFormValues]);

  useEffect(() => {
    const editedRacks = editedData?.rackConfigurations;
    if (isEditing && Array.isArray(editedRacks)) {
      setRackUnitCount(editedRacks.length);
      return;
    }
    setRackUnitCount(undefined);
  }, [isEditing, editedData?.rackConfigurations, setRackUnitCount]);

  useEffect(() => {
    if (!isEditing) {
      setFacilityPowerUnitCount(undefined);
      return;
    }
    const merged = {
      ...(request as Record<string, unknown> | undefined),
      ...editedData,
    };
    const instances = merged.facilityPowerInstances;
    if (Array.isArray(instances)) {
      setFacilityPowerUnitCount(instances.length);
      return;
    }
    setFacilityPowerUnitCount(undefined);
  }, [isEditing, editedData, request, setFacilityPowerUnitCount]);

  useEffect(() => {
    if (!requestId || !user?.email) {
      setPendingEditDraft(null);
      setRestoredRackBaseline(null);
      setRestoredSmallUpsBaseline(null);
      setRestoredLargeUpsBaseline(null);
      setRestoredCoolingSystemBaseline(null);
      setRestoredPowerDistributionBaseline(null);
      setRestoredLiquidCoolingCduBaseline(null);
      editDraftHydratedRef.current = false;
      return;
    }
    setRestoredRackBaseline(null);
    setRestoredSmallUpsBaseline(null);
    setRestoredLargeUpsBaseline(null);
    setRestoredCoolingSystemBaseline(null);
    setRestoredPowerDistributionBaseline(null);
    setRestoredLiquidCoolingCduBaseline(null);
    editDraftHydratedRef.current = false;
    void (async () => {
      const draft = await readEditRequestDraftSnapshot(user.email, requestId);
      setPendingEditDraft(draft);
    })();
  }, [requestId, user?.email]);

  // #997 Story 6.3 Phase 4b — rack edits now live in editedData.rackConfigurations
  // (dirty-tracked like any other field), so the draft snapshot no longer carries
  // a separate `rackSession`; old snapshots' rackSession is merged into editedData
  // at hydration for backward compatibility.
  const persistEditDraft = useCallback(() => {
    if (editDraftPersistSuspendedRef.current) {
      return;
    }
    if (!user?.email || !requestId || !isEditing) {
      return;
    }
    const hasChanges =
      dirtyFields.size > 0 ||
      rackSessionDirty ||
      smallUpsSessionDirty ||
      largeUpsSessionDirty ||
      coolingSystemSessionDirty ||
      powerDistributionSessionDirty ||
      liquidCoolingCduSessionDirty ||
      Object.keys(pendingCaptionEdits).length > 0;
    if (!hasChanges) {
      void clearEditRequestDraftSnapshot(user.email, requestId);
      return;
    }
    void writeEditRequestDraftSnapshot(user.email, {
      requestId,
      stepId,
      editedData,
      dirtyFields: [...dirtyFields],
      // #997 review: persist the held baseline WITH the session so a restored
      // (stale) session diffs against the truth it was edited from — not
      // restore-time-fresh truth (which would DELETE racks added since).
      rackSession:
        dirtyFields.has('rackConfigurations') &&
        rackSessionBaseline &&
        Array.isArray(editedData?.rackConfigurations)
          ? {
              baselineRacks: rackSessionBaseline,
              sessionRacks: editedData.rackConfigurations,
            }
          : undefined,
      smallUpsSession:
        dirtyFields.has('smallUpsConfigurations') &&
        smallUpsSessionBaseline &&
        Array.isArray(editedData?.smallUpsConfigurations)
          ? {
              baselineSmallUps: smallUpsSessionBaseline,
              sessionSmallUps:
                editedData.smallUpsConfigurations as SmallUpsConfiguration[],
            }
          : undefined,
      largeUpsSession:
        dirtyFields.has('largeUpsConfigurations') &&
        largeUpsSessionBaseline &&
        Array.isArray(editedData?.largeUpsConfigurations)
          ? {
              baselineLargeUps: largeUpsSessionBaseline,
              sessionLargeUps:
                editedData.largeUpsConfigurations as LargeUpsConfiguration[],
            }
          : undefined,
      coolingSystemSession:
        dirtyFields.has('coolingSystemConfigurations') &&
        coolingSystemSessionBaseline &&
        Array.isArray(editedData?.coolingSystemConfigurations)
          ? {
              baselineCoolingSystems: coolingSystemSessionBaseline,
              sessionCoolingSystems:
                editedData.coolingSystemConfigurations as CoolingSystemConfiguration[],
            }
          : undefined,
      powerDistributionSession:
        dirtyFields.has('powerDistributionConfigurations') &&
        powerDistributionSessionBaseline &&
        Array.isArray(editedData?.powerDistributionConfigurations)
          ? {
              baselinePowerDistribution: powerDistributionSessionBaseline,
              sessionPowerDistribution:
                editedData.powerDistributionConfigurations as PowerDistributionConfiguration[],
            }
          : undefined,
      liquidCoolingCduSession:
        dirtyFields.has('liquidCoolingCduConfigurations') &&
        liquidCoolingCduSessionBaseline &&
        Array.isArray(editedData?.liquidCoolingCduConfigurations)
          ? {
              baselineLiquidCoolingCdus: liquidCoolingCduSessionBaseline,
              sessionLiquidCoolingCdus:
                editedData.liquidCoolingCduConfigurations as LiquidCoolingCduConfiguration[],
            }
          : undefined,
      pendingCaptionEdits:
        Object.keys(pendingCaptionEdits).length > 0
          ? pendingCaptionEdits
          : undefined,
    });
  }, [
    user?.email,
    requestId,
    isEditing,
    dirtyFields,
    rackSessionDirty,
    rackSessionBaseline,
    smallUpsSessionDirty,
    smallUpsSessionBaseline,
    largeUpsSessionDirty,
    largeUpsSessionBaseline,
    coolingSystemSessionDirty,
    coolingSystemSessionBaseline,
    powerDistributionSessionDirty,
    powerDistributionSessionBaseline,
    liquidCoolingCduSessionDirty,
    liquidCoolingCduSessionBaseline,
    pendingCaptionEdits,
    stepId,
    editedData,
  ]);

  const persistEditDraftSync = useCallback(() => {
    if (!user?.email || !requestId || !isEditing) {
      return;
    }
    const hasChanges =
      dirtyFields.size > 0 ||
      rackSessionDirty ||
      smallUpsSessionDirty ||
      largeUpsSessionDirty ||
      coolingSystemSessionDirty ||
      powerDistributionSessionDirty ||
      liquidCoolingCduSessionDirty ||
      Object.keys(pendingCaptionEdits).length > 0;
    if (!hasChanges) {
      return;
    }
    writeEditRequestDraftSnapshotSync(user.email, {
      requestId,
      stepId,
      editedData,
      dirtyFields: [...dirtyFields],
      // #997 review: persist the held baseline WITH the session so a restored
      // (stale) session diffs against the truth it was edited from — not
      // restore-time-fresh truth (which would DELETE racks added since).
      rackSession:
        dirtyFields.has('rackConfigurations') &&
        rackSessionBaseline &&
        Array.isArray(editedData?.rackConfigurations)
          ? {
              baselineRacks: rackSessionBaseline,
              sessionRacks: editedData.rackConfigurations,
            }
          : undefined,
      smallUpsSession:
        dirtyFields.has('smallUpsConfigurations') &&
        smallUpsSessionBaseline &&
        Array.isArray(editedData?.smallUpsConfigurations)
          ? {
              baselineSmallUps: smallUpsSessionBaseline,
              sessionSmallUps:
                editedData.smallUpsConfigurations as SmallUpsConfiguration[],
            }
          : undefined,
      largeUpsSession:
        dirtyFields.has('largeUpsConfigurations') &&
        largeUpsSessionBaseline &&
        Array.isArray(editedData?.largeUpsConfigurations)
          ? {
              baselineLargeUps: largeUpsSessionBaseline,
              sessionLargeUps:
                editedData.largeUpsConfigurations as LargeUpsConfiguration[],
            }
          : undefined,
      coolingSystemSession:
        dirtyFields.has('coolingSystemConfigurations') &&
        coolingSystemSessionBaseline &&
        Array.isArray(editedData?.coolingSystemConfigurations)
          ? {
              baselineCoolingSystems: coolingSystemSessionBaseline,
              sessionCoolingSystems:
                editedData.coolingSystemConfigurations as CoolingSystemConfiguration[],
            }
          : undefined,
      powerDistributionSession:
        dirtyFields.has('powerDistributionConfigurations') &&
        powerDistributionSessionBaseline &&
        Array.isArray(editedData?.powerDistributionConfigurations)
          ? {
              baselinePowerDistribution: powerDistributionSessionBaseline,
              sessionPowerDistribution:
                editedData.powerDistributionConfigurations as PowerDistributionConfiguration[],
            }
          : undefined,
      liquidCoolingCduSession:
        dirtyFields.has('liquidCoolingCduConfigurations') &&
        liquidCoolingCduSessionBaseline &&
        Array.isArray(editedData?.liquidCoolingCduConfigurations)
          ? {
              baselineLiquidCoolingCdus: liquidCoolingCduSessionBaseline,
              sessionLiquidCoolingCdus:
                editedData.liquidCoolingCduConfigurations as LiquidCoolingCduConfiguration[],
            }
          : undefined,
      pendingCaptionEdits:
        Object.keys(pendingCaptionEdits).length > 0
          ? pendingCaptionEdits
          : undefined,
    });
  }, [
    user?.email,
    requestId,
    isEditing,
    dirtyFields,
    rackSessionDirty,
    rackSessionBaseline,
    smallUpsSessionDirty,
    smallUpsSessionBaseline,
    largeUpsSessionDirty,
    largeUpsSessionBaseline,
    coolingSystemSessionDirty,
    coolingSystemSessionBaseline,
    powerDistributionSessionDirty,
    powerDistributionSessionBaseline,
    liquidCoolingCduSessionDirty,
    liquidCoolingCduSessionBaseline,
    pendingCaptionEdits,
    stepId,
    editedData,
  ]);

  useDebouncedCallback(
    persistEditDraft,
    [
      editedData,
      dirtyFields,
      rackSessionDirty,
      smallUpsSessionDirty,
      largeUpsSessionDirty,
      coolingSystemSessionDirty,
      powerDistributionSessionDirty,
      liquidCoolingCduSessionDirty,
      pendingCaptionEdits,
      stepId,
      user?.email,
      requestId,
      isEditing,
    ],
    Boolean(user?.email && requestId && isEditing),
  );

  useBeforeUnloadPersist(
    persistEditDraftSync,
    Boolean(user?.email && requestId && isEditing),
  );

  const rawRequestStageForPreload = request?.stage || request?.sysRequestStage;
  const requestStageForPreload = rawRequestStageForPreload
    ? normalizeStage(rawRequestStageForPreload)
    : null;
  const updateRequestMutation = useOfflineUpdateRequest();
  const deleteRequestMutation = useOfflineDeleteRequest();
  const closeDraftMutation = useCloseDraftRequest();
  const rejectRequestMutation = useRejectRequest();
  const stageUpdateMutation = useStageUpdate();
  const { isOnline } = useOfflineToast();
  const requestScope = useRequestScope();

  // Preload partner contributor access level for faster field rendering
  const [accessLevelPreloaded, setAccessLevelPreloaded] = useState(false);

  // Load attachments for this request
  const {
    attachmentsBySection: rawAttachmentsBySection,
    refetch: refetchAttachments,
  } = useAttachments(requestId);
  const attachmentsBySection = useMemo(() => {
    const numericRequestId =
      requestId && !requestId.startsWith('o-')
        ? Number.parseInt(requestId, 10)
        : 1030;
    const resolvedId = Number.isNaN(numericRequestId) ? 1030 : numericRequestId;
    const withBugFixture = mergeAttachmentGroupedBugFixture(
      rawAttachmentsBySection,
      resolvedId,
    );
    if (isAttachmentGroupedBugFixtureEnabled()) {
      return withBugFixture;
    }
    return mergeFacilityPowerAttachmentFixture(withBugFixture, resolvedId);
  }, [rawAttachmentsBySection, requestId]);

  /**
   * Feed the sidebar's `Supporting Documents (N)` count (#1534).
   *
   * Derived from `attachmentsBySection` — the post-fixture map this screen also
   * hands the step — through the step's own row builder, so the nav number and
   * the step's `All Attachments (N)` header are the same number by
   * construction. Cleared on unmount so the count cannot bleed onto the next
   * request, the same discipline `setFormValues` already follows above.
   */
  const supportingDocumentCount = React.useMemo(
    () =>
      countSupportingDocumentAttachments({
        attachmentsBySection,
        requestScopeStoredStepIds: requestScope?.storedStepIds,
        requestEnabledStepIds: request?.enabledStepIds,
        requestStage: String(request?.stage ?? request?.sysRequestStage ?? ''),
      }),
    [
      attachmentsBySection,
      requestScope?.storedStepIds,
      request?.enabledStepIds,
      request?.stage,
      request?.sysRequestStage,
    ],
  );

  React.useEffect(() => {
    setSupportingDocumentCount(supportingDocumentCount);
  }, [supportingDocumentCount, setSupportingDocumentCount]);

  React.useEffect(() => {
    return () => setSupportingDocumentCount(undefined);
  }, [setSupportingDocumentCount]);

  React.useEffect(() => {
    registerAttachmentScopeDebugGlobals();
    debugAttachmentScope('RequestDetail.attachments', {
      requestId,
      requestEnabledStepIds: request?.enabledStepIds ?? null,
      requestScopeStoredStepIds: requestScope?.storedStepIds ?? null,
      bugFixtureEnabled: isAttachmentGroupedBugFixtureEnabled(),
      fpFixtureEnabled: isFacilityPowerAttachmentFixtureEnabled(),
      rawAttachmentsBySection,
      attachmentsBySection,
    });
  }, [
    requestId,
    request?.enabledStepIds,
    requestScope?.storedStepIds,
    rawAttachmentsBySection,
    attachmentsBySection,
  ]);

  // After Create Request save + file upload, refetch once more so Supporting Documents
  // grouped view and Summary appendix aren't empty on first paint (#8070).
  React.useEffect(() => {
    if (!requestId || requestId.startsWith('o-')) {
      return undefined;
    }
    if (!consumeAttachmentsUploadedHint(requestId)) {
      return undefined;
    }
    void refetchAttachments();
    const retryTimer = setTimeout(() => {
      void refetchAttachments();
    }, 800);
    return () => clearTimeout(retryTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot hint per requestId; refetchAttachments stable per id.
  }, [requestId]);

  const requestForSteps = useMemo(() => {
    if (!request) {
      return request;
    }
    return applyAttachmentGroupedBugRequestOverlay(request);
  }, [request]);

  // Check the transcription job while an import is actively being processed.
  // Offline and ordinary requests cannot have a job for this screen to track.
  const requestIdNum =
    requestId && !requestId.startsWith('o-')
      ? parseInt(requestId, 10)
      : undefined;
  const { data: jobStatus } = useJobStatus(requestIdNum, {
    // Ordinary requests have no transcription_jobs row. Probe only while this
    // screen is actively processing an import; otherwise every approval/reject
    // refetch produces an expected-but-misleading JOB_NOT_FOUND response.
    enabled: !!requestIdNum && !isNaN(requestIdNum) && processingModalVisible,
    refetchInterval: processingModalVisible ? 5000 : false, // Poll every 5 seconds when modal is visible
    retry: false, // Don't retry on 404 (no job exists) - this is expected for most requests
  });

  // Background polling for transcription jobs (continues even after modal closes)
  // This ensures the request data auto-refreshes when processing completes
  const { data: backgroundJobStatus } = useJobStatus(
    backgroundProcessingRequestId,
    {
      enabled: !!backgroundProcessingRequestId, // Only poll when actively tracking a job
      refetchInterval: 5000, // Poll every 5 seconds
      retry: false, // Don't retry on 404
    },
  );

  /**
   * #1575 (Kiran's issue 1, owner's rule of 2026-10-06): an import the person
   * watched land must not leave their own row unread.
   *
   * Both pollers are consulted because both are this request — every call that
   * starts background tracking passes `requestIdNum` — and they differ only in
   * whether the processing modal is still up. Either way the completion is the
   * moment the screen refreshes itself with the imported content, which is the
   * only honest evidence that the person saw it. The job id is part of the key
   * so a second import in the same visit fires again.
   */
  const completedImportJobKey =
    jobStatus?.status === TranscriptionJobStatus.COMPLETED
      ? `fg:${jobStatus.jobId ?? 'current'}`
      : backgroundJobStatus?.status === TranscriptionJobStatus.COMPLETED
      ? `bg:${backgroundJobStatus.jobId ?? 'current'}`
      : null;
  useRecordReadOnImportCompletion(requestId, completedImportJobKey);

  // Preload partner contributor access level AND field permissions when request loads
  useEffect(() => {
    // Reset preload state when request or stage changes
    setAccessLevelPreloaded(false);

    const preloadPermissions = async () => {
      if (!requestId || !user?.roles || !requestStageForPreload) {
        setAccessLevelPreloaded(true);
        return;
      }

      try {
        // Step 1: Preload access level for partner contributors
        if (user.roles.includes(AppRole.PARTNER_CONTRIBUTOR) && user.email) {
          const { teamInformationService } = await import(
            '../services/teamInformationService'
          );
          await teamInformationService.getPartnerContributorAccessLevel(
            requestId,
            user.email,
          );
        }

        // Step 2: Preload field permissions for the current stage
        // This populates the fieldAccessCache synchronously for instant rendering
        await fieldPermissionService.loadPermissions(requestStageForPreload);

        // Step 3: Pre-compute field access for all common sections to populate cache
        const sectionsToPreload = [
          'Opportunity Information',
          'Site Dimensions',
          'Racks',
          'Facility Power Readiness',
          'Rack Power',
          'Small UPS',
          'Cooling Readiness',
          'Liquid Cooling',
          'Customer IT Equipment',
          'Monitoring Management',
          'Supporting Documentation',
          'Feedback',
        ];

        // Sections are independent reads of the already-loaded stage matrix — preload
        // them in parallel rather than serially (#1469). Per-section catch keeps one
        // failed section from blocking the rest, exactly as the serial loop did.
        await Promise.all(
          sectionsToPreload.map(section =>
            fieldPermissionService
              .getAccessibleFields(
                user.roles,
                section,
                requestStageForPreload,
                requestId,
                user.email,
              )
              // This call populates the fieldAccessCache
              .catch(error => {
                console.error(`Failed to preload section ${section}:`, error);
              }),
          ),
        );

        setAccessLevelPreloaded(true);
      } catch (error) {
        console.error('Failed to preload permissions:', error);
        setAccessLevelPreloaded(true); // Continue anyway to avoid blocking UI
      }
    };

    preloadPermissions();
  }, [requestId, user?.email, user?.roles, requestStageForPreload]);

  // Sync local isEditing state with FormEditingContext
  // This prevents automatic query invalidation from disrupting form data during editing
  useEffect(() => {
    if (isEditing) {
      setEditingScreen('RequestDetail');
    } else {
      setFormIsEditing(false);
    }
    return () => {
      // Clear editing state when component unmounts
      setFormIsEditing(false);
    };
  }, [isEditing, setEditingScreen, setFormIsEditing]);

  // Scroll to top when step changes
  useEffect(() => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ y: 0, animated: false });
    }
  }, [stepId]);

  // Check for showProcessing URL parameter (from in-app recording acceptance)
  useEffect(() => {
    const showProcessing = getQueryParam('showProcessing');
    const fileName = getQueryParam('fileName');

    // Skip processing modal for offline requests (they can't have transcription jobs)
    if (showProcessing === 'true' && requestId && !requestId.startsWith('o-')) {
      const requestIdNum = parseInt(requestId, 10);
      if (!isNaN(requestIdNum)) {
        setProcessingModalAutoClose(true); // audio dictation: keep 30s auto-close
        setProcessingModalVisible(true);
        setProcessingFileName(fileName || undefined);
        setBackgroundProcessingRequestId(requestIdNum); // Start background polling
      }

      // Clean up URL parameters after reading them
      if (Platform.OS === 'web') {
        const newUrl = window.location.pathname + `?id=${requestId}`;
        window.history.replaceState({}, '', newUrl);
      } else {
        // On native, clear the global route to remove query params
        const newRoute = `/request-detail?id=${requestId}`;
        (global as any).__currentRoute = newRoute;
      }
    }
  }, [requestId]);

  // When an offline-created request (o-*) syncs to a real server id, the URL
  // the user is on no longer resolves. Redirect to the new id once sync reports
  // the remap, preserving the current step. (#995 multi-rack QA)
  useEffect(() => {
    if (!requestId?.startsWith('o-')) {
      return;
    }
    const removeListener = syncService.addSyncListener(syncResult => {
      const route = buildRemappedRequestRoute(
        requestId,
        syncResult.requestIdMappings,
        currentStep,
      );
      if (route) {
        navigate(route);
      }
    });
    return removeListener;
  }, [requestId, currentStep, navigate]);

  // Clear the refetching flag once the refetch completes
  React.useEffect(() => {
    if (isRefetchingAfterSave && !isFetching && request) {
      setIsRefetchingAfterSave(false);
    }
  }, [isRefetchingAfterSave, isFetching, request]);

  // Store the original requestor email when request is first loaded
  React.useEffect(() => {
    if (request && request.requestorEmail && !originalRequestorEmail) {
      setOriginalRequestorEmail(request.requestorEmail);
    }
  }, [request, originalRequestorEmail]);

  // Sync UOM context with request's UOM value (only when not editing)
  React.useEffect(() => {
    if (request && request.uom && !isEditing) {
      setUOM(request.uom as 'Imperial' | 'Metric');
    }
  }, [request, setUOM, isEditing]);

  // Sync global UOM context changes to editedData during editing
  React.useEffect(() => {
    if (isEditing && uom) {
      setEditedData((prev: any) => ({
        ...prev,
        uom: uom,
      }));
      // Only mark dirty if UOM actually differs from the saved request value
      if (request && uom !== request.uom) {
        setDirtyFields(prev => new Set(prev).add('uom'));
      }
    }
  }, [uom, isEditing, request]);

  // Auto-close processing modal and invalidate queries when job completes.
  //
  // Deps intentionally narrowed (QA-52): the previous version listed the full
  // `jobStatus` object and the unstable `refetchAttachments` callback, which
  // caused this effect to re-run on every render while a job was in a terminal
  // state. Each re-run cleared the 30s auto-close timer, re-armed it, and
  // re-invoked `invalidateQueries` + `refetchAttachments`, producing the
  // concurrent fetch storm behind QA-52 (Supporting Documents empty /
  // attachment viewer blinking).
  //
  // We key the effect on `jobStatus?.status` AND `jobStatus?.jobId`: a second
  // sequential upload that also lands on `COMPLETED` carries a new `jobId`,
  // so we still re-fire for it. `queryClient` is included because
  // `useQueryClient()` returns a stable reference for a given provider, so it
  // does not contribute to the churn. Only `refetchAttachments` is omitted —
  // its identity is stable for a given `requestId` after the PR-1 Patch A
  // change, but including it would re-introduce churn if `requestId` itself
  // flips during a completion window.
  React.useEffect(() => {
    if (!processingModalVisible) return;
    if (
      jobStatus?.status !== TranscriptionJobStatus.COMPLETED &&
      jobStatus?.status !== TranscriptionJobStatus.FAILED
    ) {
      return;
    }

    // Invalidate request queries to refresh the data
    queryClient.invalidateQueries({
      queryKey: offlineRequestsKeys.detail(requestId || ''),
    });
    queryClient.invalidateQueries({ queryKey: offlineRequestsKeys.lists() });

    // Invalidate rack configurations to show voice-imported rack updates instantly
    queryClient.invalidateQueries({
      queryKey: rackConfigurationsKeys.list(requestId || ''),
    });
    queryClient.invalidateQueries({
      queryKey: rackConfigurationsKeys.grouped(requestId || ''),
    });

    // Refetch attachments to show newly created transcription attachment
    refetchAttachments();

    // Auto-close modal after 30 seconds (matching ProcessingModal behavior)
    const timer = setTimeout(() => {
      setProcessingModalVisible(false);
      setProcessingFileName(undefined);
    }, 30000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetchAttachments omitted; see QA-52 commentary above the effect.
  }, [
    jobStatus?.status,
    jobStatus?.jobId,
    processingModalVisible,
    requestId,
    queryClient,
  ]);

  // Background completion handler (works even when modal is closed). Ensures
  // request data auto-refreshes when transcription completes.
  //
  // Same dep-narrowing rationale as the foreground effect above — see QA-52
  // commentary there. We key on `backgroundJobStatus?.status` AND
  // `backgroundJobStatus?.jobId` so a second sequential job whose status is
  // also terminal still re-triggers invalidation + attachment refetch.
  React.useEffect(() => {
    if (!backgroundProcessingRequestId) return;
    if (
      backgroundJobStatus?.status !== TranscriptionJobStatus.COMPLETED &&
      backgroundJobStatus?.status !== TranscriptionJobStatus.FAILED
    ) {
      return;
    }

    // Invalidate queries to refresh the request data
    queryClient.invalidateQueries({
      queryKey: offlineRequestsKeys.detail(requestId || ''),
    });
    queryClient.invalidateQueries({ queryKey: offlineRequestsKeys.lists() });

    // Invalidate rack configurations to show voice-imported rack updates instantly
    queryClient.invalidateQueries({
      queryKey: rackConfigurationsKeys.list(requestId || ''),
    });
    queryClient.invalidateQueries({
      queryKey: rackConfigurationsKeys.grouped(requestId || ''),
    });

    // Refetch attachments to show newly created transcription attachment
    refetchAttachments();

    // Clear background tracking after 30 seconds (stop polling)
    const timer = setTimeout(() => {
      setBackgroundProcessingRequestId(undefined);
    }, 30000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetchAttachments omitted; see QA-52 commentary above the foreground effect.
  }, [
    backgroundJobStatus?.status,
    backgroundJobStatus?.jobId,
    backgroundProcessingRequestId,
    requestId,
    queryClient,
  ]);

  // Cleanup effect: Clear background processing on unmount
  React.useEffect(() => {
    return () => {
      // Clear background tracking to stop polling when component unmounts
      if (backgroundProcessingRequestId) {
        setBackgroundProcessingRequestId(undefined);
      }
    };
  }, [backgroundProcessingRequestId]);

  // Get step visibility based on permissions for the current request stage
  const rawRequestStage = request?.stage || request?.sysRequestStage;
  const requestStage = rawRequestStage ? normalizeStage(rawRequestStage) : null;

  /**
   * Deliverables write access, asked WITH the stage.
   *
   * A second call rather than a parameter on the one at the top of this component, because
   * that one runs before `request` resolves (:652) and so can never have a stage. Without a
   * stage the hook answers from `canFallBackToEditingDeliverables`, a union across every open
   * stage — which grants at DRAFT to eight roles the DRAFT matrix denies, and held only
   * because those roles cannot enter edit mode there. Asking again once the stage is known
   * routes the answer through `fieldPermissionService.checkFieldAccess`, the branch that was
   * already correct and simply unreachable from this screen.
   *
   * Only `canEditDeliverables` is taken; every other value still comes from the first call.
   * (MR !489 review, batch 2 change 2.4.)
   */
  const { canEditDeliverables: canEditDeliverablesAtStage } =
    useFormPermissions(requestStage ?? undefined, requestId || undefined);

  /**
   * Whether the section may be SEEN, which nothing asked before this change: every check was
   * about writing, so a `-` cell hid nothing. Same answer the sidebar gates its nav item on,
   * so the item and the step it leads to cannot disagree.
   * (MR !489 review, batch 2 change 2.3.)
   */
  const canSeeDeliverables = canReadDeliverablesAtStage({
    userRoles: user?.roles,
    requestStage,
    requestId,
    userEmail: user?.email,
  });

  // Bug 8 / FR9 (Story 4.1): the four internal reviewer roles (Vertiv Sponsor,
  // Vertiv Contributor, Config Engineer, Business Owner) land in Advanced view
  // by default when a request is past DRAFT. One-shot via
  // advancedViewDefaultAppliedRef so a manual toggle to Basic is never
  // overridden. At DRAFT the toggle is not consulted (see showFullDetails
  // `!showViewToggle` short-circuit), so we skip forcing state here.
  React.useEffect(() => {
    if (advancedViewDefaultAppliedRef.current) return;
    if (!user || !requestStage) return;
    advancedViewDefaultAppliedRef.current = true;
    if (
      requestStage !== RequestStage.DRAFT &&
      roleService.isInternalReviewerRole(user)
    ) {
      setIsAdvancedView(true);
    }
  }, [user, requestStage]);

  useEffect(() => {
    if (!onStepChange) return;
    if (
      currentStep === LEGACY_COMBINED_SITE_STEP_ID ||
      currentStep === 'site-walk'
    ) {
      onStepChange('opportunity');
      return;
    }
    if (currentStep === 'racks') {
      onStepChange('rack-power');
      return;
    }
    if (currentStep === 'customer-it') {
      onStepChange('monitoring');
      return;
    }
    if (currentStep === 'requestor') {
      onStepChange('opportunity');
      return;
    }
  }, [currentStep, onStepChange]);

  // Button permissions from config (must be after requestStage is defined)
  const buttonPermissions = useButtonPermissions(
    requestStage || undefined,
    requestId || undefined,
  );

  useEffect(() => {
    if (!request || !pendingEditDraft || editDraftHydratedRef.current) {
      return;
    }
    if (pendingEditDraft.requestId !== requestId) {
      return;
    }
    const hasRestorableEditDraft =
      pendingEditDraft.dirtyFields.length > 0 ||
      pendingEditDraft.rackSession != null ||
      pendingEditDraft.smallUpsSession != null ||
      pendingEditDraft.largeUpsSession != null ||
      pendingEditDraft.coolingSystemSession != null ||
      pendingEditDraft.powerDistributionSession != null ||
      pendingEditDraft.liquidCoolingCduSession != null ||
      (pendingEditDraft.pendingCaptionEdits != null &&
        Object.keys(pendingEditDraft.pendingCaptionEdits).length > 0);
    if (!hasRestorableEditDraft) {
      editDraftHydratedRef.current = true;
      return;
    }
    if (!isBrowserReloadNavigation()) {
      editDraftHydratedRef.current = true;
      return;
    }
    if (!buttonPermissions.canShowEdit) {
      editDraftHydratedRef.current = true;
      if (user?.email && requestId) {
        void clearEditRequestDraftSnapshot(user.email, requestId);
      }
      return;
    }
    editDraftHydratedRef.current = true;
    // #997 Story 6.3: snapshots carry the rack session as
    // {baselineRacks, sessionRacks}. The session merges into editedData (where
    // the engine keeps it, marked dirty so the save-time flush diffs it); the
    // BASELINE seeds the bridge's held baseline so the restored (possibly
    // stale) session is diffed against the truth it was edited from — not
    // restore-time-fresh truth, which would DELETE racks added since the
    // snapshot. Pre-engine snapshots have the same shape (back-compat).
    // Engine-bridged sections (racks, small UPS) carry their session as
    // {baseline, session}; each merges into editedData (marked dirty so the
    // save-time flush diffs it) and seeds its bridge's held baseline so the
    // restored session diffs against the truth it was edited from.
    const restoredEditedData: Record<string, unknown> = {
      ...pendingEditDraft.editedData,
    };
    const restoredDirty = new Set(pendingEditDraft.dirtyFields);
    if (pendingEditDraft.rackSession) {
      restoredEditedData.rackConfigurations =
        pendingEditDraft.rackSession.sessionRacks;
      restoredDirty.add('rackConfigurations');
      setRestoredRackBaseline(pendingEditDraft.rackSession.baselineRacks);
    }
    if (pendingEditDraft.smallUpsSession) {
      restoredEditedData.smallUpsConfigurations =
        pendingEditDraft.smallUpsSession.sessionSmallUps;
      restoredDirty.add('smallUpsConfigurations');
      setRestoredSmallUpsBaseline(
        pendingEditDraft.smallUpsSession.baselineSmallUps,
      );
    }
    if (pendingEditDraft.largeUpsSession) {
      restoredEditedData.largeUpsConfigurations =
        pendingEditDraft.largeUpsSession.sessionLargeUps;
      restoredDirty.add('largeUpsConfigurations');
      setRestoredLargeUpsBaseline(
        pendingEditDraft.largeUpsSession.baselineLargeUps,
      );
    }
    if (pendingEditDraft.coolingSystemSession) {
      restoredEditedData.coolingSystemConfigurations =
        pendingEditDraft.coolingSystemSession.sessionCoolingSystems;
      restoredDirty.add('coolingSystemConfigurations');
      setRestoredCoolingSystemBaseline(
        pendingEditDraft.coolingSystemSession.baselineCoolingSystems,
      );
    }
    if (pendingEditDraft.powerDistributionSession) {
      restoredEditedData.powerDistributionConfigurations =
        pendingEditDraft.powerDistributionSession.sessionPowerDistribution;
      restoredDirty.add('powerDistributionConfigurations');
      setRestoredPowerDistributionBaseline(
        pendingEditDraft.powerDistributionSession.baselinePowerDistribution,
      );
    }
    if (pendingEditDraft.liquidCoolingCduSession) {
      restoredEditedData.liquidCoolingCduConfigurations =
        pendingEditDraft.liquidCoolingCduSession.sessionLiquidCoolingCdus;
      restoredDirty.add('liquidCoolingCduConfigurations');
      setRestoredLiquidCoolingCduBaseline(
        pendingEditDraft.liquidCoolingCduSession.baselineLiquidCoolingCdus,
      );
    }
    setEditedData(restoredEditedData);
    setDirtyFields(restoredDirty);
    setIsEditing(true);
    if (pendingEditDraft.pendingCaptionEdits) {
      setPendingCaptionEdits(pendingEditDraft.pendingCaptionEdits);
    }
    if (pendingEditDraft.stepId && pendingEditDraft.stepId !== stepId) {
      onStepChange?.(pendingEditDraft.stepId);
    }
    showToast.info(REQUEST_DRAFT_RESTORED_MESSAGE);
  }, [
    buttonPermissions.canShowEdit,
    onStepChange,
    pendingEditDraft,
    request,
    requestId,
    stepId,
    user?.email,
  ]);

  const handleScopeChange = React.useCallback(
    async (nextStepIds: RequestFormStepId[]) => {
      const previousIds = storedEnabledStepIds;
      const removedStepIds = diffRemovedOptionalStepIds(
        previousIds,
        nextStepIds,
      );
      setEditedData((prev: Record<string, unknown>) => ({
        ...prev,
        enabledStepIds: nextStepIds,
      }));

      const requestStageForScopeClear =
        (request?.stage as string | undefined) ??
        (request?.sysRequestStage as string | undefined);

      // #1501: with aggregate save, Add/Remove Scope is part of the edit
      // session — Save Changes sends `enabledStepIds` in the same call (the
      // server runs the scope-clear there), so the whole session is ONE
      // revision. Cancel restores the scope and its data. The destructive
      // cache purge waits for the Save (`purgeScopeRemovalCaches`).
      const deferScopeToSave = featureFlags.aggregateSave;

      if (deferScopeToSave) {
        const persistedIds = resolveEnabledStepIds(
          request?.enabledStepIds ?? null,
        );
        const backToPersisted =
          persistedIds.length === nextStepIds.length &&
          persistedIds.every(id => nextStepIds.includes(id));
        setDirtyFields(prev => {
          const next = new Set(prev);
          if (backToPersisted) {
            next.delete('enabledStepIds');
          } else {
            next.add('enabledStepIds');
          }
          return next;
        });
      } else if (requestId) {
        try {
          await updateRequestMutation.mutateAsync({
            id: requestId,
            data: { enabledStepIds: nextStepIds },
          });
        } catch (error: unknown) {
          console.error('Failed to persist scope change:', error);
          setEditedData((prev: Record<string, unknown>) => ({
            ...prev,
            enabledStepIds: previousIds ?? prev.enabledStepIds,
          }));
          requestScope?.revertScope(previousIds ?? null);
          showToast.error('Could not save scope changes. Please try again.');
          return;
        }
      }

      if (removedStepIds.length > 0) {
        // #1453: purge every local store that still references the removed
        // families BEFORE the async side effects below. The bridge's held
        // baseline is nulled by resetSessionAfterScopeRemoval, but its seeding
        // effect immediately re-seeds from `restoredBaseline` / `editedData` —
        // so a stale restored snapshot or un-stripped session resurrects the
        // cleared units into the next Save's flush diff (orphaned rows on
        // re-create, 404 "configuration not found" on delete/update). These
        // setState calls batch into one render pass, closing that window.
        setEditedData((prev: Record<string, unknown>) =>
          stripFormFieldsForRemovedSteps(
            prev,
            removedStepIds,
            nextStepIds,
            requestStageForScopeClear,
          ),
        );
        setDirtyFields(prev =>
          pruneDirtyFieldsForRemovedSteps(
            prev,
            removedStepIds,
            nextStepIds,
            requestStageForScopeClear,
          ),
        );
        for (const removedStepId of expandRemovedStepIdsForDataClear(
          removedStepIds,
          nextStepIds,
        )) {
          switch (removedStepId) {
            case 'rack-power':
              setRestoredRackBaseline(null);
              break;
            case 'small-ups':
              setRestoredSmallUpsBaseline(null);
              break;
            case 'large-ups':
              setRestoredLargeUpsBaseline(null);
              break;
            case 'power-distribution':
              setRestoredPowerDistributionBaseline(null);
              break;
            case 'cooling-readiness':
              setRestoredCoolingSystemBaseline(null);
              break;
            case 'liquid-cooling':
              setRestoredLiquidCoolingCduBaseline(null);
              break;
            default:
              break;
          }
        }

        await applyScopeRemovalSideEffects({
          queryClient,
          requestId,
          removedStepIds,
          nextStoredStepIds: nextStepIds,
          stage: requestStageForScopeClear,
          purgeCaches: !deferScopeToSave,
          handles: {
            discardOpenRackEditor: () =>
              rackSaveHandle?.discardOpenRackEditor(),
            resetRackSessionAfterScopeRemoval: () =>
              rackSaveHandle?.resetSessionAfterScopeRemoval(),
            discardOpenSmallUpsEditor: () =>
              smallUpsSaveHandle?.discardOpenSmallUpsEditor(),
            resetSmallUpsSessionAfterScopeRemoval: () =>
              smallUpsSaveHandle?.resetSessionAfterScopeRemoval(),
            discardOpenLargeUpsEditor: () =>
              largeUpsSaveHandle?.discardOpenLargeUpsEditor(),
            resetLargeUpsSessionAfterScopeRemoval: () =>
              largeUpsSaveHandle?.resetSessionAfterScopeRemoval(),
            discardOpenPowerDistributionEditor: () =>
              powerDistributionSaveHandle?.discardOpenPowerDistributionEditor(),
            resetPowerDistributionSessionAfterScopeRemoval: () =>
              powerDistributionSaveHandle?.resetSessionAfterScopeRemoval(),
            discardOpenCoolingSystemEditor: () =>
              coolingSystemSaveHandle?.discardOpenCoolingSystemEditor(),
            resetCoolingSystemSessionAfterScopeRemoval: () =>
              coolingSystemSaveHandle?.resetSessionAfterScopeRemoval(),
            discardOpenLiquidCoolingCduEditor: () =>
              liquidCoolingCduSaveHandle?.discardOpenLiquidCoolingCduEditor(),
            resetLiquidCoolingCduSessionAfterScopeRemoval: () =>
              liquidCoolingCduSaveHandle?.resetSessionAfterScopeRemoval(),
          },
          onSessionDirtyClear: (removedStepId: OptionalRequestFormStepId) => {
            switch (removedStepId) {
              case 'rack-power':
                setRackSessionDirty(false);
                break;
              case 'small-ups':
                setSmallUpsSessionDirty(false);
                break;
              case 'large-ups':
                setLargeUpsSessionDirty(false);
                break;
              case 'power-distribution':
                setPowerDistributionSessionDirty(false);
                break;
              case 'cooling-readiness':
                setCoolingSystemSessionDirty(false);
                break;
              case 'liquid-cooling':
                setLiquidCoolingCduSessionDirty(false);
                break;
              default:
                break;
            }
          },
        });
        if (!deferScopeToSave) {
          void refetchAttachments();
        }
      }

      // Same exemption as the mount-time redirect guard: the detail-only steps are not in
      // the shared step registry, so `nextStepIds` can never contain them and a scope
      // change would otherwise bounce the user off the Deliverables step.
      if (
        stepId !== 'deliverables' &&
        !nextStepIds.includes(stepId as RequestFormStepId) &&
        onStepChange
      ) {
        onStepChange(nextStepIds[0] ?? 'opportunity');
      }
    },
    [
      onStepChange,
      queryClient,
      coolingSystemSaveHandle,
      largeUpsSaveHandle,
      liquidCoolingCduSaveHandle,
      powerDistributionSaveHandle,
      rackSaveHandle,
      request?.enabledStepIds,
      request?.stage,
      request?.sysRequestStage,
      requestId,
      requestScope,
      smallUpsSaveHandle,
      stepId,
      storedEnabledStepIds,
      updateRequestMutation,
    ],
  );

  const scopeRegistration = React.useMemo<RequestScopeRegistration | null>(
    () =>
      request
        ? {
            storedStepIds: storedEnabledStepIds,
            showScopeControls: buttonPermissions.canShowEdit,
            canManageScope:
              isEditing && buttonPermissions.canShowEdit && !isManualSaving,
            onScopeChange: handleScopeChange,
          }
        : null,
    [
      buttonPermissions.canShowEdit,
      handleScopeChange,
      isEditing,
      isManualSaving,
      request != null,
      storedEnabledStepIds,
    ],
  );

  useRegisterRequestScope(scopeRegistration);

  // Check if user is a partner contributor
  const isPartnerContributor = user?.roles?.includes(
    AppRole.PARTNER_CONTRIBUTOR,
  );

  // Determine if we should show the view toggle (only for post-draft stages)
  // Partner Contributors should NOT see the view toggle because their permissions
  // are based on request-specific access levels (read/read_write), not config-based roles
  // The basic/advanced toggle is designed to show "requestor in DRAFT" vs "my role permissions",
  // which doesn't apply to Partner Contributors who have per-request access levels
  const showViewToggle =
    requestStage !== RequestStage.DRAFT && !isPartnerContributor;

  const showFullDetailsForMetrics = !showViewToggle || isAdvancedView;

  // Step visibility uses the request's actual stage. Basic view limits **fields** via
  // Requestor @ current stage (see resolveEffectiveFormViewPermissionContext), not DRAFT (#997 Bug 1).
  const effectiveStageForVisibility = requestStage;

  const {
    stepVisibility,
    isLoading: isLoadingStepVisibility,
    refreshVisibility,
  } = useStepVisibility(
    effectiveStageForVisibility || undefined,
    requestId || undefined,
  );

  // When toggling between advanced/basic view, refresh step visibility
  React.useEffect(() => {
    if (showViewToggle && requestStage) {
      // Clear ALL permission caches to force fresh load
      fieldPermissionService.clearCache();

      // Force refresh of step visibility with a delay
      const timer = setTimeout(() => {
        refreshVisibility();
      }, 100); // Increased delay to ensure cache is fully cleared

      return () => clearTimeout(timer);
    }
  }, [
    isAdvancedView,
    showViewToggle,
    requestStage,
    effectiveStageForVisibility,
    refreshVisibility,
  ]);

  React.useEffect(() => {
    setShowFullDetails(showFullDetailsForMetrics);
    setRequestStage(requestStage ?? undefined);
    setRequestId(requestId ?? undefined);
  }, [
    showFullDetailsForMetrics,
    requestStage,
    requestId,
    setRequestId,
    setRequestStage,
    setShowFullDetails,
  ]);

  React.useEffect(() => {
    return () => {
      setShowFullDetails(undefined);
      setRequestStage(undefined);
      setRequestId(undefined);
    };
  }, [setRequestId, setRequestStage, setShowFullDetails]);

  // Notify App component when request stage changes
  React.useEffect(() => {
    if (requestStage && onStageChange) {
      onStageChange(requestStage);
    }
  }, [requestStage, onStageChange, request, requestId, rawRequestStage]);

  // Notify App component when effective stage changes (for sidebar filtering)
  React.useEffect(() => {
    if (onEffectiveStageChange) {
      onEffectiveStageChange(effectiveStageForVisibility);
    }
  }, [effectiveStageForVisibility, onEffectiveStageChange]);

  // Filter detail steps based on permissions for all stages
  const visibleDetailSteps = React.useMemo(() => {
    if (isLoadingStepVisibility) {
      return detailSteps; // Show all steps while loading
    }

    return detailSteps.filter(step =>
      shouldShowStepInSidebarNav(step.id, {
        stepVisibility,
        enabledStepIds: detailSteps.map(s => s.id),
        storedStepIds: storedEnabledStepIds,
      }),
    );
  }, [
    stepVisibility,
    isLoadingStepVisibility,
    detailSteps,
    storedEnabledStepIds,
  ]);

  // Handler for toggling view mode with navigation logic
  const handleToggleView = React.useCallback(async () => {
    const newIsAdvancedView = !isAdvancedView;

    if (!newIsAdvancedView && onStepChange && user) {
      // `stepId === 'deliverables'` is defensive rather than reachable today: the Basic/Advanced
      // toggle only renders inside a registry step body, and the Deliverables step replaces that
      // body entirely. It matches the other two redirect guards so the step cannot start
      // bouncing if a toggle is ever hoisted into the header or sub-header.
      const stayOnCurrentScopedStep =
        stepId === 'deliverables' || resolvedEnabledStepIds.includes(stepId);
      if (!stayOnCurrentScopedStep) {
        try {
          const targetStepId = await findRedirectStepIdWhenSwitchingToBasic(
            detailStepIds,
            stepId,
          );
          if (targetStepId) {
            onStepChange(targetStepId);
          }
        } catch (error: unknown) {
          console.error('Error resolving basic-view step on toggle:', error);
        }
      }
    }

    setIsAdvancedView(newIsAdvancedView);
  }, [
    isAdvancedView,
    stepId,
    onStepChange,
    user,
    resolvedEnabledStepIds,
    detailStepIds,
  ]);

  // File state management for new uploads in edit mode
  const handleFilesSelected = (section: string, files: SelectedFile[]) => {
    setSelectedFiles(prev => {
      const next: Record<string, SelectedFile[]> = {
        ...prev,
        [section]: files,
      };
      if (section === SECTION_IDS.SITE_DIMENSIONS_INFO) {
        next[SECTION_IDS.SITE_DIMENSIONS_ADDITIONAL] = [];
      }
      return next;
    });
  };

  // Caption change handler - tracks changes for batch saving with Save button
  const handleCaptionChange = (attachmentId: number, caption: string) => {
    setPendingCaptionEdits(prev => ({
      ...prev,
      [attachmentId]: caption,
    }));
  };

  // Handler for "View Request" button in ProcessingModal
  // Since we're already on the request detail screen, just close the modal
  const handleNavigateToRequest = () => {
    setProcessingModalVisible(false);
    setProcessingFileName(undefined);
  };

  /**
   * Handle recording button press - shows warning modal
   */
  const handleRecordingButtonPress = () => {
    // Discard any unsaved changes before starting recording
    if (isEditing) {
      setIsEditing(false);
      setEditedData({});
      setDirtyFields(new Set());
      setSelectedFiles({});
      setPendingCaptionEdits({});
    }
    // Show warning modal
    setShowRecordingWarning(true);
  };

  const handlePasteTextButtonPress = () => {
    // Discard any unsaved changes before opening the paste modal, matching the
    // dictation button's unsaved-edit handling.
    if (isEditing) {
      setIsEditing(false);
      setEditedData({});
      setDirtyFields(new Set());
      setSelectedFiles({});
      setPendingCaptionEdits({});
    }
    setTextIntakeModalVisible(true);
  };

  const handleTextIntakeSubmit = async (
    text: string,
    options: { emailThread?: boolean } = {},
  ) => {
    if (!requestId) {
      showToast.error('Request ID is required.');
      return;
    }
    const requestIdNum = parseInt(requestId, 10);
    if (isNaN(requestIdNum)) {
      showToast.error('Invalid request ID.');
      return;
    }
    setTextIntakeSubmitting(true);
    try {
      await transcriptionService.transcribeExistingRequestFromText({
        requestId: requestIdNum,
        text,
        emailThread: options.emailThread,
      });
      setTextIntakeModalVisible(false);
      setProcessingModalAutoClose(false); // text intake: hold until user dismisses
      setProcessingModalVisible(true);
      setProcessingFileName('Pasted text');
      setBackgroundProcessingRequestId(requestIdNum);
      queryClient.invalidateQueries({
        queryKey: offlineRequestsKeys.detail(requestId),
      });
      queryClient.invalidateQueries({
        queryKey: offlineRequestsKeys.lists(),
      });
    } catch (error) {
      console.error('Text intake error:', error);
      showToast.error(
        error instanceof Error
          ? error.message
          : 'Failed to submit text. Please try again.',
      );
    } finally {
      setTextIntakeSubmitting(false);
    }
  };

  /**
   * Handle warning modal confirmation - starts recording
   */
  const handleRecordingWarningConfirm = async () => {
    setShowRecordingWarning(false);
    try {
      await handleStartRecording();
    } catch (error) {
      console.error('Failed to start recording:', error);
      showToast.error(
        'Failed to start recording. Please check microphone permissions.',
      );
    }
  };

  /**
   * Handle warning modal cancel
   */
  const handleRecordingWarningCancel = () => {
    setShowRecordingWarning(false);
  };

  /**
   * Handle recording acceptance - uploads for transcription
   */
  const handleAcceptRecordingWithUpload = React.useCallback(async () => {
    await handleAcceptRecording(async (audioFile: AudioFile) => {
      try {
        if (!requestId) {
          throw new Error('Request ID is required for transcription');
        }

        const requestIdNum = parseInt(requestId, 10);
        if (isNaN(requestIdNum)) {
          throw new Error('Invalid request ID');
        }

        // Rename audio file with user/request context
        const newFilename = generateAudioFilename({
          originalFilename: audioFile.name,
          userEmail: user?.email,
          requestId: requestIdNum,
        });
        const renamedFile = renameAudioFile(audioFile, newFilename);

        // Prepare file for upload
        const fileData = {
          uri: renamedFile.uri,
          name: renamedFile.name,
          type: renamedFile.mimeType,
          size: renamedFile.size,
          webFile: renamedFile.webFile, // Web: File object, Native: undefined
        };

        // Call transcription API for existing request
        await transcribeExistingMutation.mutateAsync({
          requestId: requestIdNum,
          file: fileData,
        });

        // Show processing modal immediately (no success toast yet - that comes when job completes)
        setProcessingModalVisible(true);
        setProcessingFileName(renamedFile.name);
        setBackgroundProcessingRequestId(requestIdNum);

        // Invalidate queries to reflect the PROCESSING stage immediately
        queryClient.invalidateQueries({
          queryKey: offlineRequestsKeys.detail(requestId),
        });
        queryClient.invalidateQueries({
          queryKey: offlineRequestsKeys.lists(),
        });
      } catch (error) {
        console.error('Transcription upload error:', error);
        showToast.error(
          'Failed to upload audio for transcription. Please try again.',
        );
        throw error; // Re-throw so the recording hook can handle state cleanup
      }
    });
  }, [
    requestId,
    handleAcceptRecording,
    transcribeExistingMutation,
    queryClient,
    user,
  ]);

  const getCurrentStepComponent = () => {
    const step = detailSteps.find(s => s.id === stepId);
    return step?.component || null;
  };

  const handleEdit = useCallback(() => {
    if (request) {
      // Initialize editedData with all request fields to ensure all changes are captured
      const initialEditedData: any = {
        ...request,
        // Ensure any undefined fields are set to empty string for consistency
        customerName: request.customerName || '',
        partnerName: request.partnerName || '',
        requestorName: request.requestorName || '',
        requestorPhone: request.requestorPhone || request.phone || '',
        phone: request.phone || '',
        region: request.region || '',
        requestType: request.requestType || '',
        stage: request.stage || '',
        description: request.description || '',
        additionalDetails: request.additionalDetails || '',
        uom: request.uom || 'Imperial', // Ensure UOM field is properly initialized
        fb_rating: request.fb_rating || undefined, // Initialize rating field
        fb_comments: request.fb_comments || '', // Initialize comments field
        // ✅ PHASE 3/4 FIX: Include Default Sponsor flag if present
        // Note: This will be updated by the opportunity step (request-backed) / team block updateEditedDataFromTeamInformation
        // when team members are loaded (PHASE 4 detection logic)
        useDefaultSponsors: (request as any).useDefaultSponsors || false,
        // Include all the new conditional fields
        isASecondaryFluidNetworkRequired:
          request.isASecondaryFluidNetworkRequired || '',
        datahall3DModel: request.datahall3DModel || '',
        datahall2DLayout: request.datahall2DLayout || '',
        datahallCeilingHeight: request.datahallCeilingHeight || '',
        datahallCeilingSupportRating:
          request.datahallCeilingSupportRating || '',
        datahallFloorType: request.datahallFloorType || '',
        datahallRaisedFloorDepth: request.datahallRaisedFloorDepth || '',
        datahallRackArrangement: request.datahallRackArrangement || '',
        datahallRackArrangementSupportingFiles:
          request.datahallRackArrangementSupportingFiles || '',
        cduArrangement: request.cduArrangement || '',
        cduRedundancyDefinition: request.cduRedundancyDefinition || '',
        cduPrimaryPipingConnections: request.cduPrimaryPipingConnections || '',
        cduSecondaryPipingConnections:
          request.cduSecondaryPipingConnections || '',
        tcsFlowPathwayDefinition: request.tcsFlowPathwayDefinition || '',
        tcsRoutingSpecialRequirements:
          request.tcsRoutingSpecialRequirements || '',
        tcsArrangementType: request.tcsArrangementType || '',
        tcsRoutingLocation: request.tcsRoutingLocation || '',
        tcsRackFlowControlType: request.tcsRackFlowControlType || '',
        tcsRackConnectionSize: request.tcsRackConnectionSize || '',
        tcsRackConnectionType: request.tcsRackConnectionType || '',
        tcsLoopPressureMax: request.tcsLoopPressureMax || '',
        tcsLoopTemperatureRange: request.tcsLoopTemperatureRange || '',
        tcsLoopPipeDiameter: request.tcsLoopPipeDiameter || '',
        tcsCoolantFlowratePerRack: request.tcsCoolantFlowratePerRack || '',
        tcsAdditionalInformation: request.tcsAdditionalInformation || '',
        tcsAdditionalAttachments: request.tcsAdditionalAttachments || '',
        coolingAdditionalReadinessInfo:
          request.coolingAdditionalReadinessInfo || '',
      };

      // Handle array fields - convert arrays to JSON strings for editing
      const arrayFields = [
        'vertivSponsors',
        'vertivContributors',
        'partnerContributors',
      ];
      arrayFields.forEach(field => {
        if (Array.isArray((request as any)[field])) {
          initialEditedData[field] = JSON.stringify((request as any)[field]);
        } else if ((request as any)[field]) {
          initialEditedData[field] = (request as any)[field];
        }
      });

      setEditedData(initialEditedData);
      setDirtyFields(new Set());
      setIsEditing(true);
    }
  }, [request]);

  /**
   * Two boundary questions with two different subjects (PRD FR28). They used to share one
   * `userType` predicate, which is why an internal user opening a customer's Draft saw a
   * button label the server was about to contradict.
   *
   * Routing asks about the request's **creator**, so it takes the server's stamped answer
   * (`requestorIsExternal` from `GET /requests/:id`) and only falls back to this viewer's
   * role when this viewer IS the creator. Display asks about the **viewer**, so it stays on
   * the viewer and reads their `External Requestor` role.
   *
   * Both fail closed, at opposite polarities — see `src/utils/requestorBoundary.ts`, which
   * owns both rules and the roles-present guard that keeps the display side from revealing
   * internal detail to a viewer whose roles never loaded.
   */
  /** Routing: the Draft submit target (PRD FR2 / FR28), a property of the creator. */
  const isExternalRequestor = resolveRequestorIsExternal(request, user);
  /** Display: the internal-only "Configuration" label (PRD FR7), about the viewer. */
  const showInternalDetail = showsInternalStageDetail(user);

  const { data: approvalGroup, isLoading: approvalGroupLoading } =
    useApprovalGroup();
  const approvalsEnabled =
    approvalGroup?.enabled === true && (approvalGroup.members?.length ?? 0) > 0;
  const isPartnerViewer = roleService.isExternalUser(user ?? null);
  // While the Approvals Group query is in flight, do not pick an intent — a premature
  // Submit to Vertiv would 403 if the gate is actually on.
  const approvalGatePending = isPartnerViewer && approvalGroupLoading;
  const useSubmitForApproval =
    isPartnerViewer &&
    approvalsEnabled &&
    isExternalRequestor &&
    !approvalGatePending;

  const { isImpersonating, mode, targetUser } = useImpersonation();
  const isReadOnlyImpersonation = isImpersonating && mode === 'read_only';

  const requestIsAwaitingApproval =
    requestStage === RequestStage.AWAITING_APPROVAL;
  const {
    viewerIsLiveApprovalGroupMember,
    viewerIsRequestor,
    viewerHasApprovalStampOnlyAccess,
  } = useApprovalStampOnlyAccess({
    requestorEmail: request?.requestorEmail,
    requestStage,
  });
  const viewerEmail = (
    isImpersonating && targetUser?.email ? targetUser.email : user?.email
  )
    ?.trim()
    .toLowerCase();
  const viewerCanStampAwaitingApproval =
    requestIsAwaitingApproval && viewerIsLiveApprovalGroupMember;
  /** Approve/Reject stamps blocked only in read-only impersonation (full access may stamp). */
  const approvalStampBlockedByReadOnlyImpersonation =
    requestIsAwaitingApproval &&
    viewerIsLiveApprovalGroupMember &&
    isReadOnlyImpersonation;

  const partnerApprovalsActive =
    isPartnerViewer && approvalsEnabled && !approvalGatePending;
  const draftMissingReasonPollsDoneRef = useRef(0);
  const rejectionPollSnapshotRef = useRef({
    requestStage,
    latestRejectionReason: request?.latestRejectionReason,
  });
  rejectionPollSnapshotRef.current = {
    requestStage,
    latestRejectionReason: request?.latestRejectionReason,
  };

  useEffect(() => {
    draftMissingReasonPollsDoneRef.current = 0;
  }, [requestId, viewerEmail]);

  useEffect(() => {
    if (request?.latestRejectionReason) {
      draftMissingReasonPollsDoneRef.current = 0;
    }
  }, [request?.latestRejectionReason]);

  const pollDetailForRejectionReadModel = shouldPollDetailForRejectionReadModel(
    {
      isOnline,
      requestStage,
      viewerIsRequestor,
      latestRejectionReason: request?.latestRejectionReason,
      partnerApprovalsActive,
      draftMissingReasonPollsDone: draftMissingReasonPollsDoneRef.current,
    },
  );

  useEffect(() => {
    if (!pollDetailForRejectionReadModel || !requestId) {
      return;
    }

    void refetchRequestDetail?.();
    const intervalId = setInterval(() => {
      const snapshot = rejectionPollSnapshotRef.current;
      const stillPolling = shouldPollDetailForRejectionReadModel({
        isOnline,
        requestStage: snapshot.requestStage,
        viewerIsRequestor,
        latestRejectionReason: snapshot.latestRejectionReason,
        partnerApprovalsActive,
        draftMissingReasonPollsDone: draftMissingReasonPollsDoneRef.current,
      });
      if (!stillPolling) {
        clearInterval(intervalId);
        return;
      }
      if (
        snapshot.requestStage === RequestStage.DRAFT &&
        !snapshot.latestRejectionReason
      ) {
        draftMissingReasonPollsDoneRef.current += 1;
      }
      void refetchRequestDetail?.();
    }, REQUEST_DETAIL_AWAITING_REJECT_POLL_MS);

    return () => clearInterval(intervalId);
  }, [
    isOnline,
    partnerApprovalsActive,
    pollDetailForRejectionReadModel,
    refetchRequestDetail,
    request?.latestRejectionReason,
    requestId,
    requestStage,
    viewerIsRequestor,
  ]);

  /**
   * The Draft submit label. Derived once and shared by the submit button and the deliverables
   * dialog's confirm button so the two cannot drift (FR11: the deliverables dialog's confirm *is*
   * the submit confirmation, so it must carry the same word).
   * Approvals gate on + partner viewer → Submit for Approval (#1456); Vertiv viewers keep
   * Submit to Vertiv even when the partner gate is on (UJ-8).
   */
  const draftSubmitLabel = useSubmitForApproval
    ? 'Submit for Approval'
    : isExternalRequestor
    ? 'Send to Vertiv'
    : 'Submit';
  /** Compact footer: shorten Approvals label so Close + Submit do not overlap. */
  const draftSubmitButtonLabel =
    useSubmitForApproval && isSmallScreen ? 'Submit' : draftSubmitLabel;
  const draftSubmitIntent = useSubmitForApproval
    ? 'submit_for_approval'
    : 'submit_to_vertiv';

  const handleStageChangeClick = () => {
    // Reject and Approve are both terminal writes. Ignore Approve while a
    // reject is pending or its dialog is open.
    if (rejectRequestMutation.isPending || showRejectConfirmation) {
      return;
    }
    // Draft submit asks what the requester needs first, and that dialog's confirm *is* the
    // confirmation (FR11) — two modals in sequence would read as a defect. Gate on
    // currentStage, not nextStage: DRAFT and the legacy SITE_ASSESSMENT advance now share
    // the same nextStage and cannot be told apart by it.
    const currentStage = (request?.stage ||
      request?.sysRequestStage) as RequestStage;
    if (currentStage === RequestStage.DRAFT) {
      if (approvalGatePending) {
        return;
      }
      /**
       * Already looking at the Deliverables step? Then the dialog would show the same list
       * back, which reads as the app asking a question it can see the answer to.
       *
       * Skipped ONLY when the dialog would have had nothing to say. It carries one rule
       * beyond the field list — at least one deliverable (FR21a) — so the skip is gated on
       * a non-empty list. Skipping unconditionally would submit a request with nothing on
       * it, silently, which is the A1 defect cycle 4 fixed.
       *
       * The confirmation is not skipped, only the field list: submitting is one-way and
       * sends mail, so it falls through to the same stage-change dialog every other stage
       * uses. No snapshot is taken, because this path opens no editor and changes nothing.
       */
      /**
       * Never open the checklist onto a list we cannot vouch for.
       *
       * While the read is in flight, or after it failed, `deliverables` is empty for reasons
       * that have nothing to do with what is stored. Opening then showed an empty checklist
       * for a request that has deliverables, the user ticked one to satisfy FR21a, and the
       * submit flushed that single item over the real list. Fall through to the plain stage
       * confirmation instead: submitting still works, it simply does not touch deliverables.
       * (MR !489 review, finding 01.)
       */
      if (deliverablesSession.isLoading || !deliverablesSession.isBaseTrusted) {
        setShowStageChangeConfirmation(true);
        return;
      }

      if (
        shouldSkipDeliverablesDialog({
          stepId,
          deliverableCount: deliverablesSession.deliverables.length,
        })
      ) {
        setShowStageChangeConfirmation(true);
        return;
      }

      // Restore point for Cancel. Taken here rather than inside the dialog so cancelling
      // the submit drops only what was typed in the dialog, not whatever the surrounding
      // edit session already had pending.
      restoreDeliverablesOnDialogCancelRef.current =
        deliverablesSession.snapshotPendingDeliverables();
      setShowDeliverablesDialog(true);
      return;
    }
    setShowStageChangeConfirmation(true);
  };

  const handleConfirmStageChange = async () => {
    if (!request || !requestId) return;
    // The Approve dialog can already be open when Reject starts. Do not
    // send the stamp if a reject is now open or in flight.
    if (showRejectConfirmation || rejectRequestMutation.isPending) {
      return;
    }

    const currentStage = (request.stage ||
      request.sysRequestStage) as RequestStage;
    const nextStage = getNextStage(currentStage, isExternalRequestor);
    const isApprovalStamp =
      currentStage === RequestStage.AWAITING_APPROVAL &&
      viewerIsLiveApprovalGroupMember;

    if (!nextStage) {
      showToast.error(
        'This request has already been closed and cannot be submitted further.',
      );
      return;
    }

    // Stage advance = handoff: Vertiv Sponsor required (Story 3.8 / #1401).
    // Manual Save does not re-check sponsor — see requiresVertivSponsorForManualSave.
    //
    // Kept over the POC's `currentStage === 'DRAFT'` check on merge: this helper is
    // `currentStage !== nextStage`, so it is target-agnostic and cannot silently stop
    // matching when the Draft transition splits by requestor type (PRD FR3, risk R1).
    if (requiresVertivSponsorForStageAdvance(currentStage, nextStage)) {
      // Fetch fresh team member data from the server to ensure we have the latest.
      // An approval stamp does not submit editedData, so a local sponsor clear
      // must not block it.
      let sponsors = isApprovalStamp
        ? request.vertivSponsors || []
        : editedData.vertivSponsors !== undefined
        ? editedData.vertivSponsors
        : request.vertivSponsors || [];

      // If sponsors were explicitly cleared in this edit session, do not
      // fall back to /team (would ignore the assessor's empty selection).
      const sponsorsDirtyCleared =
        !isApprovalStamp &&
        dirtyFields.has('vertivSponsors') &&
        !hasVertivSponsorSelection(sponsors);

      // If no sponsors in memory (and not dirty-cleared), fetch from server
      if (!hasVertivSponsorSelection(sponsors) && !sponsorsDirtyCleared) {
        try {
          const teamMembers = (await api.get(
            `/requests/${requestId}/team`,
          )) as any;
          // Accept both regular sponsors and Default Sponsors
          const sponsorMembers = teamMembers.filter(
            (tm: any) =>
              tm.userRole === 'vertiv_sponsor' ||
              tm.userRole === 'default_sponsor_nam' ||
              tm.userRole === 'default_sponsor_emea' ||
              tm.userRole === 'default_sponsor_apac',
          );
          sponsors = sponsorMembers.map((tm: any) => tm.userEmail);
        } catch (error) {
          console.error('Failed to fetch team members:', error);
        }
      }

      if (!hasVertivSponsorSelection(sponsors)) {
        showToast.error('Please select at least one Vertiv Sponsor.');
        setShowStageChangeConfirmation(false);
        // The deliverables dialog is the Draft entry point now, so it is the one that has to close.
        setShowDeliverablesDialog(false);
        return;
      }
    }

    // Note: Team member selection for CONFIGURATION stage is now optional
    // Validation removed per requirements - vertiv/partner contributor fields are optional

    // Show loading toast
    const loadingToast = showToast.loading('Submitting request...');

    try {
      // Build submission data from only dirty fields (proper HTTP PATCH semantics)
      const processedData: Record<string, any> = {};
      dirtyFields.forEach(field => {
        processedData[field] = editedData[field];
      });
      const dirtyFacilityPowerInstances = dirtyFields.has(
        'facilityPowerInstances',
      )
        ? processedData.facilityPowerInstances
        : undefined;

      // Convert JSON strings back to arrays for team member fields
      const arrayFields = [
        'vertivSponsors',
        'vertivContributors',
        'partnerContributors',
      ];
      arrayFields.forEach(field => {
        if (processedData[field] && typeof processedData[field] === 'string') {
          try {
            processedData[field] = JSON.parse(processedData[field]);
          } catch (e) {
            // Field is not valid JSON, keeping as string
          }
        }
      });

      if (processedData.requestForInformation === 'true') {
        processedData.requestForInformation = true;
      } else if (processedData.requestForInformation === 'false') {
        processedData.requestForInformation = false;
      }

      normalizeRequestNumericFields(processedData);
      Object.assign(
        processedData,
        prepareTextAreaFieldsForTransport(processedData),
      );

      // Prepare data for submission, excluding stage fields to avoid conflicts
      const dataToSubmit =
        Object.keys(processedData).length > 0 ? processedData : {};

      // Remove stage fields to prevent conflicts during stage updates

      const {
        stage,
        sysRequestStage,
        sysRequestSubmitterRole,
        ...cleanDataToSubmit
      } = stripRepeatableInstanceArraysFromPatch(dataToSubmit);

      // Set the refetching flag BEFORE calling the mutation to prevent any flash
      setIsRefetchingAfterSave(true);

      await stageUpdateMutation.mutateAsync({
        requestId,
        // Approve is a stamp, not an edit. Keep its payload empty even if a
        // stale local edit draft left dirty fields behind for this request.
        requestData: isApprovalStamp ? {} : cleanDataToSubmit,
        currentStage,
        newStage: nextStage,
        submitIntent:
          currentStage === RequestStage.DRAFT
            ? draftSubmitIntent
            : isApprovalStamp
            ? 'approve'
            : 'submit_to_vertiv',
      });

      // The deliverable changes made in the dialog ride along with this save, so one
      // action persists everything (amended frozen block). Run after the stage change
      // commits, not before: if the submit fails the pending list is untouched, the dialog
      // reopens still carrying it, and nothing was written on a submit that did not happen.
      //
      // Guarded, like the team-member write below and for the same reason. The stage change
      // has ALREADY committed by this point, so letting a deliverables failure escape sent
      // control to the outer catch, which reported "Failed to submit request. Please try
      // again." on a request that was submitted, skipped the edit-state cleanup, and invited
      // the user to submit again — advancing the request a second stage.
      // (MR !489 review, finding 05.)
      // Set when the deliverables did not persist. The submit itself still succeeded, so
      // the generic success toast below would also be true — but firing both leaves the
      // user with a red message and a green one about the same action and no way to tell
      // which outcome to act on. The error message already says the request WAS submitted,
      // so it carries the good news too and the success toast is redundant.
      // (MR !489 review, batch 3, change 3.3.)
      let submitDeliverablesFailed = false;

      try {
        // Both halves matter: a throw AND a refusal. `'conflict'` and `'failed'` return
        // normally by design, so a catch alone let a submit whose deliverables the server
        // refused report as a clean submit. (MR !489 review, batch 1b.)
        const submitDeliverablesOutcome = isApprovalStamp
          ? 'nothing'
          : await deliverablesSaveHandle.flushPendingDeliverableChanges();
        if (submitDeliverablesOutcome === 'conflict') {
          submitDeliverablesFailed = true;
          showToast.error(
            'Request submitted, but the deliverables were not saved: they changed while you were editing. Reopen the request to see the current list.',
          );
        } else if (submitDeliverablesOutcome === 'failed') {
          submitDeliverablesFailed = true;
          showToast.error(
            'Request submitted, but the deliverables could not be saved. Open the request and save them again.',
          );
        }
      } catch (deliverablesError) {
        submitDeliverablesFailed = true;
        console.error(
          'Request submitted, but its deliverables could not be saved:',
          deliverablesError,
        );
        // The pending list is deliberately left intact so a retry still has it.
        showToast.error(
          'Request submitted, but the deliverables could not be saved. Open the request and save them again.',
        );
      }

      // Guarded for the same reason as the deliverables flush above and the team-member
      // write below: the stage change has ALREADY committed, so an escape from here reaches
      // the outer catch, reports "Failed to submit request. Please try again." on a request
      // that WAS submitted, and skips the edit-state cleanup — leaving Submit pressable and
      // inviting a second stage advance. It sat unguarded between two guarded writes.
      //
      // No toast and no `submitDeliverablesFailed`: this only refreshes the local cache
      // (`offlineCache.updateCachedRequestRepeatableConfigurations`, which can reject on a
      // storage quota error). Nothing the user entered is lost, the server already has it,
      // and the next fetch repairs the cache. Same treatment as the team-member write.
      // (MR !489 review, batch 3 remediation.)
      if (!isApprovalStamp && Array.isArray(dirtyFacilityPowerInstances)) {
        try {
          await persistDirtyFacilityPowerInstances(dirtyFacilityPowerInstances);
        } catch (facilityPowerError) {
          console.error(
            'Request submitted, but the Facility Power cache could not be refreshed:',
            facilityPowerError,
          );
        }
      }

      // Save team members using consolidated approach (if any team data in editedData).
      // Online this makes ONE PUT request instead of three separate requests. Offline it
      // queues onto the request's own update queue, exactly as the Save path does — this
      // call used to be unconditional, so an offline submit threw the team edit away.
      if (
        !isApprovalStamp &&
        (processedData.vertivSponsors !== undefined ||
          processedData.vertivContributors !== undefined ||
          processedData.partnerContributors !== undefined)
      ) {
        try {
          if (isOnline) {
            await saveAllTeamMembersFromEditedData(requestId, processedData);
          } else {
            await queueOfflineTeamMemberEdit(requestId, processedData);
          }
        } catch (teamError) {
          console.error(
            'Failed to update team members during stage change:',
            teamError,
          );
          // Don't throw - we want the stage change to succeed even if team members fail
        }
      }

      // Ensure loading toast is dismissed before showing success
      showToast.dismiss(loadingToast);

      // Suppressed when the deliverables did not persist: that error toast already reports
      // the submit as succeeded, so adding a green "submitted successfully!" beside it just
      // makes the user guess which one to believe. Note this is the ONLY thing the flag
      // gates — the cleanup below still runs either way, because the stage change did
      // commit. (MR !489 review, batch 3, change 3.3.)
      if (!submitDeliverablesFailed) {
        // Small delay to ensure toast is dismissed before showing success
        setTimeout(() => {
          if (
            currentStage === RequestStage.DRAFT &&
            draftSubmitIntent === 'submit_for_approval'
          ) {
            showToast.success(AWAITING_APPROVAL_SUBMIT_TOAST);
          } else if (
            currentStage === RequestStage.AWAITING_APPROVAL &&
            viewerIsLiveApprovalGroupMember
          ) {
            // Mirrors the email subject split in EXPERIENCE.md: "your request" belongs to
            // the requestor, and the common case is a Group Member stamping someone else's.
            showToast.success(approveSuccessToastMessage(viewerIsRequestor));
          } else {
            showToast.success(
              `Request submitted successfully! Status changed to: ${getStageDisplayName(
                nextStage,
                showInternalDetail,
              )}`,
            );
          }
        }, 100);
      }

      setIsEditing(false);
      setEditedData({});
      setDirtyFields(new Set());
      setSelectedFiles({});
      setPendingCaptionEdits({});
      if (user?.email && requestId) {
        void clearEditRequestDraftSnapshot(user.email, requestId);
      }

      // Peer group members only have GET while AWAITING_APPROVAL. After Approve they
      // would land on Access Denied. The requestor stays — they still own the row.
      if (
        currentStage === RequestStage.AWAITING_APPROVAL &&
        viewerIsLiveApprovalGroupMember &&
        !viewerIsRequestor
      ) {
        navigate('/request');
      }
    } catch (error: any) {
      console.error('Failed to submit request:', error);

      // Ensure loading toast is dismissed on error
      showToast.dismiss(loadingToast);

      // Small delay before showing error toast
      setTimeout(() => {
        showToast.error(
          error.message || 'Failed to submit request. Please try again.',
        );
      }, 100);

      // Reset the refetching flag on error
      setIsRefetchingAfterSave(false);
    } finally {
      // Final safety check to ensure loading toast is dismissed
      setTimeout(() => {
        showToast.dismiss(loadingToast);
      }, 200);
    }
  };

  /**
   * Deliverables dialog confirm. Closes first, then submits: the dialog has no busy state, so
   * leaving it up for the length of the mutation would read as frozen, and the sponsor
   * gate inside `handleConfirmStageChange` closes it on failure anyway.
   */
  const handleDeliverablesDialogConfirm = async () => {
    restoreDeliverablesOnDialogCancelRef.current = null;
    setShowDeliverablesDialog(false);
    await handleConfirmStageChange();
  };

  /**
   * Cancel cancels the submit **and** the deliverable changes made inside the dialog
   * (amended matrix row). It rolls back to the restore point taken when the dialog opened,
   * rather than discarding everything pending, so a Cancel here cannot silently throw away
   * deliverable edits made in the section before Submit was pressed.
   */
  const handleDeliverablesDialogCancel = () => {
    restoreDeliverablesOnDialogCancelRef.current?.();
    restoreDeliverablesOnDialogCancelRef.current = null;
    setShowDeliverablesDialog(false);
  };

  /**
   * The third exit: keep the deliverable changes, do not submit.
   *
   * Without it the dialog's only two exits are both wrong for someone who opened Submit to
   * check what they had asked for, adjusted it, and then thought better of submitting: Confirm
   * is one-way and sends mail, and Cancel rolls the adjustment back.
   *
   * Drops the restore point first, as Confirm does. This is defence in depth rather than the
   * mechanism: `handleStageChangeClick` re-takes the point every time the dialog opens, so a
   * stale one cannot survive to be applied by a later Cancel. It is cleared because leaving a
   * rollback to a now-committed state lying around invites exactly that bug if the open path
   * ever changes.
   *
   * Closes before flushing for the same reason Confirm does: the dialog has no busy state, so
   * holding it up for the length of a write would read as frozen.
   *
   * Silent on success (human decision): the section behind the dialog re-renders with the saved
   * list, which is the confirmation. A FAILURE still speaks, because silence there would be
   * indistinguishable from success. `flushPendingDeliverableChanges` clears the pending list
   * only after the write lands, so a failed save stays retryable through Submit.
   */
  const handleDeliverablesDialogSave = async () => {
    restoreDeliverablesOnDialogCancelRef.current = null;
    setShowDeliverablesDialog(false);
    try {
      // The docblock above promises a failure speaks. A refusal does not throw, so without
      // this branch the promise held only for exceptions, and the dialog closed silently on
      // a write the server had refused — with the section's own error banner behind a step
      // the user may not be on. (MR !489 review, batch 1b.)
      const outcome =
        await deliverablesSaveHandle.flushPendingDeliverableChanges();
      if (outcome === 'conflict') {
        showToast.error(
          'The deliverables changed while you were editing, so your changes were not saved. Reopen the request to see the current list.',
        );
      } else if (outcome === 'failed') {
        showToast.error('Could not save the deliverables. Please try again.');
      }
    } catch (error) {
      console.error('Failed to save request deliverables:', error);
      showToast.error('Could not save the deliverables. Please try again.');
    }
  };

  const discardEditWork = useCallback(async () => {
    editDraftPersistSuspendedRef.current = true;

    // A newly opened repeatable editor is provisional until its unit-level Save.
    // Close it before dropping request edit mode so Cancel cannot strand an
    // unsaved draft as a read-only unit screen (#999 follow-up).
    instanceSaveHandleRef.current?.discardOpenInstanceEditor();
    rackSaveHandle?.discardOpenRackEditor();
    smallUpsSaveHandle?.discardOpenSmallUpsEditor();
    largeUpsSaveHandle?.discardOpenLargeUpsEditor();
    powerDistributionSaveHandle?.discardOpenPowerDistributionEditor();
    coolingSystemSaveHandle?.discardOpenCoolingSystemEditor();
    liquidCoolingCduSaveHandle?.discardOpenLiquidCoolingCduEditor();

    setEditedData({});
    setDirtyFields(new Set());
    setSelectedFiles({});
    setPendingCaptionEdits({});
    setRackSessionDirty(false);
    setRestoredRackBaseline(null);
    setSmallUpsSessionDirty(false);
    setRestoredSmallUpsBaseline(null);
    setLargeUpsSessionDirty(false);
    setRestoredLargeUpsBaseline(null);
    setCoolingSystemSessionDirty(false);
    setRestoredCoolingSystemBaseline(null);
    setPowerDistributionSessionDirty(false);
    setRestoredPowerDistributionBaseline(null);
    setLiquidCoolingCduSessionDirty(false);
    setRestoredLiquidCoolingCduBaseline(null);
    // Deliverables are request fields now, so Cancel throws their pending changes away
    // exactly like it throws away an edited customer name.
    discardPendingDeliverables();
    setIsEditing(false);
    // #1501: with aggregate save, Add/Remove Scope waits for Save — Cancel
    // restores the persisted scope (editedData.enabledStepIds is dropped above;
    // the context is reverted explicitly because it may hold the narrowed set).
    // Without it, scope changes persisted immediately and Cancel keeps them.
    if (featureFlags.aggregateSave) {
      requestScope?.revertScope(request?.enabledStepIds ?? null);
    }

    if (user?.email && requestId) {
      void clearEditRequestDraftSnapshot(user.email, requestId).finally(() => {
        editDraftPersistSuspendedRef.current = false;
      });
    } else {
      editDraftPersistSuspendedRef.current = false;
    }
  }, [
    coolingSystemSaveHandle,
    discardPendingDeliverables,
    largeUpsSaveHandle,
    liquidCoolingCduSaveHandle,
    powerDistributionSaveHandle,
    rackSaveHandle,
    request?.enabledStepIds,
    requestId,
    requestScope,
    smallUpsSaveHandle,
    user?.email,
  ]);

  const handleCancel = () => {
    void discardEditWork();
  };

  const { confirm, confirmationDialog } = useConfirmationDialog();

  const tryLeaveRequestDetail = useCallback(async (): Promise<boolean> => {
    if (!isEditing) {
      return true;
    }

    const hasFiles = Object.values(selectedFiles).some(
      files => files.length > 0,
    );
    const hasCaptions = Object.keys(pendingCaptionEdits).length > 0;
    const hasSmallUpsEditor =
      smallUpsSaveHandle?.hasUnsavedSmallUpsEditor() ?? false;
    const hasLargeUpsEditor =
      largeUpsSaveHandle?.hasUnsavedLargeUpsEditor() ?? false;
    const hasPowerDistributionEditor =
      powerDistributionSaveHandle?.hasUnsavedPowerDistributionEditor() ?? false;
    const hasCoolingSystemEditor =
      coolingSystemSaveHandle?.hasUnsavedCoolingSystemEditor() ?? false;
    const hasLiquidCoolingCduEditor =
      liquidCoolingCduSaveHandle?.hasUnsavedLiquidCoolingCduEditor() ?? false;
    const instanceSave = instanceSaveHandleRef.current;
    const hasInstanceEditor = instanceSave?.hasUnsavedInstanceEditor() ?? false;
    const hasRackEditor = rackSaveHandle?.hasUnsavedRackEditor() ?? false;
    const hasRackSession =
      rackSaveHandle?.hasPendingRackChanges() ?? rackSessionDirty;
    const hasSmallUpsSession =
      smallUpsSaveHandle?.hasPendingSmallUpsChanges() ?? smallUpsSessionDirty;
    const hasLargeUpsSession =
      largeUpsSaveHandle?.hasPendingLargeUpsChanges() ?? largeUpsSessionDirty;
    const hasPowerDistributionSession =
      powerDistributionSaveHandle?.hasPendingPowerDistributionChanges() ??
      powerDistributionSessionDirty;
    const hasCoolingSystemSession =
      coolingSystemSaveHandle?.hasPendingCoolingSystemChanges() ??
      coolingSystemSessionDirty;
    const hasLiquidCoolingCduSession =
      liquidCoolingCduSaveHandle?.hasPendingLiquidCoolingCduChanges() ??
      liquidCoolingCduSessionDirty;
    const hasFieldEdits = dirtyFields.size > 0;
    const hasDeliverableChanges =
      deliverablesSaveHandle.hasPendingDeliverableChanges();

    if (
      !hasDeliverableChanges &&
      !hasFiles &&
      !hasCaptions &&
      !hasInstanceEditor &&
      !hasSmallUpsEditor &&
      !hasLargeUpsEditor &&
      !hasPowerDistributionEditor &&
      !hasCoolingSystemEditor &&
      !hasLiquidCoolingCduEditor &&
      !hasRackEditor &&
      !hasRackSession &&
      !hasSmallUpsSession &&
      !hasLargeUpsSession &&
      !hasPowerDistributionSession &&
      !hasCoolingSystemSession &&
      !hasLiquidCoolingCduSession &&
      !hasFieldEdits
    ) {
      return true;
    }

    const confirmed = await confirm({
      title: RACK_DISCARD_UNSAVED_TITLE,
      message: RACK_DISCARD_UNSAVED_MESSAGE,
      confirmText: RACK_DISCARD_UNSAVED_DISCARD_LABEL,
      cancelText: RACK_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
      confirmButtonStyle: 'danger',
    });

    if (!confirmed) {
      return false;
    }

    instanceSave?.discardOpenInstanceEditor();
    smallUpsSaveHandle?.discardOpenSmallUpsEditor();
    largeUpsSaveHandle?.discardOpenLargeUpsEditor();
    powerDistributionSaveHandle?.discardOpenPowerDistributionEditor();
    coolingSystemSaveHandle?.discardOpenCoolingSystemEditor();
    liquidCoolingCduSaveHandle?.discardOpenLiquidCoolingCduEditor();
    rackSaveHandle?.discardOpenRackEditor();
    void discardEditWork();
    return true;
  }, [
    confirm,
    deliverablesSaveHandle,
    dirtyFields.size,
    discardEditWork,
    isEditing,
    pendingCaptionEdits,
    rackSaveHandle,
    rackSessionDirty,
    smallUpsSaveHandle,
    smallUpsSessionDirty,
    largeUpsSaveHandle,
    largeUpsSessionDirty,
    powerDistributionSaveHandle,
    powerDistributionSessionDirty,
    coolingSystemSaveHandle,
    coolingSystemSessionDirty,
    liquidCoolingCduSaveHandle,
    liquidCoolingCduSessionDirty,
    selectedFiles,
  ]);

  useRegisterRequestLeaveGuard(tryLeaveRequestDetail);

  const tryChangeRequestStep = useCallback(
    async (nextStepId: string): Promise<boolean> => {
      if (nextStepId === stepId) {
        return true;
      }
      const instanceSave = instanceSaveHandleRef.current;
      if (instanceSave?.isInstanceEditorOpen()) {
        const mayLeave = await instanceSave.tryDismissInstanceEditor();
        if (!mayLeave) {
          return false;
        }
      } else {
        instanceSave?.discardOpenInstanceEditor();
      }
      const smallUpsSave = smallUpsSaveHandle;
      if (smallUpsSave?.isSmallUpsEditorOpen()) {
        const mayLeave = await smallUpsSave.tryDismissSmallUpsEditor();
        if (!mayLeave) {
          return false;
        }
      } else {
        smallUpsSave?.discardOpenSmallUpsEditor();
      }
      const largeUpsSave = largeUpsSaveHandle;
      if (largeUpsSave?.isLargeUpsEditorOpen()) {
        const mayLeave = await largeUpsSave.tryDismissLargeUpsEditor();
        if (!mayLeave) {
          return false;
        }
      } else {
        largeUpsSave?.discardOpenLargeUpsEditor();
      }
      const powerDistributionSave = powerDistributionSaveHandle;
      if (powerDistributionSave?.isPowerDistributionEditorOpen()) {
        const mayLeave =
          await powerDistributionSave.tryDismissPowerDistributionEditor();
        if (!mayLeave) {
          return false;
        }
      } else {
        powerDistributionSave?.discardOpenPowerDistributionEditor();
      }
      const coolingSystemSave = coolingSystemSaveHandle;
      if (coolingSystemSave?.isCoolingSystemEditorOpen()) {
        const mayLeave =
          await coolingSystemSave.tryDismissCoolingSystemEditor();
        if (!mayLeave) {
          return false;
        }
      } else {
        coolingSystemSave?.discardOpenCoolingSystemEditor();
      }
      const liquidCoolingCduSave = liquidCoolingCduSaveHandle;
      if (liquidCoolingCduSave?.isLiquidCoolingCduEditorOpen()) {
        const mayLeave =
          await liquidCoolingCduSave.tryDismissLiquidCoolingCduEditor();
        if (!mayLeave) {
          return false;
        }
      } else {
        liquidCoolingCduSave?.discardOpenLiquidCoolingCduEditor();
      }
      rackSaveHandle?.discardOpenRackEditor();
      return true;
    },
    [
      coolingSystemSaveHandle,
      largeUpsSaveHandle,
      liquidCoolingCduSaveHandle,
      powerDistributionSaveHandle,
      rackSaveHandle,
      smallUpsSaveHandle,
      stepId,
    ],
  );

  useRegisterRequestStepChangeGuard(tryChangeRequestStep);

  const handleSave = async () => {
    dismissActiveRichTextKeyboard();
    if (!(await waitForInlineImagePipelineIdle())) {
      showToast.error(
        'Wait for inline images to finish uploading before saving.',
      );
      return;
    }
    if (!request || !requestId) {
      return;
    }

    // These seven are used throughout handleSave (pending-list flush, session
    // dirty checks). The repeatable-save-target refactor moved the
    // `instanceSave` binding into the block below and dropped the other six,
    // leaving 28 dangling references from here down.
    const rackSave = rackSaveHandle;
    const instanceSave = instanceSaveHandleRef.current;
    const smallUpsSave = smallUpsSaveHandle;
    const largeUpsSave = largeUpsSaveHandle;
    const powerDistributionSave = powerDistributionSaveHandle;
    const coolingSystemSave = coolingSystemSaveHandle;
    const liquidCoolingCduSave = liquidCoolingCduSaveHandle;

    if (repeatableSaveTarget) {
      setIsManualSaving(true);
      try {
        const committed = await commitRepeatableSaveTarget(
          repeatableSaveTarget,
          {
            'facility-power': instanceSave
              ? {
                  isOpen: instanceSave.isInstanceEditorOpen,
                  commit: instanceSave.commitOpenInstanceEditor,
                }
              : undefined,
            rack: rackSaveHandle
              ? {
                  isOpen: rackSaveHandle.isRackEditorOpen,
                  commit: rackSaveHandle.commitOpenRackEditor,
                }
              : undefined,
            'small-ups': smallUpsSaveHandle
              ? {
                  isOpen: smallUpsSaveHandle.isSmallUpsEditorOpen,
                  commit: smallUpsSaveHandle.commitOpenSmallUpsEditor,
                }
              : undefined,
            'large-ups': largeUpsSaveHandle
              ? {
                  isOpen: largeUpsSaveHandle.isLargeUpsEditorOpen,
                  commit: largeUpsSaveHandle.commitOpenLargeUpsEditor,
                }
              : undefined,
            'power-distribution': powerDistributionSaveHandle
              ? {
                  isOpen:
                    powerDistributionSaveHandle.isPowerDistributionEditorOpen,
                  commit:
                    powerDistributionSaveHandle.commitOpenPowerDistributionEditor,
                }
              : undefined,
            cooling: coolingSystemSaveHandle
              ? {
                  isOpen: coolingSystemSaveHandle.isCoolingSystemEditorOpen,
                  commit: coolingSystemSaveHandle.commitOpenCoolingSystemEditor,
                }
              : undefined,
            'liquid-cooling': liquidCoolingCduSaveHandle
              ? {
                  isOpen:
                    liquidCoolingCduSaveHandle.isLiquidCoolingCduEditorOpen,
                  commit:
                    liquidCoolingCduSaveHandle.commitOpenLiquidCoolingCduEditor,
                }
              : undefined,
          },
        );
        const feedback = {
          'facility-power': [
            'Facility Power saved.',
            'Could not save Facility Power. Please try again.',
          ],
          rack: ['Rack saved.', 'Could not save rack. Please try again.'],
          'small-ups': [
            'Small UPS saved.',
            'Could not save small UPS. Please try again.',
          ],
          'large-ups': [
            'Large UPS saved.',
            'Could not save large UPS. Please try again.',
          ],
          'power-distribution': [
            'Power distribution saved.',
            'Could not save power distribution. Please try again.',
          ],
          cooling: [
            'Cooling system saved.',
            'Could not save cooling system. Please try again.',
          ],
          'liquid-cooling': [
            'CDU saved.',
            'Could not save CDU. Please try again.',
          ],
        } as const;
        if (committed) {
          showToast.success(feedback[repeatableSaveTarget][0]);
        } else {
          showToast.error(feedback[repeatableSaveTarget][1]);
        }
      } finally {
        setIsManualSaving(false);
      }
      return;
    }

    // Manual Save stays on stage — never re-gate Vertiv Sponsor (Story 3.8 / #1401).
    // SoT wired so future regressions cannot skip the helper.
    if (requiresVertivSponsorForManualSave()) {
      const sponsors =
        editedData.vertivSponsors !== undefined
          ? editedData.vertivSponsors
          : request.vertivSponsors || [];
      if (!hasVertivSponsorSelection(sponsors)) {
        showToast.error('Please select at least one Vertiv Sponsor.');
        return;
      }
    }

    // Note: Team member selection for CONFIGURATION stage is now optional
    // Validation removed per requirements - vertiv/partner contributor fields are optional

    // Validate load percentage range (0-100)
    if (
      editedData.smallUpsLoadPercentage !== undefined &&
      editedData.smallUpsLoadPercentage !== ''
    ) {
      const pct = parseFloat(String(editedData.smallUpsLoadPercentage));
      if (isNaN(pct) || pct < 0 || pct > 100) {
        showToast.error('Load Percentage must be between 0 and 100.');
        return;
      }
    }

    const hasNewFiles = Object.keys(selectedFiles).some(
      section => selectedFiles[section].length > 0,
    );
    const hasCaptionEdits = Object.entries(pendingCaptionEdits).length > 0;
    const pendingRackOps = await getPendingRackOpsForRequest(requestId);

    const hasPendingFacilityPowerListChanges =
      instanceSave?.hasPendingListChanges?.() ?? false;
    const hasPendingRackSession =
      rackSave?.hasPendingRackChanges() ?? rackSessionDirty;
    const hasPendingSmallUpsSession =
      smallUpsSave?.hasPendingSmallUpsChanges() ?? smallUpsSessionDirty;
    const hasPendingLargeUpsSession =
      largeUpsSave?.hasPendingLargeUpsChanges() ?? largeUpsSessionDirty;
    const hasPendingPowerDistributionSession =
      powerDistributionSave?.hasPendingPowerDistributionChanges() ??
      powerDistributionSessionDirty;
    const hasPendingCoolingSystemSession =
      coolingSystemSave?.hasPendingCoolingSystemChanges() ??
      coolingSystemSessionDirty;
    const hasPendingLiquidCoolingCduSession =
      liquidCoolingCduSave?.hasPendingLiquidCoolingCduChanges() ??
      liquidCoolingCduSessionDirty;

    const hasPendingDeliverableChanges =
      deliverablesSaveHandle.hasPendingDeliverableChanges();

    const hasRequestFieldChanges =
      dirtyFields.size > 0 || hasNewFiles || hasCaptionEdits;
    const hasRackOnlyChanges =
      pendingRackOps.length > 0 || hasPendingRackSession;
    // Deliverables sit here rather than with the request fields: like the repeatable
    // sections they are their own records, so a save carrying only deliverable changes must
    // not PATCH the requests table.
    const hasRepeatableChildTableChanges =
      hasPendingSmallUpsSession ||
      hasPendingLargeUpsSession ||
      hasPendingPowerDistributionSession ||
      hasPendingCoolingSystemSession ||
      hasPendingLiquidCoolingCduSession ||
      hasPendingFacilityPowerListChanges ||
      hasPendingDeliverableChanges;

    if (
      !hasRequestFieldChanges &&
      !hasRackOnlyChanges &&
      !hasRepeatableChildTableChanges
    ) {
      showToast.info('No changes to save');
      return;
    }

    const specifySaveError = opportunitySpecifySaveError(
      editedData as Record<string, unknown>,
    );
    if (specifySaveError) {
      showToast.error(specifySaveError);
      return;
    }

    // #1501: scopes removed during this edit session (deferred from Remove
    // Scope). The parent write below runs the server scope-clear; the local
    // caches are purged after it, and those families send no row ops.
    const scopeRemovedStepIds = dirtyFields.has('enabledStepIds')
      ? diffRemovedOptionalStepIds(
          request.enabledStepIds ?? null,
          (editedData.enabledStepIds as RequestFormStepId[] | undefined) ??
            null,
        )
      : [];
    const scopeRemovedFamilies = new Set(
      expandRemovedStepIdsForDataClear(
        scopeRemovedStepIds,
        (editedData.enabledStepIds as string[] | undefined) ?? null,
      ),
    );

    setIsManualSaving(true);
    editDraftPersistSuspendedRef.current = true;
    // #1501: every op this Save queues offline carries one saveGroupId, so a
    // later sync can batch the group into one aggregate call (one history
    // entry per original Save). Ended in the finally below — never leaks.
    offlineCache.beginOfflineSaveGroup();
    const loadingToast = showToast.loading('Saving changes...');

    // Staged uploads scoped to a NEW repeatable unit carry its temp id in the
    // section key. The rack flush below swaps temp→real ids, and its server-side
    // attachment remap only moves rows already in the DB — so these keys must be
    // remapped BEFORE uploadFilesForRequest runs, or the file lands in a dead
    // `unit.{tempId}` bucket the saved unit's editor never reads (#997 Obs 6).
    let filesForUpload = selectedFiles;
    /**
     * What the deliverables flush achieved, read by every success toast below so none of
     * them can claim a save the server refused. `'nothing'` while there is nothing pending.
     */
    let deliverablesOutcome: DeliverablesFlushOutcome = 'nothing';

    try {
      // Deliverables first: no id remapping, no attachment interaction, and it keeps the
      // "Save commits the pending deliverable changes together with the rest of the form"
      // rule true even if a later step of the save bails out early.
      //
      // The outcome is captured rather than discarded. A refusal does not throw — aborting
      // the whole save over one refused section would be worse than the refusal — so
      // without this the save ran on to "Changes saved successfully!" while the server
      // still held the old list. (MR !489 review, batch 1b.)
      if (hasPendingDeliverableChanges) {
        deliverablesOutcome =
          await deliverablesSaveHandle.flushPendingDeliverableChanges();
      }

      if (hasPendingFacilityPowerListChanges && instanceSave) {
        await instanceSave.flushPendingListChanges?.();
      } else if (hasPendingFacilityPowerListChanges && !instanceSave) {
        showToast.dismiss(loadingToast);
        setIsManualSaving(false);
        editDraftPersistSuspendedRef.current = false;
        showToast.info(
          'Facility Power changes are still loading. Try Save Changes again.',
        );
        return;
      }

      // Shared by the legacy PATCH path below and the aggregate save branch:
      // the parent request's dirty-field body, exactly as the PATCH always
      // built it (#1501 — hoisted verbatim, one implementation for both paths).
      const instanceUnitRemaps: Array<{
        idMap: Map<number, number>;
        fields: ReadonlySet<string>;
      }> = [];
      const buildParentPatchData = () => {
        const processedData: Record<string, any> = {};
        dirtyFields.forEach(field => {
          processedData[field] = editedData[field];
        });
        if (
          instanceUnitRemaps.length > 0 &&
          processedData.dimensionUnits != null
        ) {
          let units: unknown = processedData.dimensionUnits;
          for (const remap of instanceUnitRemaps) {
            units = remapInstanceUnitIds(units, remap.idMap, remap.fields);
          }
          processedData.dimensionUnits = units;
        }
        const dirtyFacilityPowerInstances = dirtyFields.has(
          'facilityPowerInstances',
        )
          ? processedData.facilityPowerInstances
          : undefined;

        // Team member save does a full PUT replacement — if ANY team role changed,
        // include ALL team roles so unchanged roles aren't dropped.
        const teamFields = [
          'vertivSponsors',
          'vertivContributors',
          'partnerContributors',
        ];
        if (teamFields.some(f => dirtyFields.has(f))) {
          teamFields.forEach(f => {
            if (processedData[f] === undefined) {
              processedData[f] = editedData[f];
            }
          });
          if (processedData.useDefaultSponsors === undefined) {
            processedData.useDefaultSponsors = editedData.useDefaultSponsors;
          }
          if (processedData.region === undefined) {
            processedData.region = editedData.region;
          }
        }

        // Convert JSON strings back to arrays for team member fields
        teamFields.forEach(field => {
          if (
            processedData[field] &&
            typeof processedData[field] === 'string'
          ) {
            try {
              processedData[field] = JSON.parse(processedData[field]);
            } catch (e) {
              // Field is not valid JSON, keeping as string
            }
          }
        });

        if (processedData.requestForInformation === 'true') {
          processedData.requestForInformation = true;
        } else if (processedData.requestForInformation === 'false') {
          processedData.requestForInformation = false;
        }

        normalizeRequestNumericFields(processedData);
        Object.assign(
          processedData,
          prepareTextAreaFieldsForTransport(processedData),
        );

        // Team fields (and useDefaultSponsors) never go to the requests table —
        // they ride the team segment/endpoint.
        const {
          vertivSponsors: _,
          vertivContributors: __,
          partnerContributors: ___,
          useDefaultSponsors: ____,
          ...requestDataWithInstances
        } = processedData;
        const requestDataWithoutTeam = stripRepeatableInstanceArraysFromPatch(
          requestDataWithInstances,
        );
        return {
          processedData,
          requestDataWithoutTeam,
          dirtyFacilityPowerInstances,
        };
      };

      // ── #1501 Option B: ONE aggregate call (parent + team + all six families)
      // replaces the per-family flushes, the request PATCH and the team PUT
      // below — one Version History entry per Save. Uploads, captions and the
      // refetch tail are shared with the legacy path. Falls back to the legacy
      // sequence when the server flag is off (404 AGGREGATE_SAVE_DISABLED),
      // when offline, for offline requests, or when queued offline ops exist.
      let aggregateHandled = false;
      const aggregateBridgesReady =
        (!hasPendingRackSession || !!rackSave) &&
        (!hasPendingSmallUpsSession || !!smallUpsSave) &&
        (!hasPendingLargeUpsSession || !!largeUpsSave) &&
        (!hasPendingPowerDistributionSession || !!powerDistributionSave) &&
        (!hasPendingCoolingSystemSession || !!coolingSystemSave) &&
        (!hasPendingLiquidCoolingCduSession || !!liquidCoolingCduSave);
      if (
        featureFlags.aggregateSave &&
        isOnline &&
        !requestId.startsWith('o-') &&
        pendingRackOps.length === 0 &&
        aggregateBridgesReady
      ) {
        const {
          processedData: aggProcessed,
          requestDataWithoutTeam: aggParent,
        } = buildParentPatchData();
        const wantsTeam =
          aggProcessed.vertivSponsors !== undefined ||
          aggProcessed.vertivContributors !== undefined ||
          aggProcessed.partnerContributors !== undefined;
        const parentData =
          Object.keys(aggParent).length > 0 &&
          !dirtyFieldsAreOnlyRepeatablePatchKeys(dirtyFields)
            ? aggParent
            : {};
        try {
          const teamRows = wantsTeam
            ? await buildTeamMembersFromEditedData(requestId, aggProcessed)
            : null;
          // A family whose scope this Save removes sends no row ops — the
          // server's scope-clear deletes its rows in the parent segment.
          const unlessRemoved = <T,>(stepKey: string, bridge: T) =>
            scopeRemovedFamilies.has(stepKey as OptionalRequestFormStepId)
              ? null
              : bridge;
          const bridges: AggregateFamilyBridges = {
            racks: unlessRemoved('rack-power', rackSave),
            smallUps: unlessRemoved('small-ups', smallUpsSave),
            largeUps: unlessRemoved('large-ups', largeUpsSave),
            coolingSystems: unlessRemoved(
              'cooling-readiness',
              coolingSystemSave,
            ),
            liquidCoolingCdus: unlessRemoved(
              'liquid-cooling',
              liquidCoolingCduSave,
            ),
            powerDistribution: unlessRemoved(
              'power-distribution',
              powerDistributionSave,
            ),
          };
          const outcome = await attemptAggregateSave({
            requestId,
            parentData,
            teamRows: teamRows as never,
            bridges,
          });
          if (outcome.kind !== 'unsupported') {
            aggregateHandled = true;
            // #997 Obs 6: remap staged uploads on NEW units before the upload.
            for (const idMap of outcome.createdIdMapsByFamily.values()) {
              if (idMap.size > 0) {
                filesForUpload = remapSelectedFilesForRepeatableInstances(
                  filesForUpload,
                  idMap,
                );
              }
            }
            setSelectedFiles(filesForUpload);
            if (outcome.result?.status === 'partial') {
              const failedSection =
                outcome.result.failed?.section ?? 'a section';
              showToast.dismiss(loadingToast);
              setIsManualSaving(false);
              editDraftPersistSuspendedRef.current = false;
              showToast.error(
                `Some changes could not be saved (${failedSection}). Everything before it was saved — press Save Changes again to retry the rest.`,
              );
              return;
            }
          }
        } catch (aggregateError) {
          console.error('Aggregate save failed:', aggregateError);
          showToast.dismiss(loadingToast);
          setIsManualSaving(false);
          editDraftPersistSuspendedRef.current = false;
          showToast.error('Could not save changes. Please try again.');
          return;
        }
      }

      if (!aggregateHandled && hasPendingSmallUpsSession && smallUpsSave) {
        const smallUpsCreatedIdMap =
          await smallUpsSave.flushPendingSmallUpsChanges();
        if (smallUpsCreatedIdMap && smallUpsCreatedIdMap.size > 0) {
          // #997 Story 6.4 / Obs 6: remap staged uploads on NEW units from
          // `base.unit.{temp}` → `base.unit.{real}` BEFORE the file upload — the
          // flush's server-side remap only moves rows already in the DB.
          filesForUpload = remapSelectedFilesForRepeatableInstances(
            filesForUpload,
            smallUpsCreatedIdMap,
          );
          setSelectedFiles(filesForUpload);
        }
      } else if (
        !aggregateHandled &&
        hasPendingSmallUpsSession &&
        !smallUpsSave
      ) {
        showToast.dismiss(loadingToast);
        setIsManualSaving(false);
        editDraftPersistSuspendedRef.current = false;
        showToast.info(
          'Small UPS changes are still loading. Try Save Changes again.',
        );
        return;
      }

      if (!aggregateHandled && hasPendingLargeUpsSession && largeUpsSave) {
        const largeUpsCreatedIdMap =
          await largeUpsSave.flushPendingLargeUpsChanges();
        if (largeUpsCreatedIdMap && largeUpsCreatedIdMap.size > 0) {
          // #997 Story 6.5 / Obs 6: remap staged uploads on NEW units from
          // `base.unit.{temp}` → `base.unit.{real}` BEFORE the file upload — the
          // flush's server-side remap only moves rows already in the DB.
          filesForUpload = remapSelectedFilesForRepeatableInstances(
            filesForUpload,
            largeUpsCreatedIdMap,
          );
          setSelectedFiles(filesForUpload);
        }
      } else if (
        !aggregateHandled &&
        hasPendingLargeUpsSession &&
        !largeUpsSave
      ) {
        showToast.dismiss(loadingToast);
        setIsManualSaving(false);
        editDraftPersistSuspendedRef.current = false;
        showToast.info(
          'Large UPS changes are still loading. Try Save Changes again.',
        );
        return;
      }

      if (
        !aggregateHandled &&
        hasPendingPowerDistributionSession &&
        powerDistributionSave
      ) {
        const powerDistributionCreatedIdMap =
          await powerDistributionSave.flushPendingPowerDistributionChanges();
        if (
          powerDistributionCreatedIdMap &&
          powerDistributionCreatedIdMap.size > 0
        ) {
          instanceUnitRemaps.push({
            idMap: powerDistributionCreatedIdMap,
            fields: INSTANCE_UNIT_FIELDS.powerDistribution,
          });
          // #997 Story 6.7 / Obs 6: remap staged uploads on NEW units from
          // `base.unit.{temp}` → `base.unit.{real}` BEFORE the file upload — the
          // flush's server-side remap only moves rows already in the DB.
          filesForUpload = remapSelectedFilesForRepeatableInstances(
            filesForUpload,
            powerDistributionCreatedIdMap,
          );
          setSelectedFiles(filesForUpload);
        }
      } else if (
        !aggregateHandled &&
        hasPendingPowerDistributionSession &&
        !powerDistributionSave
      ) {
        showToast.dismiss(loadingToast);
        setIsManualSaving(false);
        editDraftPersistSuspendedRef.current = false;
        showToast.info(
          'Power distribution changes are still loading. Try Save Changes again.',
        );
        return;
      }

      if (
        !aggregateHandled &&
        hasPendingCoolingSystemSession &&
        coolingSystemSave
      ) {
        const coolingSystemCreatedIdMap =
          await coolingSystemSave.flushPendingCoolingSystemChanges();
        if (coolingSystemCreatedIdMap && coolingSystemCreatedIdMap.size > 0) {
          instanceUnitRemaps.push({
            idMap: coolingSystemCreatedIdMap,
            fields: INSTANCE_UNIT_FIELDS.coolingSystems,
          });
          // #997 Story 6.6 / Obs 6: remap staged uploads on NEW units from
          // `base.unit.{temp}` → `base.unit.{real}` BEFORE the file upload — the
          // flush's server-side remap only moves rows already in the DB.
          filesForUpload = remapSelectedFilesForRepeatableInstances(
            filesForUpload,
            coolingSystemCreatedIdMap,
          );
          setSelectedFiles(filesForUpload);
        }
      } else if (
        !aggregateHandled &&
        hasPendingCoolingSystemSession &&
        !coolingSystemSave
      ) {
        showToast.dismiss(loadingToast);
        setIsManualSaving(false);
        editDraftPersistSuspendedRef.current = false;
        showToast.info(
          'Cooling system changes are still loading. Try Save Changes again.',
        );
        return;
      }

      if (
        !aggregateHandled &&
        hasPendingLiquidCoolingCduSession &&
        liquidCoolingCduSave
      ) {
        const liquidCoolingCduCreatedIdMap =
          await liquidCoolingCduSave.flushPendingLiquidCoolingCduChanges();
        if (
          liquidCoolingCduCreatedIdMap &&
          liquidCoolingCduCreatedIdMap.size > 0
        ) {
          // #997 Story 6.8 / Obs 6: remap staged uploads on NEW CDUs from
          // `base.unit.{temp}` → `base.unit.{real}` BEFORE the file upload — the
          // flush's server-side remap only moves rows already in the DB.
          filesForUpload = remapSelectedFilesForRepeatableInstances(
            filesForUpload,
            liquidCoolingCduCreatedIdMap,
          );
          setSelectedFiles(filesForUpload);
        }
      } else if (
        !aggregateHandled &&
        hasPendingLiquidCoolingCduSession &&
        !liquidCoolingCduSave
      ) {
        showToast.dismiss(loadingToast);
        setIsManualSaving(false);
        editDraftPersistSuspendedRef.current = false;
        showToast.info(
          'Liquid cooling CDU changes are still loading. Try Save Changes again.',
        );
        return;
      }

      if (!aggregateHandled && hasPendingRackSession && rackSave) {
        const rackCreatedIdMap = await rackSave.flushPendingRackChanges();
        if (rackCreatedIdMap && rackCreatedIdMap.size > 0) {
          instanceUnitRemaps.push({
            idMap: rackCreatedIdMap,
            fields: INSTANCE_UNIT_FIELDS.racks,
          });
          filesForUpload = remapSelectedFilesForRepeatableInstances(
            filesForUpload,
            rackCreatedIdMap,
          );
          // Persist the remap so a retry after a later save-step failure does
          // not re-stage the files under the now-dead temp keys.
          setSelectedFiles(filesForUpload);
        }
      } else if (!aggregateHandled && hasPendingRackSession && !rackSave) {
        showToast.dismiss(loadingToast);
        setIsManualSaving(false);
        editDraftPersistSuspendedRef.current = false;
        showToast.info(
          'Rack changes are still loading. Try Save Changes again.',
        );
        return;
      } else if (
        !hasRequestFieldChanges &&
        !hasRepeatableChildTableChanges &&
        pendingRackOps.length > 0
      ) {
        const syncResult = await syncService.syncOfflineRackOperations();
        if (syncResult.failed > 0) {
          showToast.dismiss(loadingToast);
          editDraftPersistSuspendedRef.current = false;
          showToast.error(
            'Some rack changes could not sync. Check your connection and try again.',
          );
          return;
        }
        await queryClient.invalidateQueries({
          queryKey: rackConfigurationsKeys.list(requestId),
        });
      }

      if (
        !hasRequestFieldChanges &&
        !hasNewFiles &&
        !hasCaptionEdits &&
        !hasRepeatableChildTableChanges
      ) {
        showToast.dismiss(loadingToast);
        setIsEditing(false);
        setEditedData({});
        setDirtyFields(new Set());
        setRackSessionDirty(false);
        setRestoredRackBaseline(null);
        setSmallUpsSessionDirty(false);
        setRestoredSmallUpsBaseline(null);
        setLargeUpsSessionDirty(false);
        setRestoredLargeUpsBaseline(null);
        setPowerDistributionSessionDirty(false);
        setRestoredPowerDistributionBaseline(null);
        setCoolingSystemSessionDirty(false);
        setRestoredCoolingSystemBaseline(null);
        setLiquidCoolingCduSessionDirty(false);
        setRestoredLiquidCoolingCduBaseline(null);
        setIsManualSaving(false);
        if (user?.email && requestId) {
          void clearEditRequestDraftSnapshot(user.email, requestId).finally(
            () => {
              editDraftPersistSuspendedRef.current = false;
            },
          );
        } else {
          editDraftPersistSuspendedRef.current = false;
        }
        reportSaveOutcome(
          deliverablesOutcome,
          isOnline
            ? 'Changes saved successfully!'
            : 'Changes saved offline. Will sync when back online.',
        );
        return;
      }

      // First, upload any selected files (only when online and not an offline request)
      // Note: File uploads require a server-side request ID, so offline requests can't upload files
      const isOfflineRequest = requestId.startsWith('o-');
      if (hasNewFiles && isOnline && !isOfflineRequest) {
        showToast.dismiss(loadingToast);
        const uploadingToast = showToast.loading('Uploading files...');

        const uploadResult = await fileUploadService.uploadFilesForRequest(
          parseInt(requestId, 10),
          filesForUpload,
          progress => {
            setUploadProgress(progress);
          },
        );

        showToast.dismiss(uploadingToast);
        // Don't clear progress bar yet - keep it showing during the save process
        setUploadProgress({
          section: 'request-data',
          fileName: 'Saving request...',
          progress: 100,
          status: 'uploading',
        });

        if (!uploadResult.success) {
          setUploadProgress(null); // Clear on error
          setIsManualSaving(false); // Re-enable all inputs
          showToast.error(
            `Failed to upload some files: ${uploadResult.errors.join(', ')}`,
          );
          return;
        }

        // Refresh attachments to show the new files
        await refetchAttachments();
      } else if (hasNewFiles && (!isOnline || isOfflineRequest)) {
        // Warn about file uploads while offline or for offline requests
        console.warn(
          'File uploads skipped -',
          isOfflineRequest ? 'offline request (sync first)' : 'offline mode',
        );
      }

      // Build PATCH body from only dirty fields (proper HTTP PATCH semantics)
      const {
        processedData,
        requestDataWithoutTeam,
        dirtyFacilityPowerInstances,
      } = buildParentPatchData();

      // Set the refetching flag BEFORE calling the mutation to prevent any flash
      setIsRefetchingAfterSave(true);

      // Only PATCH fields the requests table understands. Repeatable rows (Facility Power
      // instances, child-table drafts) persist via cache flush or dedicated APIs — sending
      // them here 500s after stage advance when those are the only dirty fields.
      if (
        !aggregateHandled &&
        Object.keys(requestDataWithoutTeam).length > 0 &&
        !dirtyFieldsAreOnlyRepeatablePatchKeys(dirtyFields)
      ) {
        await updateRequestMutation.mutateAsync({
          id: requestId,
          data: requestDataWithoutTeam,
        });
      }

      // Save team members using consolidated approach.
      // Online this makes ONE PUT request instead of three separate requests. Offline it
      // queues onto the request's own update queue, which the sync replays through
      // `saveTeamMembersFromForm`. The `isOnline &&` that used to sit on this condition
      // meant an offline team edit was neither saved nor queued — silently dropped (C2).
      if (
        !aggregateHandled &&
        (processedData.vertivSponsors !== undefined ||
          processedData.vertivContributors !== undefined ||
          processedData.partnerContributors !== undefined)
      ) {
        try {
          if (isOnline) {
            await saveAllTeamMembersFromEditedData(requestId, processedData);

            // Wait a brief moment to ensure database transaction is committed
            await new Promise(resolve => setTimeout(resolve, 200));
          } else {
            await queueOfflineTeamMemberEdit(requestId, processedData);
          }
        } catch (teamError) {
          console.error('Failed to update team members:', teamError);
          // Don't throw - we want the main request update to succeed
        }
      }

      // Save pending caption edits (only when online)
      const captionEntries = Object.entries(pendingCaptionEdits);
      if (isOnline && captionEntries.length > 0) {
        const failedCaptions: number[] = [];
        for (const [attachmentIdStr, caption] of captionEntries) {
          const attachmentId = parseInt(attachmentIdStr, 10);
          try {
            await attachmentService.updateAttachmentCaption(
              attachmentId,
              caption,
            );
          } catch (captionError) {
            console.error(
              `Failed to save caption for attachment ${attachmentId}:`,
              captionError,
            );
            failedCaptions.push(attachmentId);
            // Don't throw - continue saving other captions
          }
        }
        if (failedCaptions.length > 0) {
          showToast.error(
            `Failed to save ${failedCaptions.length} caption(s). Please try saving again.`,
          );
          // Retain only the failed caption edits so user can retry
          const failedSet = new Set(failedCaptions);
          setPendingCaptionEdits(prev => {
            const retained: Record<number, string> = {};
            for (const [id, cap] of Object.entries(prev)) {
              if (failedSet.has(Number(id))) {
                retained[Number(id)] = cap;
              }
            }
            return retained;
          });
        } else {
          setPendingCaptionEdits({});
        }
      } else if (!isOnline && captionEntries.length > 0) {
        console.warn('Caption updates skipped - offline mode');
      }

      // Persist client-cache repeatable rows before refetch so server payload
      // without facilityPowerInstances does not wipe local session rows.
      if (Array.isArray(dirtyFacilityPowerInstances)) {
        await persistDirtyFacilityPowerInstances(dirtyFacilityPowerInstances);
      }

      if (isOnline && request && requestForSteps) {
        const previousReferences = new Set(
          collectRequestRichHtmlAttachmentIds(
            request as unknown as Record<string, unknown>,
          ),
        );
        const currentReferences = new Set(
          collectRequestRichHtmlAttachmentIds(
            requestForSteps as unknown as Record<string, unknown>,
          ),
        );
        const removedReferences = new Set(
          Array.from(previousReferences).filter(
            id => !currentReferences.has(id),
          ),
        );
        const orphanIds = new Set<number>();
        Object.values(attachmentsBySection).forEach(attachments => {
          attachments.forEach(attachment => {
            if (
              attachmentService.isInlineAttachment(attachment) &&
              removedReferences.has(attachment.id)
            ) {
              orphanIds.add(attachment.id);
            }
          });
        });

        let failedOrphanDeletes = 0;
        for (const attachmentId of orphanIds) {
          const result = await attachmentService.deleteAttachment(attachmentId);
          if (!result.success) {
            failedOrphanDeletes += 1;
          }
        }
        if (failedOrphanDeletes > 0) {
          showToast.error(
            `Saved changes, but ${failedOrphanDeletes} removed inline image(s) could not be deleted.`,
          );
        }
      }

      // #1501: the parent write above ran the server scope-clear — now drop the
      // removed scopes' rows from the local caches, before the refetch, so the
      // cached-configuration re-attach cannot bring them back as ghosts.
      if (scopeRemovedStepIds.length > 0) {
        await purgeScopeRemovalCaches({
          queryClient,
          requestId,
          removedStepIds: scopeRemovedStepIds,
          nextStoredStepIds:
            (editedData.enabledStepIds as string[] | undefined) ?? null,
          stage:
            (request.stage as string | undefined) ??
            (request.sysRequestStage as string | undefined),
        });
        void refetchAttachments();
      }

      // Refetch the request to ensure we have the latest data including team members
      // Do this BEFORE setting isEditing to false to ensure data is available
      if (isOnline && requestId) {
        await queryClient.refetchQueries({
          queryKey: offlineRequestsKeys.detail(requestId),
        });

        const configurationCachePatch: Record<string, unknown> = {};
        // All six repeatable sections (Racks, Small UPS, Large UPS, Cooling, Power
        // Distribution, Liquid Cooling) are on the engine bridge — their flush
        // already mirrors the normalized rows into the offline cache
        // (`mirror{Small,Large}UpsToOfflineRequestCache` /
        // `mirrorCoolingSystemToOfflineRequestCache` /
        // `mirrorPowerDistributionToOfflineRequestCache` /
        // `mirrorLiquidCoolingCduToOfflineRequestCache`), so no session-cache patch
        // here (#997 Story 6.8).
        if (Object.keys(configurationCachePatch).length > 0) {
          await offlineCache.updateCachedRequestRepeatableConfigurations(
            requestId,
            configurationCachePatch as Parameters<
              typeof offlineCache.updateCachedRequestRepeatableConfigurations
            >[1],
          );
          queryClient.setQueryData(
            offlineRequestsKeys.detail(requestId),
            (current: typeof request | undefined) =>
              current ? { ...current, ...configurationCachePatch } : current,
          );
        }

        // Also refetch attachments to ensure captions and other attachment data are up to date
        await refetchAttachments();

        // Additional small delay to ensure refetched data is propagated to components
        // This helps with slower connections where data might not be immediately available
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      setIsEditing(false);
      setEditedData({});
      setDirtyFields(new Set());
      setRackSessionDirty(false);
      setRestoredRackBaseline(null);
      setSmallUpsSessionDirty(false);
      setRestoredSmallUpsBaseline(null);
      setLargeUpsSessionDirty(false);
      setRestoredLargeUpsBaseline(null);
      setPowerDistributionSessionDirty(false);
      setRestoredPowerDistributionBaseline(null);
      setCoolingSystemSessionDirty(false);
      setRestoredCoolingSystemBaseline(null);
      setLiquidCoolingCduSessionDirty(false);
      setRestoredLiquidCoolingCduBaseline(null);
      setSelectedFiles({}); // Clear selected files after successful save
      if (user?.email && requestId) {
        void clearEditRequestDraftSnapshot(user.email, requestId).finally(
          () => {
            editDraftPersistSuspendedRef.current = false;
          },
        );
      } else {
        editDraftPersistSuspendedRef.current = false;
      }

      // Hide loading toast and show success
      showToast.dismiss(loadingToast);
      setUploadProgress(null); // Clear progress bar after everything is saved
      setIsManualSaving(false); // Re-enable all inputs
      if (!isOnline) {
        // Offline mode - changes saved locally
        reportSaveOutcome(
          deliverablesOutcome,
          'Changes saved offline. Will sync when back online.',
        );
      } else if (hasNewFiles) {
        reportSaveOutcome(
          deliverablesOutcome,
          'Request and files saved successfully!',
        );
      } else {
        reportSaveOutcome(deliverablesOutcome, 'Request saved successfully!');
      }
    } catch (error) {
      console.error('Failed to update request:', error);
      editDraftPersistSuspendedRef.current = false;
      showToast.dismiss(loadingToast);
      setUploadProgress(null); // Clear progress bar on error
      setIsManualSaving(false); // Re-enable all inputs
      const saveErrorMessage =
        error instanceof ApiError
          ? error.message
          : error instanceof Error
          ? error.message
          : '';
      showToast.error(
        saveErrorMessage.trim() || 'Failed to save changes. Please try again.',
      );
      // Reset the refetching flag on error
      setIsRefetchingAfterSave(false);
    } finally {
      offlineCache.endOfflineSaveGroup();
    }
  };

  const updateEditedData = useCallback(
    (field: string, value: string | string[] | number | boolean) => {
      setEditedData((prev: any) => ({
        ...prev,
        [field]: value,
      }));
      setDirtyFields(prev => new Set(prev).add(field));
      if (field === 'partnerName' && value !== '' && value != null) {
        setOpportunityValidationErrors(prev =>
          prev.some(error => error.field === 'partnerName')
            ? prev.filter(error => error.field !== 'partnerName')
            : prev,
        );
      }
    },
    [],
  );

  const handleAttachmentDeleted = useCallback(
    async (attachmentId: number) => {
      const fromEdited = requestForSteps
        ? removeRichHtmlAttachmentFromRequestData(
            requestForSteps as unknown as Record<string, unknown>,
            attachmentId,
          )
        : {};
      // The live editor resolves `sitescope-attachment://` to a data URI, so the
      // edited HTML often has no protocol URL left to strip. Fall back to the
      // last saved request, which still has the reference.
      const fromSaved = request
        ? removeRichHtmlAttachmentFromRequestData(
            request as unknown as Record<string, unknown>,
            attachmentId,
          )
        : {};
      const patch = { ...fromSaved, ...fromEdited };
      if (Object.keys(patch).length > 0) {
        applyEditedDataPatch(patch);
      }
      await refetchAttachments();
    },
    [request, requestForSteps, applyEditedDataPatch, refetchAttachments],
  );

  const handleDelete = () => {
    if (!request || !requestId) return;
    setShowDeleteConfirmation(true);
  };

  /**
   * #1149 Issue 9 — compact footer tools.
   *
   * On a phone the footer is one non-wrapping row with `overflow: hidden`
   * ancestors, so a fourth control pushed Submit off-screen and CLIPPED it.
   * Compact layouts collapse the intake tools, Close (on a Draft) and Archive
   * into one menu; the per-item permission gates are exactly the ones the
   * separate buttons use, so nobody gains or loses an action by changing
   * layout.
   *
   * Close joined the menu because, with it still in the row, a Draft at 375pt
   * needed ~328pt against ~287pt and the right-hand group slid over Edit.
   */
  const isCompactFooter = useCompactRequestFooter();
  const footerToolsDisabled =
    isManualSaving ||
    transcribeExistingMutation.isPending ||
    deleteRequestMutation.isPending;

  /**
   * `handleCloseDraft` is declared further down, after this memo, so the menu
   * item calls it through a ref that always holds the current handler rather
   * than capturing a binding that is not initialised yet.
   */
  const handleCloseDraftRef = useRef<() => void>(() => {});

  /**
   * The compact Close item: same gate and same disabled rules as the wide
   * footer's standalone button. `canShowDelete` is the flag
   * `PermissionAwareButton buttonType="delete"` reads, and it is already false
   * under read-only impersonation.
   */
  const showCloseDraftItem =
    (requestStage === RequestStage.DRAFT ||
      requestStage === RequestStage.AWAITING_APPROVAL) &&
    buttonPermissions.canShowDelete;
  const closeDraftItemDisabled =
    closeDraftMutation.isPending ||
    stageUpdateMutation.isPending ||
    !isOnline ||
    isManualSaving ||
    recordingState !== 'idle';

  const archiveItemDisabled =
    deleteRequestMutation.isPending ||
    isManualSaving ||
    recordingState !== 'idle';

  const footerToolItems = useMemo(() => {
    const items: RequestToolsMenuItem[] = [];
    if (viewerHasApprovalStampOnlyAccess) {
      return items;
    }
    const intakeBusy = recordingState !== 'idle' || !isOnline;
    if (buttonPermissions.canShowRecording) {
      items.push({
        key: 'record',
        label: 'Record audio',
        hint: isOnline
          ? 'Use audio to update or create the request'
          : 'Needs a connection',
        icon: <Mic size={20} color={colors.textPrimary} />,
        onPress: () => {
          if (!intakeBusy) {
            handleRecordingButtonPress();
          }
        },
        testID: 'detail-record-audio-button',
      });
    }
    if (buttonPermissions.canShowTextIntake) {
      items.push({
        key: 'paste-text',
        label: 'Paste text',
        hint: isOnline
          ? 'Paste text from emails or notes to update or create the request'
          : 'Needs a connection',
        icon: <TextIcon size={20} color={colors.textPrimary} />,
        onPress: () => {
          if (!intakeBusy) {
            handlePasteTextButtonPress();
          }
        },
        testID: 'detail-paste-text-button',
      });
    }
    if (showCloseDraftItem) {
      items.push({
        key: 'close-request',
        label: 'Close request',
        hint: 'Request is still available in your request list',
        icon: <XCircle size={20} color={colors.textPrimary} />,
        onPress: () => handleCloseDraftRef.current(),
        group: 'actions',
        disabled: closeDraftItemDisabled,
        testID: 'detail-close-draft-button',
      });
    }
    if (buttonPermissions.canShowDelete) {
      // No hint, per the approved design: the confirmation dialog it opens
      // already says what Archive does. Disabled on the same conditions as the
      // wide footer's Archive button.
      items.push({
        key: 'archive',
        label: 'Archive request',
        icon: <Archive size={20} color={colors.error} />,
        onPress: handleDelete,
        destructive: true,
        disabled: archiveItemDisabled,
        testID: 'detail-delete-button',
      });
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    buttonPermissions.canShowRecording,
    buttonPermissions.canShowTextIntake,
    buttonPermissions.canShowDelete,
    showCloseDraftItem,
    closeDraftItemDisabled,
    archiveItemDisabled,
    recordingState,
    isOnline,
    viewerHasApprovalStampOnlyAccess,
  ]);

  const handleConfirmDelete = async () => {
    if (!request || !requestId) return;

    // Show loading toast
    const loadingToast = showToast.loading('Archiving request...');

    try {
      await deleteRequestMutation.mutateAsync(requestId);

      // Hide loading toast and show success
      showToast.dismiss(loadingToast);
      showToast.success('Request archived successfully!');

      // Navigate after a short delay to let user see the success message
      setTimeout(() => {
        navigate('/request');
      }, 1000);
    } catch (error) {
      console.error('Failed to archive request:', error);
      showToast.error('Failed to archive request. Please try again.');
    }
  };

  const handleCloseDraft = () => {
    if (!request || !requestId) return;
    setShowCloseDraftConfirmation(true);
  };
  handleCloseDraftRef.current = handleCloseDraft;

  const handleReject = () => {
    if (!request || !requestId) return;
    if (showStageChangeConfirmation || stageUpdateMutation.isPending) {
      return;
    }
    setRejectReason('');
    setRejectReasonError(null);
    setShowRejectConfirmation(true);
  };

  const handleConfirmReject = async () => {
    if (!requestId) return;
    if (showStageChangeConfirmation || stageUpdateMutation.isPending) {
      return;
    }

    const trimmedReason = rejectReason.trim();
    if (!trimmedReason) {
      setRejectReasonError('Rejection reason is required.');
      throw new Error('Rejection reason is required.');
    }

    const loadingToast = showToast.loading('Rejecting request...');
    try {
      await rejectRequestMutation.mutateAsync({
        requestId,
        reason: trimmedReason,
      });
      showToast.dismiss(loadingToast);
      showToast.success('Request rejected.');
      setRejectReason('');
      setRejectReasonError(null);
      setShowRejectConfirmation(false);
      // Peer approvers lose Draft detail access after reject; return to the list.
      navigate('/request');
    } catch (error) {
      showToast.dismiss(loadingToast);
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to reject request. Please try again.';
      showToast.error(message);
      throw error;
    }
  };

  const handleConfirmCloseDraft = async () => {
    if (!request || !requestId) return;

    setShowCloseDraftConfirmation(false);
    const loadingToast = showToast.loading('Closing request...');

    try {
      await closeDraftMutation.mutateAsync(requestId);

      showToast.dismiss(loadingToast);
      showToast.success('Request closed.');

      // Same landing as the archive handler: back to the list, where the request is now
      // findable under the Closed filter rather than gone.
      setTimeout(() => {
        navigate('/request');
      }, 1000);
    } catch (error) {
      showToast.dismiss(loadingToast);
      // Fixed copy, matching handleConfirmDelete above. Server text such as
      // "User does not have delete permission for DRAFT stage" or "HTTP 500" is a
      // diagnostic, not user-facing copy, so it stays in the console only.
      console.error('Failed to close request:', error);
      showToast.error('Failed to close request. Please try again.');
    }
  };

  // sidebarWidth is now handled by App.tsx layout

  // Check if user is a partner contributor who needs access level preloaded
  const isPartnerContributorUser = user?.roles?.includes(
    AppRole.PARTNER_CONTRIBUTOR,
  );
  const needsAccessLevelPreload = isPartnerContributorUser && requestId;
  const isFullyLoaded =
    !isLoading && (!needsAccessLevelPreload || accessLevelPreloaded);

  useEffect(() => {
    if (
      requestSaveTransition.activeRequestId !== requestId ||
      !isFullyLoaded ||
      !request ||
      isFetching
    ) {
      return undefined;
    }

    // Keep the cross-screen save overlay through the destination's populated
    // paint. Wait for fetch to settle, then defer until after layout so the
    // modal does not lift while dashes/stale errors are still on screen.
    let cancelled = false;
    let firstFrame: number | null = null;
    let secondFrame: number | null = null;
    const interaction = InteractionManager.runAfterInteractions(() => {
      if (cancelled) {
        return;
      }
      firstFrame = requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }
        secondFrame = requestAnimationFrame(() => {
          if (!cancelled) {
            requestSaveTransition.complete(requestId);
          }
        });
      });
    });

    return () => {
      cancelled = true;
      interaction.cancel();
      if (firstFrame != null) {
        cancelAnimationFrame(firstFrame);
      }
      if (secondFrame != null) {
        cancelAnimationFrame(secondFrame);
      }
    };
  }, [isFullyLoaded, isFetching, request, requestId, requestSaveTransition]);

  // Check for authorization errors first
  const isAccessDenied =
    error && (error as any)?.message?.includes('Access denied');
  // Use original requestor email for authorization check (in case it was edited)
  // Ensure team member arrays are never undefined to prevent false access denied during refetch
  const requestForAuth =
    request && originalRequestorEmail
      ? {
          ...request,
          requestorEmail: originalRequestorEmail,
          vertivSponsors: request.vertivSponsors ?? [],
          vertivContributors: request.vertivContributors ?? [],
          partnerContributors: request.partnerContributors ?? [],
        }
      : request
      ? {
          ...request,
          vertivSponsors: request.vertivSponsors ?? [],
          vertivContributors: request.vertivContributors ?? [],
          partnerContributors: request.partnerContributors ?? [],
        }
      : request;

  const awaitingPartnerAccessPending =
    isPartnerViewer &&
    approvalGatePending &&
    (requestForAuth?.sysRequestStage === RequestStage.AWAITING_APPROVAL ||
      requestForAuth?.stage === RequestStage.AWAITING_APPROVAL);

  const hasNoAccess =
    requestForAuth &&
    !awaitingPartnerAccessPending &&
    !requestAuthService.canAccessRequest(user, requestForAuth as any, {
      approvalGroup,
    });

  const isDraftStage = requestStage === RequestStage.DRAFT;
  const showRejectionReasonNotice =
    isDraftStage && Boolean(request?.latestRejectionReason);
  const requestToolbarFormDataRef = useRef<Record<string, unknown>>({});
  requestToolbarFormDataRef.current = request
    ? ({ ...(request as Record<string, unknown>), ...editedData } as Record<
        string,
        unknown
      >)
    : {};
  const isBrowsingByLocation = currentStep === 'location-view';

  // Remember the step the user came from so leaving Devices lands there.
  const preLocationViewStepRef = useRef<string>('opportunity');
  if (!isBrowsingByLocation && currentStep !== 'impersonation-audit') {
    preLocationViewStepRef.current = currentStep;
  }

  const handleToggleBrowseByLocation = useCallback(() => {
    if (currentStep === 'location-view') {
      const previous = preLocationViewStepRef.current;
      const target = detailStepIds.includes(previous)
        ? previous
        : detailStepIds[0] ?? 'opportunity';
      onStepChange?.(target);
      return;
    }
    onStepChange?.('location-view');
  }, [currentStep, detailStepIds, onStepChange]);

  /**
   * Devices view row tap (#999 Story 3.5 / 3.7) — open that unit's editor on
   * its step, then return to Devices when the editor closes.
   */
  const handleUnitPress = useCallback(
    (stepId: DeviceStepId, unit: DeviceSourceUnit) => {
      if (recordingState !== 'idle') {
        return;
      }
      const unitId = coerceDevicesUnitId(unit.id);
      if (unitId == null) {
        return;
      }
      devicesEditorNavigation.setPendingOpen(stepId, unitId);
      onStepChange?.(stepId);
    },
    [onStepChange, recordingState],
  );

  /** Edit-session overlays so Devices mirrors uncommitted order (Story 3.7). */
  const devicesUnitOverrides = useMemo(
    () =>
      isEditing
        ? {
            racks: editedData.rackConfigurations as
              | RackConfiguration[]
              | undefined,
            smallUps: editedData.smallUpsConfigurations as
              | SmallUpsConfiguration[]
              | undefined,
            largeUps: editedData.largeUpsConfigurations as
              | LargeUpsConfiguration[]
              | undefined,
            powerDistribution: editedData.powerDistributionConfigurations as
              | PowerDistributionConfiguration[]
              | undefined,
            coolingSystems: editedData.coolingSystemConfigurations as
              | CoolingSystemConfiguration[]
              | undefined,
            liquidCoolingCdus: editedData.liquidCoolingCduConfigurations as
              | LiquidCoolingCduConfiguration[]
              | undefined,
          }
        : undefined,
    [
      isEditing,
      editedData.rackConfigurations,
      editedData.smallUpsConfigurations,
      editedData.largeUpsConfigurations,
      editedData.powerDistributionConfigurations,
      editedData.coolingSystemConfigurations,
      editedData.liquidCoolingCduConfigurations,
    ],
  );

  /**
   * Devices reorder (#999 Story 3.7 / 3.9) — shared displayOrder via edit
   * sessions; writers keyed by device step (no ×6 switch).
   */
  const devicesReorderSessions = useMemo((): DevicesReorderSessionMap => {
    const persisted = (requestForSteps ?? request ?? {}) as Record<
      string,
      unknown
    >;
    const bind = (
      key:
        | 'rackConfigurations'
        | 'smallUpsConfigurations'
        | 'largeUpsConfigurations'
        | 'powerDistributionConfigurations'
        | 'coolingSystemConfigurations'
        | 'liquidCoolingCduConfigurations',
    ) => ({
      getSessionUnits: () =>
        (editedData[key] ?? persisted[key]) as DeviceSourceUnit[] | undefined,
      setSessionUnits: (units: OrderableUnit[]) =>
        applyEditedDataPatch({ [key]: units }),
    });
    return {
      'rack-power': bind('rackConfigurations'),
      'small-ups': bind('smallUpsConfigurations'),
      'large-ups': bind('largeUpsConfigurations'),
      'power-distribution': bind('powerDistributionConfigurations'),
      'cooling-readiness': bind('coolingSystemConfigurations'),
      'liquid-cooling': bind('liquidCoolingCduConfigurations'),
    };
  }, [editedData, requestForSteps, request, applyEditedDataPatch]);

  const handleDevicesReorder = useCallback(
    (
      stepId: DeviceStepId,
      visibleUnits: readonly DeviceSourceUnit[],
      fromIndex: number,
      toIndex: number,
    ) => {
      if (!isEditing) {
        return;
      }
      const applied = applyDevicesStepReorder(
        devicesReorderSessions,
        stepId,
        visibleUnits,
        fromIndex,
        toIndex,
      );
      if (!applied) {
        showToast.error(
          'Edit session is still loading. Try again in a moment.',
        );
      }
    },
    [isEditing, devicesReorderSessions],
  );

  useEffect(() => {
    devicesEditorNavigation.resetSession();
  }, [requestId]);

  // Abandon return-to-Devices when leaving an equipment step for another step
  // that is not Devices (sidebar jump with editor still "open" path).
  const devicesNavPreviousStepRef = useRef(currentStep);
  useEffect(() => {
    const previous = devicesNavPreviousStepRef.current;
    devicesNavPreviousStepRef.current = currentStep;
    // Pending targets are valid only for the immediate Devices -> equipment
    // transition. Re-entering Devices without consumption means the target is
    // stale; discard it before a later cache/filter change can surprise-open it.
    if (previous !== 'location-view' && currentStep === 'location-view') {
      if (devicesEditorNavigation.hasPendingOpen()) {
        showToast.info('That unit is no longer available.');
      }
      devicesEditorNavigation.abandonReturnToDevices();
      return;
    }
    if (
      previous !== currentStep &&
      previous !== 'location-view' &&
      currentStep !== 'location-view'
    ) {
      devicesEditorNavigation.abandonReturnToDevices();
    }
  }, [currentStep]);

  // Keep the return handler pointed at the latest onStepChange without making
  // the register/reset effect depend on its identity. onStepChange changes
  // identity every time currentFormStep changes (App.tsx useCallback dep), so a
  // [onStepChange] dependency here would fire this effect's cleanup —
  // resetSession() — mid-navigation and wipe the pending open that a Devices row
  // tap just set, leaving the user on the step list instead of the unit editor.
  const devicesReturnStepChangeRef = useRef(onStepChange);
  useEffect(() => {
    devicesReturnStepChangeRef.current = onStepChange;
  }, [onStepChange]);

  useEffect(() => {
    devicesEditorNavigation.registerReturnHandler(() => {
      devicesReturnStepChangeRef.current?.('location-view');
    });
    return () => {
      devicesEditorNavigation.registerReturnHandler(null);
      devicesEditorNavigation.resetSession();
    };
  }, []);

  const requestToolbarProps = useMemo(
    () =>
      isFullyLoaded && request && !error && !hasNoAccess
        ? {
            title: getRequestDetailToolbarTitle(request, requestId),
            // #1531: the number the title drops as soon as the request has a
            // project or customer name — which is every request that got past
            // the first step.
            requestNumber: getRequestDetailToolbarRequestNumber(
              request,
              requestId,
            ),
            stage: requestStage,
            rawStage: rawRequestStage ?? null,
            formDataRef: requestToolbarFormDataRef,
            isStageUpdating: stageUpdateMutation.isPending,
            showInternalDetail,
            disabled: recordingState !== 'idle',
            titleAccessory: (
              <RequestFailedIndicator
                requestId={request.id || requestId || ''}
                stage={requestStage}
                size="small"
                enabled={isDraftStage}
              />
            ),
            actionLeading:
              requestId && !requestId.startsWith('o-') ? (
                <LocationTagsControl
                  requestId={requestId}
                  requestStage={requestStage}
                  requestorEmail={request?.requestorEmail}
                  disabled={recordingState !== 'idle'}
                  variant="toolbar"
                />
              ) : null,
            // A fragment, not a row View: on compact the toolbar wraps the
            // group between these buttons (RequestToolbar.actionTrailing).
            actionTrailing:
              requestId && !requestId.startsWith('o-') ? (
                <>
                  <ToolbarActionButton
                    label="Revision History"
                    icon={RotateCcw}
                    onPress={() =>
                      navigate(`/request-versions?id=${requestId}`)
                    }
                    disabled={recordingState !== 'idle'}
                    testID="version-history-button"
                    accessibilityLabel="Revision History"
                    accessibilityHint="View the change history for this request"
                  />
                  <DevicesViewToolbarButton
                    onPress={handleToggleBrowseByLocation}
                    selected={isBrowsingByLocation}
                    disabled={recordingState !== 'idle'}
                    variant="toolbar"
                  />
                  {/* Discussion (#1069 Story 1.2, C2; Story 5.3, 2026-09-08):
                      a toolbar action on every viewport; nothing floats over
                      content. Always rendered — it shows its pressed state
                      while the panel is open, docked or not. It used to hide
                      itself while docked and open; the owner reversed that. */}
                  <DiscussionToggle
                    open={discussionOpen}
                    hasUnread={correspondenceHasUnread}
                    disabled={recordingState !== 'idle'}
                    onToggle={() => setDiscussionOpen(v => !v)}
                  />
                </>
              ) : null,
          }
        : null,
    [
      error,
      hasNoAccess,
      isDraftStage,
      isFullyLoaded,
      navigate,
      onStepChange,
      handleToggleBrowseByLocation,
      isBrowsingByLocation,
      recordingState,
      request,
      requestId,
      requestStage,
      rawRequestStage,
      showInternalDetail,
      stageUpdateMutation.isPending,
      discussionOpen,
      // `isDiscussionDocked` left this list with the `docked` prop it fed
      // (#1069 Story 5.3). The value is still computed and still used by the
      // dock itself — it just no longer reaches the toolbar toggle, which now
      // renders in every layout.
      correspondenceHasUnread,
    ],
  );
  useRequestFrameToolbar(requestToolbarProps);

  // Loading state - wait for both request data AND access level (for partner contributors)
  if (!isFullyLoaded) {
    return (
      <View style={[styles.container]}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007bff" />
          <Text style={styles.loadingText}>
            {!accessLevelPreloaded && needsAccessLevelPreload
              ? 'Loading permissions...'
              : 'Loading request details...'}
          </Text>
        </View>
      </View>
    );
  }

  // Don't show error screen if we're currently saving/updating the request
  // This prevents the flash of "Access Denied" during the refetch after save
  const isSaving =
    updateRequestMutation.isPending || stageUpdateMutation.isPending;

  // Error state - distinguish between access denied and not found
  // IMPORTANT: Also check !isFetching and !isLoading to prevent showing error during initial load and refetch operations
  if (
    (error || !request || hasNoAccess) &&
    !isSaving &&
    !isRefetchingAfterSave &&
    !isFetching &&
    !isLoading
  ) {
    let errorTitle = 'Request Not Found';
    let errorMessage =
      'The requested item could not be found or there was an error loading it.';
    let errorColor = '#dc3545';

    if (isAccessDenied || hasNoAccess) {
      errorTitle = 'Access Denied';
      errorMessage = requestAuthService.getAccessDeniedMessage(
        user,
        request as any,
      );
      errorColor = '#fd7e14'; // Orange color for access denied
    }

    return (
      <View style={[styles.container]}>
        <View style={styles.errorContainer}>
          <AlertCircle size={48} color={errorColor} />
          <Text style={styles.errorTitle}>{errorTitle}</Text>
          <Text style={styles.errorMessage}>{errorMessage}</Text>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigate('/request')}
          >
            <Text style={styles.backButtonText}>Back to Requests</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // Derive the "show full details" state based on stage and toggle
  // For DRAFT stage, always show full details (no toggle available)
  const showFullDetails = !showViewToggle || isAdvancedView;

  /**
   * The three pieces of the page body, each built ONCE and placed by the
   * platform layout below (spec "Docked discussion panel: page scrollbar at
   * the window edge", 2026-09-07 — that spec's "open by default" half was
   * reversed by Story 5.3 on 2026-09-08; the layout described here was kept).
   * Two layouts, one form.
   */
  const formBody = (
    <View style={styles.formContainer}>
      {showRejectionReasonNotice ? (
        <View style={styles.rejectionBanner} testID="rejection-reason-banner">
          <Text style={styles.rejectionBannerTitle}>
            {request.latestRejectionRejectedByEmail
              ? `This request was rejected by: ${request.latestRejectionRejectedByEmail}`
              : 'This request was rejected.'}
          </Text>
          <Text style={styles.rejectionBannerText}>
            {collapseLineBreaks(request.latestRejectionReason ?? '')}
          </Text>
        </View>
      ) : null}
      {currentStep === 'location-view' ? (
        <DevicesView
          requestId={requestId ?? ''}
          enabledStepIds={resolvedEnabledStepIds}
          unitOverrides={devicesUnitOverrides}
          onUnitPress={handleUnitPress}
          onReorderUnits={isEditing ? handleDevicesReorder : undefined}
          canReorderStep={
            isEditing
              ? stepId =>
                  Boolean(devicesReorderSessions[stepId]?.getSessionUnits())
              : undefined
          }
        />
      ) : currentStep === 'impersonation-audit' ? (
        <ImpersonationAuditStep
          requestId={requestId ? parseInt(requestId, 10) : 0}
          isAdmin={isITAdministrator}
        />
      ) : stepId === 'deliverables' && !canSeeDeliverables ? (
        // Hidden outright, not rendered read-only. The DRAFT matrix marks
        // Deliverables `-` for eleven of fourteen roles, and `-` means absent rather
        // than uneditable. The sidebar item is gated on the same answer, so this arm
        // is normally unreachable; it stands because a step can also be reached by
        // URL. (MR !489 review, batch 2 change 2.3.)
        <></>
      ) : stepId === 'deliverables' ? (
        // Rendered here rather than through the step registry, which
        // `buildRequestFormSteps` shares with Create Request. Read-only unless the
        // user has pressed Edit, exactly like every other field, and always read-only
        // on CLOSED (FR18). A prop rather than `useRequestStage`: this arm is outside
        // any `RequestStageProvider`, and the stage is already in scope here.
        <RequestDeliverablesStep
          deliverables={deliverablesSession.deliverables}
          onAdd={deliverablesSession.addDeliverableEntries}
          onEdit={deliverablesSession.editDeliverableComment}
          onRemove={deliverablesSession.removeDeliverableType}
          readOnly={
            requestStage === RequestStage.CLOSED ||
            !isEditing ||
            !canEditDeliverablesAtStage ||
            // No trustworthy starting list means no editing, whatever the
            // permissions say. A save sends the whole list and the server replaces
            // what it holds, so an edit from a base the device cannot vouch for
            // deletes every row it never saw. (MR !489 review, finding 01.)
            !deliverablesSession.isBaseTrusted
          }
          readOnlyReason={
            requestStage === RequestStage.CLOSED
              ? 'closed'
              : // Ahead of the permission reasons: when the list itself could not be
              // established, telling someone they lack permission (or to press Edit)
              // explains the wrong thing.
              !deliverablesSession.isBaseTrusted
              ? 'untrusted-base'
              : // Both conditions, and the reason has changed. This used to read
              // `canEditDeliverables`, which is stage-blind on this screen: the
              // first `useFormPermissions` call runs before `request` resolves, so
              // it answered from the cross-stage role union and returned true at
              // DRAFT for roles whose Deliverables cell is `-`. `canShowEdit` was
              // carrying the stage on its own to stop those roles being told to
              // "Press Edit" when no Edit button exists.
              //
              // `canEditDeliverablesAtStage` now asks WITH the stage, so it is
              // honest by itself. `canShowEdit` stays because the two answer
              // different questions: one is the field's own cell, the other is
              // whether this person can enter edit mode here at all, and the
              // invitation is a lie unless both hold.
              // (MR !489 review, batch 2 change 2.4.)
              !buttonPermissions.canShowEdit || !canEditDeliverablesAtStage
              ? 'no-permission'
              : 'view'
          }
          isLoading={deliverablesSession.isLoading}
          error={deliverablesSession.error}
          notice={deliverablesSession.notice}
        />
      ) : (
        (() => {
          const StepComponent = getCurrentStepComponent();
          if (StepComponent) {
            if (stepId === 'summary') {
              return (
                <RequestStageProvider
                  requestStage={requestStage || undefined}
                  requestId={requestId}
                >
                  <StepFirstSubsectionExpandProvider key={stepId}>
                    <Summary
                      request={requestForSteps ?? request}
                      editedData={editedData}
                      isEditing={isEditing}
                      attachmentsBySection={attachmentsBySection}
                      requestId={
                        requestId ? parseInt(requestId, 10) : undefined
                      }
                      isAdmin={isITAdministrator}
                      dirtyFields={dirtyFields}
                    />
                  </StepFirstSubsectionExpandProvider>
                </RequestStageProvider>
              );
            }

            return (
              <RequestStageProvider
                requestStage={requestStage || undefined}
                requestId={requestId}
              >
                <StepFirstSubsectionExpandProvider key={stepId}>
                  <StepComponent
                    request={(requestForSteps ?? request)!}
                    isEditing={isEditing && !isManualSaving}
                    editedData={editedData}
                    updateEditedData={updateEditedData}
                    attachmentsBySection={attachmentsBySection}
                    selectedFiles={selectedFiles}
                    onFilesSelected={handleFilesSelected}
                    onRefetchAttachments={refetchAttachments}
                    onDeleteAttachment={handleAttachmentDeleted}
                    showFullDetails={showFullDetails}
                    showViewToggle={showViewToggle}
                    isAdvancedView={isAdvancedView}
                    onToggleView={handleToggleView}
                    disabled={isManualSaving}
                    offlineMode={shouldDisableUploads}
                    onCaptionChange={handleCaptionChange}
                    pendingCaptionEdits={pendingCaptionEdits}
                    enabledStepIds={resolvedEnabledStepIds}
                    {...(stepId === 'opportunity'
                      ? {
                          validationErrors: opportunityValidationErrors,
                          ...(opportunityRhfValidationRevision > 0
                            ? {
                                rhfValidationRevision:
                                  opportunityRhfValidationRevision,
                              }
                            : {}),
                        }
                      : {})}
                    {...(stepId === 'rack-power'
                      ? {
                          onRackEditorActiveChange:
                            handleRackEditorActiveChange,
                          registerInstanceSaveHandle:
                            registerRackInstanceSaveHandle,
                          applyEditedDataPatch,
                          rackConfigurationsDirty:
                            dirtyFields.has('rackConfigurations'),
                        }
                      : {})}
                    {...(stepId === 'facility-power'
                      ? {
                          registerInstanceSaveHandle,
                          onInstanceEditorActiveChange:
                            handleInstanceEditorActiveChange,
                          applyEditedDataPatch,
                        }
                      : {})}
                    {...(stepId === 'small-ups'
                      ? {
                          onSmallUpsEditorActiveChange:
                            handleSmallUpsEditorActiveChange,
                          registerInstanceSaveHandle:
                            registerSmallUpsInstanceSaveHandle,
                          applyEditedDataPatch,
                          smallUpsConfigurationsDirty: dirtyFields.has(
                            'smallUpsConfigurations',
                          ),
                        }
                      : {})}
                    {...(stepId === 'large-ups'
                      ? {
                          onLargeUpsEditorActiveChange:
                            handleLargeUpsEditorActiveChange,
                          registerInstanceSaveHandle:
                            registerLargeUpsInstanceSaveHandle,
                          applyEditedDataPatch,
                          largeUpsConfigurationsDirty: dirtyFields.has(
                            'largeUpsConfigurations',
                          ),
                        }
                      : {})}
                    {...(stepId === 'power-distribution'
                      ? {
                          onPowerDistributionEditorActiveChange:
                            handlePowerDistributionEditorActiveChange,
                          registerInstanceSaveHandle:
                            registerPowerDistributionInstanceSaveHandle,
                          applyEditedDataPatch,
                          powerDistributionConfigurationsDirty: dirtyFields.has(
                            'powerDistributionConfigurations',
                          ),
                        }
                      : {})}
                    {...(stepId === 'cooling-readiness'
                      ? {
                          onCoolingSystemEditorActiveChange:
                            handleCoolingSystemEditorActiveChange,
                          registerInstanceSaveHandle:
                            registerCoolingSystemInstanceSaveHandle,
                          applyEditedDataPatch,
                          coolingSystemConfigurationsDirty: dirtyFields.has(
                            'coolingSystemConfigurations',
                          ),
                        }
                      : {})}
                    {...(stepId === 'liquid-cooling'
                      ? {
                          onLiquidCoolingCduEditorActiveChange:
                            handleLiquidCoolingCduEditorActiveChange,
                          registerInstanceSaveHandle:
                            registerLiquidCoolingCduInstanceSaveHandle,
                          applyEditedDataPatch,
                          liquidCoolingCduConfigurationsDirty: dirtyFields.has(
                            'liquidCoolingCduConfigurations',
                          ),
                        }
                      : {})}
                  />
                </StepFirstSubsectionExpandProvider>
              </RequestStageProvider>
            );
          }
          return (
            <View style={styles.placeholderContainer}>
              <Text style={styles.placeholderTitle}>
                {visibleDetailSteps.find(s => s.id === stepId)?.title || 'Step'}
              </Text>
              <Text style={styles.placeholderText}>
                This step component is not yet implemented
              </Text>
            </View>
          );
        })()
      )}
    </View>
  );

  const footerBand = (
    <>
      {/* Upload Progress Bar */}
      {uploadProgress && (
        <UploadProgressBar
          progress={uploadProgress.progress}
          fileName={uploadProgress.fileName}
        />
      )}

      {/* Audio Recording Bar - appears above footer during recording */}
      <AudioRecordingBar
        recordingState={recordingState}
        elapsedTime={elapsedTime}
        visible={recordingState !== 'idle'}
        onPause={handlePauseRecording}
        onResume={handleResumeRecording}
        onDiscard={handleDiscardRecording}
        onAccept={handleAcceptRecordingWithUpload}
      />

      {/* Sticky Footer — in-flow on native; safe inset is part of footer band */}
      <View
        style={[
          styles.footer,
          nativeFooterPaddingBottom != null && {
            paddingBottom: nativeFooterPaddingBottom,
          },
        ]}
        testID="request-detail-footer"
      >
        {viewerCanStampAwaitingApproval &&
        viewerIsRequestor &&
        !approvalStampBlockedByReadOnlyImpersonation ? (
          <Text testID="self-approve-helper" style={styles.approvalFooterNote}>
            You are in the Approvals Group. After you submit, Approve to send to
            Vertiv.
          </Text>
        ) : null}
        <View
          testID="request-detail-footer-content"
          style={[
            styles.footerContent,
            {
              justifyContent: isSmallScreen ? 'space-between' : 'space-between',
            },
          ]}
        >
          {/* Left buttons for all screens - Navigation and Action buttons */}
          <View style={styles.leftButtonsContainer}>
            {/* Action Buttons (Recording/Edit/Delete) */}
            <View style={styles.actionButtonsGroup}>
              {/* #1149 Issue 9 — compact: the intake tools and Archive
                      collapse behind one control so Submit cannot be clipped
                      off the end of this non-wrapping row. Wide web keeps the
                      separate buttons; it has the room. */}
              {isCompactFooter && !isEditing && (
                <RequestToolsMenu
                  items={footerToolItems}
                  size={40}
                  disabled={footerToolsDisabled}
                  testID="detail-tools-menu"
                />
              )}
              {/* Recording button - controlled by config permissions. While the
                    grants are still loading, a dimmed fixed-size stand-in holds the
                    slot so the footer does not reflow when the icon pops in (#1469). */}
              {!isCompactFooter &&
                !isEditing &&
                (buttonPermissions.isLoading ? (
                  <View
                    testID="detail-record-audio-loading"
                    style={[
                      styles.footerAudioPlaceholder,
                      styles.iconButtonDisabled,
                    ]}
                  />
                ) : (
                  buttonPermissions.canShowRecording && (
                    <AudioRecordingButton
                      testID="detail-record-audio-button"
                      onPress={handleRecordingButtonPress}
                      disabled={
                        recordingState !== 'idle' ||
                        isManualSaving ||
                        transcribeExistingMutation.isPending ||
                        !isOnline
                      }
                      size={spacing.formControlHeight}
                    />
                  )
                ))}
              {/* Paste text button - controlled by config permissions */}
              {!isCompactFooter &&
                !isEditing &&
                buttonPermissions.canShowTextIntake && (
                  <PasteTextButton
                    testID="detail-paste-text-button"
                    onPress={handlePasteTextButtonPress}
                    disabled={
                      recordingState !== 'idle' ||
                      isManualSaving ||
                      transcribeExistingMutation.isPending ||
                      !isOnline
                    }
                    size={40}
                  />
                )}
              {/* Archive button - controlled by config permissions at EVERY stage,
              including DRAFT. `PermissionAwareButton` does all the role work, as it
              does for every other control here: the DRAFT `Button - Delete` cell is
              `W` for Requestor, External Requestor and IT Administrator and `-` for
              the other eleven roles, and at Site Assessment, Configuration and
              Closed it is `W` for IT Administrator only.

              Not exhaustive: `fieldPermissions.PROCESSING.json` has `Button - Delete`
              as `-` for every role, IT Administrator included, so nobody archives
              there. It is listed for completeness only — `loadStagePermissions` and
              the middleware's `normalizeStage` both map PROCESSING onto DRAFT, so no
              live request is governed by that matrix today.

              A blanket `requestStage !== DRAFT` guard used to sit here, on the
              reasoning that the Close control below replaces Archive on a Draft.
              Close is an ADDITIONAL exit, not a replacement — it leaves the request
              in the list, which is the point of it — so the guard removed the only
              way to get rid of an erroneous Draft, and removed it from the IT
              Administrator too, in direct contradiction of the comment that used to
              stand here. (MR !489 review, batch 3, change 3.4.)

              Both exits coexist because the Close control is gated on this same
              `Button - Delete` cell, so no permission-matrix change was needed. */}
              {!isCompactFooter &&
                !isEditing &&
                !viewerHasApprovalStampOnlyAccess && (
                  <PermissionAwareButton
                    buttonType="delete"
                    requestStage={requestStage || undefined}
                    requestId={requestId || undefined}
                    loadingComponent={
                      <View
                        testID="detail-delete-button-loading"
                        style={[styles.iconButton, styles.iconButtonDisabled]}
                      />
                    }
                  >
                    <TouchableOpacity
                      testID="detail-delete-button"
                      style={[
                        styles.iconButton,
                        (deleteRequestMutation.isPending ||
                          isManualSaving ||
                          recordingState !== 'idle') &&
                          styles.iconButtonDisabled,
                      ]}
                      onPress={handleDelete}
                      disabled={
                        deleteRequestMutation.isPending ||
                        isManualSaving ||
                        recordingState !== 'idle'
                      }
                    >
                      {deleteRequestMutation.isPending ? (
                        <ActivityIndicator size="small" color="#666" />
                      ) : (
                        <Archive size={18} color="#666" />
                      )}
                    </TouchableOpacity>
                  </PermissionAwareButton>
                )}

              {/* Edit button - controlled by config permissions.
                  Peer approvers stamp only; they do not get Edit. */}
              {!isEditing && !viewerHasApprovalStampOnlyAccess && (
                <PermissionAwareButton
                  buttonType="edit"
                  requestStage={requestStage || undefined}
                  requestId={requestId || undefined}
                  loadingComponent={
                    <View
                      testID="detail-edit-button-loading"
                      style={[
                        styles.editButtonWithText,
                        styles.iconButtonDisabled,
                      ]}
                    />
                  }
                >
                  <TouchableOpacity
                    testID="detail-edit-button"
                    style={[
                      styles.editButtonWithText,
                      (isManualSaving || recordingState !== 'idle') &&
                        styles.iconButtonDisabled,
                    ]}
                    onPress={handleEdit}
                    disabled={isManualSaving || recordingState !== 'idle'}
                  >
                    <Edit size={18} color="#fff" />
                    <Text style={styles.editButtonText}>Edit</Text>
                  </TouchableOpacity>
                </PermissionAwareButton>
              )}
            </View>
          </View>

          {/* Right-hand action group: on wide layouts the Draft Close sits next to Submit, which is
          where a requestor looks for "I am finished with this request". Rendered
          only outside edit mode, exactly as the submit button already was, so the
          footer keeps its two-child space-between layout while editing. */}
          {!isEditing && request && (
            <View style={styles.rightButtonsContainer}>
              {/* Close a Draft without archiving it. Gated by the SAME permission cell as
              Archive (`Button - Delete`) plus a DRAFT-only stage guard in JSX, so no
              permission-matrix change was needed. Wide layouts only: on the compact
              footer this button made the row overrun and slide over Edit, so there it
              is the `close-request` item in the tools menu, with the same gate. */}
              {!isCompactFooter && requestStage === RequestStage.DRAFT && (
                <PermissionAwareButton
                  buttonType="delete"
                  requestStage={requestStage || undefined}
                  requestId={requestId || undefined}
                  loadingComponent={
                    <View
                      testID="detail-close-draft-button-loading"
                      style={[
                        isSmallScreen
                          ? styles.closeDraftCompactButton
                          : styles.closeDraftTextButton,
                        styles.closeDraftButtonDisabled,
                      ]}
                    />
                  }
                >
                  <TouchableOpacity
                    testID="detail-close-draft-button"
                    style={[
                      isSmallScreen
                        ? styles.closeDraftCompactButton
                        : styles.closeDraftTextButton,
                      // `!isOnline` belongs in the style condition as well as the
                      // `disabled` prop below: without it the control looks fully
                      // enabled offline while doing nothing when pressed.
                      (closeDraftMutation.isPending ||
                        stageUpdateMutation.isPending ||
                        !isOnline ||
                        isManualSaving ||
                        recordingState !== 'idle') &&
                        styles.closeDraftButtonDisabled,
                    ]}
                    onPress={handleCloseDraft}
                    disabled={
                      closeDraftMutation.isPending ||
                      // Close and Submit are both terminal-ish writes on the same Draft.
                      // Each disables the other so they cannot be fired concurrently.
                      stageUpdateMutation.isPending ||
                      !isOnline ||
                      isManualSaving ||
                      recordingState !== 'idle'
                    }
                  >
                    {closeDraftMutation.isPending ? (
                      <ActivityIndicator size="small" color="#666" />
                    ) : (
                      <Text
                        style={
                          isSmallScreen
                            ? styles.closeDraftCompactButtonText
                            : styles.closeDraftButtonText
                        }
                      >
                        Close
                      </Text>
                    )}
                  </TouchableOpacity>
                </PermissionAwareButton>
              )}

              {/* Submit button - controlled by config permissions */}
              {(() => {
                const currentStage = (request.stage ||
                  request.sysRequestStage) as RequestStage;
                const nextStage = getNextStage(
                  currentStage,
                  isExternalRequestor,
                );

                // Don't show submit button if already closed or no next stage
                if (!nextStage || currentStage === RequestStage.CLOSED) {
                  return null;
                }

                // A partner who is not a live group member has no stamp on an
                // awaiting request. Read-only impersonation keeps the member
                // from stamping and explains why.
                if (
                  currentStage === RequestStage.AWAITING_APPROVAL &&
                  approvalStampBlockedByReadOnlyImpersonation
                ) {
                  return (
                    <Text
                      testID="detail-approval-stamp-impersonation-hint"
                      style={styles.approvalFooterNote}
                    >
                      Approve and Reject are unavailable in read-only
                      impersonation.
                    </Text>
                  );
                }
                if (
                  currentStage === RequestStage.AWAITING_APPROVAL &&
                  isPartnerViewer &&
                  !viewerCanStampAwaitingApproval
                ) {
                  return null;
                }

                // Submit reads as a publish action out of DRAFT only. Gate on
                // currentStage, not nextStage: DRAFT and the legacy SITE_ASSESSMENT
                // advance now share the same nextStage and cannot be told apart by it.
                // The button uses the compact label; the deliverables dialog keeps
                // the full draftSubmitLabel.
                const submitLabel =
                  currentStage === RequestStage.DRAFT
                    ? draftSubmitButtonLabel
                    : currentStage === RequestStage.AWAITING_APPROVAL
                    ? viewerCanStampAwaitingApproval
                      ? 'Approve'
                      : 'Submit to Vertiv'
                    : null;
                const showReject =
                  viewerCanStampAwaitingApproval && !viewerIsRequestor;

                return (
                  <>
                    {showReject ? (
                      <TouchableOpacity
                        testID="detail-reject-button"
                        style={[
                          isSmallScreen
                            ? styles.rejectCompactButton
                            : styles.rejectTextButton,
                          (rejectRequestMutation.isPending ||
                            stageUpdateMutation.isPending ||
                            showStageChangeConfirmation ||
                            !isOnline ||
                            isManualSaving ||
                            recordingState !== 'idle') &&
                            styles.rejectButtonDisabled,
                        ]}
                        onPress={handleReject}
                        disabled={
                          rejectRequestMutation.isPending ||
                          stageUpdateMutation.isPending ||
                          showStageChangeConfirmation ||
                          !isOnline ||
                          isManualSaving ||
                          recordingState !== 'idle'
                        }
                      >
                        <Text style={styles.rejectButtonText}>Reject</Text>
                      </TouchableOpacity>
                    ) : null}
                    <PermissionAwareButton
                      buttonType="submit"
                      requestStage={
                        submitPermissionStageForDetail(
                          currentStage,
                          isPartnerViewer,
                        ) || undefined
                      }
                      requestId={requestId || undefined}
                      loadingComponent={
                        <View
                          testID="detail-submit-button-loading"
                          style={[
                            isSmallScreen
                              ? styles.submitCompactButton
                              : styles.submitTextButton,
                            isSmallScreen
                              ? styles.submitCompactButtonDisabled
                              : styles.submitTextButtonDisabled,
                          ]}
                        />
                      }
                    >
                      <TouchableOpacity
                        testID="detail-submit-button"
                        style={[
                          isSmallScreen
                            ? styles.submitCompactButton
                            : styles.submitTextButton,
                          (stageUpdateMutation.isPending ||
                            rejectRequestMutation.isPending ||
                            showRejectConfirmation ||
                            isManualSaving ||
                            recordingState !== 'idle') &&
                            (isSmallScreen
                              ? styles.submitCompactButtonDisabled
                              : styles.submitTextButtonDisabled),
                        ]}
                        onPress={handleStageChangeClick}
                        disabled={
                          stageUpdateMutation.isPending ||
                          // Mirror of the Close button's guard: a Draft close in flight
                          // blocks Submit so the two writes cannot race.
                          closeDraftMutation.isPending ||
                          // Reject and Approve must not run together.
                          rejectRequestMutation.isPending ||
                          showRejectConfirmation ||
                          !isOnline ||
                          isManualSaving ||
                          recordingState !== 'idle'
                        }
                      >
                        {stageUpdateMutation.isPending ? (
                          <ActivityIndicator size="small" color="#fff" />
                        ) : isSmallScreen ? (
                          <View style={styles.compactButtonContent}>
                            {/* A one-line label uses the same 14pt type as the
                            Edit button beside it; only the stacked two-line
                            "Send to / <stage>" below needs the smaller size to
                            fit the 40pt height. */}
                            {nextStage === RequestStage.CLOSED ? (
                              <Text style={styles.submitTextButtonText}>
                                Close
                              </Text>
                            ) : submitLabel ? (
                              <Text style={styles.submitTextButtonText}>
                                {submitLabel}
                              </Text>
                            ) : (
                              <>
                                <Text style={styles.setToText}>Send to</Text>
                                <Text style={styles.stageButtonText}>
                                  {/* No viewer passed on purpose: CONFIGURATION is the only
                              stage whose label depends on the viewer, and the branch
                              above intercepts it. The fallback can only ever render a
                              viewer-independent label. */}
                                  {nextStage === RequestStage.CONFIGURATION
                                    ? 'Config Team'
                                    : getStageDisplayName(nextStage)}
                                </Text>
                              </>
                            )}
                          </View>
                        ) : (
                          <>
                            <Text style={styles.submitTextButtonText}>
                              {nextStage === RequestStage.CLOSED
                                ? 'Close'
                                : submitLabel ??
                                  `Send to ${
                                    // Viewer-independent by construction — see the compact
                                    // branch above.
                                    nextStage === RequestStage.CONFIGURATION
                                      ? 'Config Team'
                                      : getStageDisplayName(nextStage)
                                  }`}
                            </Text>
                            {/* Forward arrow signals a pipeline move, so it belongs on
                        "Send to ..." but not on the terminal Submit actions. */}
                            {!submitLabel && (
                              <ChevronRight
                                size={16}
                                color="#fff"
                                style={styles.submitChevron}
                              />
                            )}
                          </>
                        )}
                      </TouchableOpacity>
                    </PermissionAwareButton>
                  </>
                );
              })()}
            </View>
          )}

          {/* Cancel and Save buttons - only shown when editing */}
          {isEditing && (
            <View style={styles.editButtonsContainer}>
              {/* Cancel text */}
              <TouchableOpacity
                testID="detail-cancel-button"
                onPress={handleCancel}
                disabled={isManualSaving || recordingState !== 'idle'}
              >
                <Text
                  style={[
                    styles.cancelText,
                    (isManualSaving || recordingState !== 'idle') &&
                      styles.cancelTextDisabled,
                  ]}
                >
                  Cancel
                </Text>
              </TouchableOpacity>

              {/* Save button - controlled by config permissions */}
              <PermissionAwareButton
                buttonType="save"
                requestStage={requestStage || undefined}
                requestId={requestId || undefined}
                loadingComponent={
                  <View
                    testID="detail-save-button-loading"
                    style={[styles.saveButton, styles.saveButtonDisabled]}
                  />
                }
              >
                <TouchableOpacity
                  testID="detail-save-button"
                  style={[
                    styles.saveButton,
                    (updateRequestMutation.isPending ||
                      isManualSaving ||
                      recordingState !== 'idle' ||
                      inlineImageUploadsInFlight) &&
                      styles.saveButtonDisabled,
                  ]}
                  onPress={handleSave}
                  disabled={
                    updateRequestMutation.isPending ||
                    isManualSaving ||
                    recordingState !== 'idle' ||
                    inlineImageUploadsInFlight
                  }
                >
                  {updateRequestMutation.isPending || isManualSaving ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : (
                    <>
                      <Save size={16} color="#666" style={styles.saveIcon} />
                      <Text style={styles.saveButtonText}>
                        {footerSaveLabel}
                      </Text>
                    </>
                  )}
                </TouchableOpacity>
              </PermissionAwareButton>
            </View>
          )}
        </View>
      </View>
    </>
  );

  const dockedPanel = isDiscussionDocked ? (
    <CorrespondenceDock open={discussionOpen} paneHeight={dockPaneHeight}>
      {rendered =>
        rendered ? (
          <CorrespondencePanel
            /* The screen's own state, not a literal (code review 2026-09-11):
               `CorrespondenceDock` keeps this mounted through the close
               animation, and a summary landing in that window must not drive
               a listing or a mark-read for a panel the user just closed. */
            visible={discussionOpen}
            presentation="docked"
            spaceUnread={correspondenceSpaceUnread}
            spaceActivity={correspondenceSpaceActivity}
            accessRevoked={correspondenceAccessRevoked}
            onSpaceUnreadKnown={noteCorrespondenceSpaceUnread}
            onThreadRead={handleThreadRead}
            onClose={() => setDiscussionOpen(false)}
            onComposerFocusChange={setComposerFocused}
            session={discussionSession}
            requestId={requestId ?? undefined}
            requestLabel={getRequestCorrespondenceLabel(requestId)}
            requestorEmail={request?.requestorEmail}
            requestorName={request?.requestorName}
          />
        ) : null
      }
    </CorrespondenceDock>
  ) : null;

  return (
    <DimensionUnitsProvider
      units={editedData.dimensionUnits ?? request?.dimensionUnits}
      onChange={next => {
        setEditedData((prev: Record<string, unknown>) => ({
          ...prev,
          dimensionUnits: next,
        }));
        setDirtyFields(prev => markDimensionUnitsDirty(prev));
      }}
      loadDefaults={false}
    >
      <View style={styles.container}>
        <FormKeyboardScrollProvider
          scrollRef={scrollViewRef}
          bottomOffset={KEYBOARD_BOTTOM_OFFSET}
        >
          {/* Correspondence dock row (#1069): the panel is a flex SIBLING of the
            request content, so opening it pushes the content left and shrinks
            it rather than covering it. Only the page body is wrapped — the
            modals below stay direct children of the column container. */}
          {isWebLayout ? (
            /* Web (spec 2026-09-07): ONE scroll view wraps both the content
             column and the dock, so the page scrollbar sits at the window's
             right edge instead of between the form and the panel. The dock is
             sticky at pane height beside the column; the footer band is
             sticky-bottom INSIDE the column so it never runs under the panel. */
            <View style={styles.mainContent}>
              <RecordTableDragScrollLockProvider>
                <RequestDetailFormScroll
                  scrollViewRef={scrollViewRef}
                  contentContainerStyle={styles.webScrollRow}
                  onLayout={handleDockPaneLayout}
                  scrollPaddingBottom={composerFocused ? 0 : footerBandHeight}
                >
                  <View style={styles.webContentColumn}>
                    <View style={styles.content}>{formBody}</View>
                    <View
                      style={styles.stickyFooterBand}
                      onLayout={handleFooterBandLayout}
                    >
                      {footerBand}
                    </View>
                  </View>
                  {dockedPanel}
                </RequestDetailFormScroll>
              </RecordTableDragScrollLockProvider>
            </View>
          ) : (
            /* Native: the panel never docks and the scroll structure is
             untouched — the scroll view, then the in-flow footer band. */
            <View style={styles.dockRow}>
              <View style={styles.mainContent}>
                <RecordTableDragScrollLockProvider>
                  <RequestDetailFormScroll scrollViewRef={scrollViewRef}>
                    {formBody}
                  </RequestDetailFormScroll>
                </RecordTableDragScrollLockProvider>
                {footerBand}
              </View>
              {dockedPanel}
            </View>
          )}

          {/* Stage Change Confirmation Dialog */}
          {request &&
            (() => {
              const currentStage = (request.stage ||
                request.sysRequestStage) as RequestStage;
              const nextStage = getNextStage(currentStage, isExternalRequestor);

              if (!nextStage) return null;

              const currentStageName = getStageDisplayName(
                currentStage,
                showInternalDetail,
              );
              const nextStageName = getStageDisplayName(
                nextStage,
                showInternalDetail,
              );
              // Viewer-aware labels can collapse two stages onto one word, at which point
              // `from "In Progress" to "In Progress"` reads as a defect. Describe the action
              // instead of the (unchanged) label.
              const stageChangeMessage =
                currentStageName === nextStageName
                  ? `Are you sure you want to move this request forward? It will continue to show as "${nextStageName}". This action cannot be undone.`
                  : `Are you sure you want to change the stage from "${currentStageName}" to "${nextStageName}"? This action cannot be undone.`;

              /**
               * On a Draft this dialog is now reachable as the SUBMIT confirmation, because
               * submitting from the Deliverables step skips the deliverables dialog. It must
               * therefore speak the language of the button that opened it: pressing "Send to
               * Vertiv" and being asked to "Change Stage" hands an external requestor the
               * internal vocabulary this batch exists to keep away from them, and `draftSubmitLabel`
               * exists precisely so the two cannot drift (FR11). Every other stage is unchanged.
               */
              const isApproveSubmitToVertiv =
                currentStage === RequestStage.AWAITING_APPROVAL &&
                viewerIsLiveApprovalGroupMember;
              const confirmationCopy = getStageConfirmationCopy(
                currentStage === RequestStage.DRAFT,
                draftSubmitLabel,
                isApproveSubmitToVertiv,
              );

              return (
                <ConfirmationDialog
                  visible={showStageChangeConfirmation}
                  onClose={() => setShowStageChangeConfirmation(false)}
                  onConfirm={handleConfirmStageChange}
                  title={confirmationCopy.title}
                  message={confirmationCopy.message ?? stageChangeMessage}
                  confirmText={confirmationCopy.confirmText}
                  cancelText="Cancel"
                  confirmButtonStyle="primary"
                  showWarningIcon={!isApproveSubmitToVertiv}
                />
              );
            })()}

          {/* Deliverables dialog — the Draft submit confirmation (FR20). Replaces the stage-change
          confirmation above on DRAFT only; every other stage still uses that one. It is a
          second view of the same pending list, so no remount key and no staged copy. */}
          <RequestDeliverablesDialog
            visible={showDeliverablesDialog}
            deliverables={deliverablesSession.deliverables}
            onAdd={deliverablesSession.addDeliverableEntries}
            onEdit={deliverablesSession.editDeliverableComment}
            onRemove={deliverablesSession.removeDeliverableType}
            onCancel={handleDeliverablesDialogCancel}
            onConfirm={handleDeliverablesDialogConfirm}
            confirmLabel={draftSubmitLabel}
            onSaveChanges={handleDeliverablesDialogSave}
            /*
             * Gated on the SAME permission cell as the section (`:3985`), not just on whether
             * there is something to write. Adversarial review, 2026-08-12: `canEditDeliverables`
             * reached the section and stopped there, so a role that cannot write this field was
             * blocked from the section and handed a standalone persist button in the dialog. The
             * server's PUT guard is `requireRequestAccess('edit')`, which is button-level and does
             * not close it either.
             */
            canSaveChanges={
              deliverablesSession.hasUnsavedChanges &&
              canEditDeliverablesAtStage
            }
          />

          {/* Close Draft Confirmation Dialog.
          The copy says permanent on purpose: CLOSED is terminal and nothing in the product
          reopens a request. */}
          <ConfirmationDialog
            visible={showCloseDraftConfirmation}
            onClose={() => setShowCloseDraftConfirmation(false)}
            onConfirm={handleConfirmCloseDraft}
            title="Close Request"
            message={`Are you sure you want to close request ${request?.id}? This is permanent: a closed request cannot be reopened or submitted. It will stay in your request list under Closed.`}
            confirmText="Close Request"
            cancelText="Cancel"
            confirmButtonStyle="danger"
          />

          <ConfirmationDialog
            visible={showRejectConfirmation}
            onClose={() => {
              setShowRejectConfirmation(false);
              setRejectReason('');
              setRejectReasonError(null);
            }}
            onConfirm={handleConfirmReject}
            title="Reject Request"
            message="Provide the rejection reason. The request will return to Draft and the requestor will see this reason."
            confirmText="Reject"
            cancelText="Cancel"
            confirmButtonStyle="danger"
            loadingText="Rejecting..."
          >
            <View style={styles.rejectReasonContainer}>
              <Text style={styles.rejectReasonLabel}>Rejection reason</Text>
              <TextInput
                testID="reject-reason-input"
                accessibilityLabel="Rejection reason"
                accessibilityHint="Required reason shown to the requestor"
                style={[
                  styles.rejectReasonInput,
                  rejectReasonError && styles.rejectReasonInputError,
                ]}
                value={rejectReason}
                onChangeText={value => {
                  setRejectReason(value);
                  if (rejectReasonError && value.trim()) {
                    setRejectReasonError(null);
                  }
                }}
                placeholder="Explain what needs to change before approval"
                placeholderTextColor={colors.inputPlaceholder}
                multiline
                numberOfLines={2}
                maxLength={REJECT_REASON_MAX_LENGTH}
                textAlignVertical="top"
                inputAccessoryViewID={
                  Platform.OS === 'ios'
                    ? REJECT_REASON_ACCESSORY_VIEW_ID
                    : undefined
                }
              />
              <KeyboardDismissToolbar
                nativeID={REJECT_REASON_ACCESSORY_VIEW_ID}
              />
              <View
                style={styles.rejectReasonCharCountRow}
                testID="reject-reason-char-count"
              >
                <Text
                  style={styles.rejectReasonCharCount}
                  accessibilityLabel={`${rejectReason.length} of ${REJECT_REASON_MAX_LENGTH} characters`}
                >
                  {rejectReason.length} / {REJECT_REASON_MAX_LENGTH}
                </Text>
              </View>
              {rejectReasonError ? (
                <Text
                  testID="reject-reason-error"
                  accessibilityRole="alert"
                  style={styles.rejectReasonError}
                >
                  {rejectReasonError}
                </Text>
              ) : null}
            </View>
          </ConfirmationDialog>

          {/* Delete Confirmation Dialog */}
          <ConfirmationDialog
            visible={showDeleteConfirmation}
            onClose={() => setShowDeleteConfirmation(false)}
            onConfirm={handleConfirmDelete}
            title="Archive Request"
            message={`Are you sure you want to archive request ${request?.id}? The request will be removed from the request list and set to closed status. You can still access it via direct link.`}
            confirmText="Archive Request"
            cancelText="Cancel"
            confirmButtonStyle="danger"
          />

          {/* Processing Modal for in-app recording transcription */}
          {requestIdNum && (
            <ProcessingModal
              visible={processingModalVisible}
              requestId={requestIdNum}
              fileName={processingFileName}
              hideViewRequestButton={true}
              autoCloseOnComplete={processingModalAutoClose}
              onClose={() => {
                setProcessingModalVisible(false);
                setProcessingFileName(undefined);
              }}
              onNavigateToRequest={handleNavigateToRequest}
            />
          )}

          {/* Text intake (Paste text) modal */}
          <TextIntakeModal
            visible={textIntakeModalVisible}
            onClose={() => setTextIntakeModalVisible(false)}
            onSubmit={handleTextIntakeSubmit}
            isSubmitting={textIntakeSubmitting}
          />

          {/* Recording Warning Modal */}
          <RecordingWarningModal
            visible={showRecordingWarning}
            onConfirm={handleRecordingWarningConfirm}
            onCancel={handleRecordingWarningCancel}
          />

          {confirmationDialog}

          {/* Request Correspondence (#1069) — the OVERLAY path: native and
            narrow web. Renders the panel only when not docked. */}
          <CorrespondenceLauncher
            open={discussionOpen}
            onToggle={() => setDiscussionOpen(v => !v)}
            docked={isDiscussionDocked}
            session={discussionSession}
            spaceUnread={correspondenceSpaceUnread}
            spaceActivity={correspondenceSpaceActivity}
            accessRevoked={correspondenceAccessRevoked}
            onSpaceUnreadKnown={noteCorrespondenceSpaceUnread}
            onThreadRead={handleThreadRead}
            requestId={requestId ?? undefined}
            requestLabel={getRequestCorrespondenceLabel(requestId)}
            requestorEmail={request?.requestorEmail}
            requestorName={request?.requestorName}
          />
        </FormKeyboardScrollProvider>
      </View>
    </DimensionUnitsProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.surfaceRequestCanvas,
    ...Platform.select({
      web: {
        overflow: 'hidden' as any, // Prevent page-level scrolling
      },
    }),
  },
  /**
   * The row the request content and the docked correspondence panel share
   * (#1069). `minHeight: 0` so the column inside can shrink and scroll.
   */
  dockRow: {
    flex: 1,
    flexDirection: 'row',
    minHeight: 0,
  },
  mainContent: {
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    ...Platform.select({
      web: {
        overflow: 'hidden' as any, // Prevent main content from scrolling
      },
    }),
  },
  scrollContainer: {
    flex: 1,
  },
  content: {
    padding: 20,
    alignItems: isWeb ? 'center' : 'stretch',
    ...Platform.select({
      web: {
        flexGrow: 1,
        // In-flow footer in RequestFrame — smaller inset than legacy fixed footer (160px).
        paddingBottom: 80,
      },
    }),
  },
  /**
   * Web (spec 2026-09-07): the scroll view's content is a ROW of
   * [content column, dock]. `flexGrow: 1` makes the row at least the pane
   * tall, which is what lets the footer band's `bottom: 0` hold it at the
   * pane's bottom on a short step; `stretch` gives the column that height.
   */
  webScrollRow: {
    flexGrow: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  /** The request content beside the dock. `minWidth: 0` so it can shrink. */
  webContentColumn: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'column',
  },
  /**
   * Upload bar, recording bar and footer, pinned to the bottom of the pane
   * while the column scrolls past them. Sticky INSIDE the column rather than
   * in-flow below the scroll view, because below it the band would span under
   * the panel, which is full height by intent. No `zIndex` (P3): the band is
   * positioned and later in DOM order, so it already paints above the static
   * content, and a stacking context here would cap the recording bar's own
   * z-index. Its measured height becomes the scroll view's
   * `scroll-padding-bottom`, so a focused field is never hidden under it.
   */
  stickyFooterBand: {
    position: 'sticky' as any,
    bottom: 0,
  },
  formContainer: {
    width: '100%',
    maxWidth: isWeb ? spacing.formMaxWidth : undefined,
    alignSelf: isWeb ? 'center' : 'stretch',
  },
  rejectionBanner: {
    width: '100%',
    backgroundColor: colors.surfaceWarningMuted,
    borderWidth: 1,
    borderColor: colors.warning,
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  rejectionBannerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 6,
  },
  rejectionBannerText: {
    fontSize: 14,
    lineHeight: 20,
    color: colors.textSecondary,
  },
  subsectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 20,
  },
  placeholderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 60,
  },
  placeholderTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ff6b35',
    marginBottom: 16,
    textAlign: 'center',
  },
  placeholderText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  footer: {
    backgroundColor: '#fff',
    paddingHorizontal: 24,
    flexShrink: 0,
    ...Platform.select({
      web: {
        paddingTop: REQUEST_FORM_NATIVE_FOOTER_VERTICAL_PADDING,
        paddingBottom: REQUEST_FORM_NATIVE_FOOTER_VERTICAL_PADDING,
        height: 'auto',
        minHeight: 52,
        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: 2,
        },
        shadowOpacity: 0.08,
        shadowRadius: 3,
        elevation: 3,
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: -2,
        },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      },
    }),
  },
  footerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    /**
     * #1550 — `space-between` only adds leftover space. On a tight compact
     * native row the left (tools + Edit) and right (Close + Submit) clusters
     * meet, so Edit and Close look like one control. Intra-group `gap: 8` on
     * `actionButtonsGroup` cannot help: `leftButtonsContainer` is
     * `overflow: hidden` and clips its own trailing margin. A parent `gap`
     * sits between the two children, outside that clip, and does not shrink
     * the `flexShrink: 0` Submit group.
     */
    gap: 8,
    maxWidth: isWeb ? spacing.formMaxWidth : undefined,
    alignSelf: isWeb ? 'center' : 'stretch',
    width: '100%',
    ...Platform.select({
      web: {
        minHeight: 52,
        height: 'auto',
      },
      default: {
        minHeight: REQUEST_FORM_NATIVE_FOOTER_CONTENT_HEIGHT,
      },
    }),
    ...Platform.select({
      android: {
        paddingHorizontal: 8,
        paddingLeft: 0,
      },
      ios: {
        paddingHorizontal: 4,
        paddingLeft: 0,
      },
    }),
  },
  navigationButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  leftButtonsContainer: {
    /** Yields before the Submit/Close group — see rightButtonsContainer. */
    flexShrink: 1,
    minWidth: 0,
    overflow: 'hidden',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    ...Platform.select({
      ios: {
        marginLeft: -8,
        gap: 8,
      },
      android: {
        marginLeft: -8,
        gap: 8,
      },
    }),
  },
  actionButtonsGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    ...Platform.select({
      ios: {
        marginRight: 8,
      },
      android: {
        marginRight: 8,
      },
    }),
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    gap: 12,
  },
  backButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 44,
    minHeight: 44,
  },
  backButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
    textAlign: 'center',
  },
  rightButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    justifyContent: 'flex-end',
    ...Platform.select({
      android: {
        marginLeft: 8,
      },
      default: {
        gap: 12,
      },
    }),
  },
  saveButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
    minWidth: 44,
    minHeight: 44,
    marginLeft: Platform.OS === 'android' ? 6 : 0,
    flexShrink: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
    textAlign: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: '#ccc',
  },
  submitButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: '#3B82F6',
    borderRadius: 4,
    marginLeft: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  submitButtonText: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
    marginLeft: 6,
  },
  submitButtonDisabled: {
    backgroundColor: '#93BBFC',
    opacity: 0.6,
  },
  submitIcon: {
    marginRight: 0,
  },
  submitTextButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: '#ff6b35',
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
  },
  submitTextButtonText: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
  },
  submitTextButtonDisabled: {
    opacity: 0.6,
  },
  submitChevron: {
    marginLeft: 4,
  },
  submitCompactButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#ff6b35',
    borderRadius: 6,
    minHeight: 40,
    maxHeight: 40,
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      ios: {
        marginRight: -8,
      },
      android: {
        marginRight: -8,
      },
    }),
  },
  submitCompactButtonDisabled: {
    opacity: 0.6,
  },
  /* Draft Close. Same geometry as the submit button on both arms so the two sit level in
     the footer, outlined rather than filled because Submit stays the primary action. */
  rightButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    /**
     * #1149 Issue 9 — never let the primary actions be the thing that
     * overflows. Every View defaults to flexShrink: 0 and the footer's
     * ancestors set overflow: hidden, so a too-wide row was CLIPPED rather
     * than wrapped or scrolled, and Submit silently vanished. The left tool
     * group yields first; this group keeps its width.
     */
    flexShrink: 0,
  },
  closeDraftTextButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: 'transparent',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#666',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
  },
  closeDraftButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '600',
  },
  closeDraftCompactButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'transparent',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#666',
    minHeight: 40,
    maxHeight: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeDraftCompactButtonText: {
    fontSize: 12,
    color: '#666',
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 14,
  },
  closeDraftButtonDisabled: {
    opacity: 0.6,
  },
  rejectTextButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    backgroundColor: 'transparent',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.error,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  rejectCompactButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'transparent',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.error,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  rejectButtonText: {
    fontSize: 14,
    color: colors.error,
    fontWeight: '600',
  },
  approvalFooterNote: {
    fontSize: 13,
    color: colors.textSecondary,
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  rejectButtonDisabled: {
    opacity: 0.6,
  },
  rejectReasonContainer: {
    marginTop: 16,
  },
  rejectReasonLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.textPrimary,
    marginBottom: 8,
  },
  rejectReasonInput: {
    minHeight: 64,
    borderWidth: 1,
    borderColor: colors.inputBorder,
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: colors.textPrimary,
    backgroundColor: colors.backgroundPrimary,
    fontSize: 14,
    lineHeight: 20,
  },
  rejectReasonCharCountRow: {
    width: '100%' as const,
    minWidth: 0,
    marginTop: spacing.charCountMarginTop,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  rejectReasonCharCount: {
    fontSize: fontSize.charCount,
    lineHeight: lineHeight.charCount,
    color: colors.textSecondary,
  },
  rejectReasonInputError: {
    borderColor: colors.error,
  },
  rejectReasonError: {
    color: colors.error,
    fontSize: 13,
    marginTop: 8,
  },
  compactButtonContent: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 0,
  },
  setToText: {
    fontSize: 10,
    color: '#fff',
    fontWeight: '400',
    opacity: 0.9,
    lineHeight: 12,
  },
  stageButtonText: {
    fontSize: 12,
    color: '#fff',
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 14,
  },
  editButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: '#ff6b35',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 44,
    minHeight: 40,
    marginLeft: Platform.OS === 'android' ? 6 : 0,
    flexShrink: 1,
  },
  editButtonText: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
  },
  saveIcon: {
    marginRight: 8,
  },
  editButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  cancelText: {
    fontSize: 14,
    color: '#dc3545',
    fontWeight: '500',
  },
  cancelTextDisabled: {
    color: '#ccc',
  },
  impersonationStampHint: {
    fontSize: 12,
    color: '#666',
    marginTop: 8,
    maxWidth: 280,
    textAlign: 'center',
  },
  editIcon: {
    marginRight: 0,
  },
  deleteButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 44,
    minHeight: 44,
    marginLeft: Platform.OS === 'android' ? 6 : 0,
    flexShrink: 1,
  },
  deleteButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
    marginLeft: 8,
  },
  deleteButtonDisabled: {
    backgroundColor: '#ccc',
  },
  continueButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 44,
    minHeight: 44,
  },
  continueButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
    textAlign: 'center',
  },
  deleteIcon: {
    marginRight: 0,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  loadingText: {
    fontSize: 16,
    color: '#666',
    marginTop: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
    marginTop: 16,
    marginBottom: 8,
  },
  errorMessage: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
  },
  navigationContainer: {
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
  navigationContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: isWeb ? spacing.formMaxWidth : undefined,
    alignSelf: isWeb ? 'center' : 'stretch',
    width: '100%',
  },
  navBackButton: {
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 40,
    minHeight: 40,
  },
  navBackButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
    textAlign: 'center',
  },
  navContinueButton: {
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 40,
    minHeight: 40,
  },
  navContinueButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
    textAlign: 'center',
  },
  navButtonDisabled: {
    backgroundColor: '#f0f0f0',
    borderColor: '#e0e0e0',
    opacity: 0.6,
  },
  iconButton: {
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 40,
    minHeight: 40,
  },
  /**
   * Stand-in for the round AudioRecordingButton while button grants load (#1469):
   * same diameter as the icon it replaces (`spacing.formControlHeight`, the size the
   * button is rendered at), dimmed via the shared `iconButtonDisabled` overlay.
   */
  footerAudioPlaceholder: {
    width: spacing.formControlHeight,
    height: spacing.formControlHeight,
    borderRadius: spacing.formControlHeight / 2,
    backgroundColor: colors.backgroundSecondary,
  },
  editButtonWithText: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 0,
    backgroundColor: '#ff6b35',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    minWidth: 70,
    minHeight: 40,
  },
  iconButtonDisabled: {
    backgroundColor: '#ccc',
    opacity: 0.6,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    ...Platform.select({
      ios: {
        marginTop: -2, // Slight upward adjustment on mobile
      },
      android: {
        marginTop: -2, // Slight upward adjustment on mobile
      },
    }),
  },
  titleText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ff6b35',
  },
  copyButton: {
    padding: 4,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        ':hover': {
          backgroundColor: '#f8f9fa',
        },
      },
    }),
  },
});
